import React from 'react';
import { useTooltip } from '../../hooks';
// Direct import rather than the './helpers' barrel: formatData reaches the components barrel,
// which re-exports this file, so the barrel would pull every helper into that loop. Both ends are
// read at render time, never while a module is still evaluating, which is what keeps it working.
import { iconClassName } from '../../helpers/formatData';

import './Icon.css';

export const Icon = ({ name, label, value, className, values, tooltip = '', onClick }) => {
  const { showTooltip, hideTooltip } = useTooltip();

  if (!name) return null;

  // Normalises the name for the `icons` lookup below. Spaces only: the map is keyed with
  // underscores (`arrow_left`, `caps_lock`), so converting those here would turn every
  // lookup into a miss. The CSS class is derived separately, by iconClassName.
  name = name.replace(/ /g, '-').toLowerCase();

  const icons = {
    arrow_left: arrowLeft(),
    arrow_right: arrowRight(),
    about: about(),
    not_found: notFound(),
    support: support(),
    internal_resource: internalResource(),
    docs: docs(),
    api_docs: apiDocs(),
    profile: profile(),
    password: password(),
    signout: signout(),
    katalogue_small: katalogueSmall(),
    katalogue: katalogue({ className }),
    home: home(),
    datasets: datasets(),
    attributes: attributes(),
    attribute_sensitivity: attributeSensitivity({ label, value, values, showTooltip, hideTooltip }),
    glossaries: glossaries(),
    users: users(),
    user_groups: userGroups(),
    connections: connections(),
    custom_properties: customProperties(),
    tasks: tasks(),
    schedules: schedules(),
    attribute_units: attributeUnits(),
    settings: settings(),
    toggle_visibility: toggleVisibility(),
    caps_lock: capsLock(),
    descriptions: descriptions(),
    small_descriptions: descriptionsSmall(),
    statistics: statistics(),
    tree: tree(),
    flat: flat(),
    editor_bold: editorBold(),
    editor_italic: editorItalic(),
    editor_underline: editorUnderline(),
    editor_ol: editorOl(),
    editor_ul: editorUl(),
    editor_reference: editorReference(),
    editor_table: editorTable(),
    editor_relationships: editorRelationships(),
    editor_lineage: editorLineage(),
    editor_code: editorCode(),
    editor_focus: editorFocus(),
    expand: expand(),
    search: search(),
    filter: filter(),
    fit: fit(),
    reload: reload(),
    node_size: nodeSize(),
    light_mode: lightMode(),
    dark_mode: darkMode(),
    auto_mode: autoMode(),
    relationship_graph_spinner: relationshipGraphSpinner(),
    lineage_graph_spinner: lineageGraphSpinner(),
  };

  const icon = icons[name];

  if (icon !== undefined && onClick) {
    return <div onClick={onClick}>{icon}</div>;
  } else if (icon !== undefined) {
    return tooltip ? (
      <div
        onMouseEnter={() => tooltip && showTooltip({ title: tooltip })}
        onMouseLeave={() => tooltip && hideTooltip()}
      >
        {icon}
      </div>
    ) : (
      icon
    );
  } else {
    return (
      <div
        className={'icon icon-' + iconClassName(name) + (className ? ` ${className}` : '')}
        onMouseEnter={() => tooltip && showTooltip({ title: tooltip })}
        onMouseLeave={() => tooltip && hideTooltip()}
        onClick={onClick}
      ></div>
    );
  }
};

const arrowLeft = () => {
  return (
    <svg
      className="icon-editor icon-arrow-left"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M3.709,9.907c0,-0 -3.499,-2.5 -3.5,-2.5c-0.129,-0.092 -0.209,-0.242 -0.209,-0.407c0,-0.165 0.08,-0.315 0.209,-0.407c0.001,-0 3.5,-2.5 3.5,-2.5c0.225,-0.16 0.537,-0.108 0.698,0.116c0.16,0.225 0.108,0.537 -0.116,0.698l-2.231,1.593l11.44,0c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-11.44,0l2.231,1.593c0.224,0.161 0.276,0.473 0.116,0.698c-0.161,0.224 -0.473,0.276 -0.698,0.116Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const arrowRight = () => {
  return (
    <svg
      className="icon-editor icon-arrow-right"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M11.94,7.5l-11.44,0c-0.276,-0 -0.5,-0.224 -0.5,-0.5c0,-0.276 0.224,-0.5 0.5,-0.5l11.44,0l-2.231,-1.593c-0.224,-0.161 -0.276,-0.473 -0.116,-0.698c0.161,-0.224 0.473,-0.276 0.698,-0.116c-0,0 3.499,2.5 3.5,2.5c0.129,0.092 0.209,0.242 0.209,0.407c0,0.165 -0.08,0.315 -0.209,0.407c-0.001,0 -3.5,2.5 -3.5,2.5c-0.225,0.16 -0.537,0.108 -0.698,-0.116c-0.16,-0.225 -0.108,-0.537 0.116,-0.698l2.231,-1.593Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const about = () => {
  return (
    <svg
      className="icon icon-about"
      width="100%"
      height="100%"
      viewBox="0 0 22 22"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M11,0c6.071,0 11,4.929 11,11c-0,6.071 -4.929,11 -11,11c-6.071,-0 -11,-4.929 -11,-11c0,-6.071 4.929,-11 11,-11Zm-0,1c-5.519,0 -10,4.481 -10,10c0,5.519 4.481,10 10,10c5.519,-0 10,-4.481 10,-10c-0,-5.519 -4.481,-10 -10,-10Zm-1,9.008l-1.5,-0c-0.28,-0 -0.508,-0.228 -0.508,-0.508c0,-0.28 0.228,-0.508 0.508,-0.508l3,0c0.28,0 0.508,0.228 0.508,0.508c-0,0.03 -0.003,0.06 -0.008,0.089c-0,0.001 0,0.003 0,0.005l0,6.398l1.5,0c0.28,0 0.508,0.228 0.508,0.508c-0,0.28 -0.228,0.508 -0.508,0.508l-5,-0c-0.28,-0 -0.508,-0.228 -0.508,-0.508c0,-0.28 0.228,-0.508 0.508,-0.508l1.5,0l0,-5.984Zm2,-5.008l0,1c0,0.552 -0.448,1 -1,1c-0.552,0 -1,-0.448 -1,-1l-0,-1c-0,-0.552 0.448,-1 1,-1c0.552,0 1,0.448 1,1Z"
        style={{ fill: '#b8adab' }}
      />

      <path
        className="fill"
        d="M11,-0c6.071,-0 11,4.929 11,11c0,6.071 -4.929,11 -11,11c-6.071,-0 -11,-4.929 -11,-11c0,-6.071 4.929,-11 11,-11Zm-1,10.008l0,5.984l-1.5,0c-0.28,0 -0.508,0.228 -0.508,0.508c0,0.28 0.228,0.508 0.508,0.508l5,-0c0.28,-0 0.508,-0.228 0.508,-0.508c-0,-0.28 -0.228,-0.508 -0.508,-0.508l-1.5,0l0,-6.398l-0,-0.005c0.005,-0.029 0.008,-0.059 0.008,-0.089c-0,-0.28 -0.228,-0.508 -0.508,-0.508l-3,0c-0.28,0 -0.508,0.228 -0.508,0.508c0,0.28 0.228,0.508 0.508,0.508l1.5,-0Zm2,-5.008c0,-0.552 -0.448,-1 -1,-1c-0.552,0 -1,0.448 -1,1l-0,1c-0,0.552 0.448,1 1,1c0.552,0 1,-0.448 1,-1l0,-1Z"
        style={{ fill: 'url(#gradient-about)' }}
      />

      <linearGradient
        id="gradient-about"
        x1="0%"
        y1="0%"
        x2="100%"
        y2="100%"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
        <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
      </linearGradient>
    </svg>
  );
};

const notFound = () => {
  return (
    <svg
      className="icon icon-not-found"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M2.155,19.492c-0.051,0.005 -0.103,0.008 -0.155,0.008c-0.467,0 -0.906,-0.213 -1.256,-0.598c-0.442,-0.487 -0.744,-1.267 -0.744,-2.152c-0,-0.885 0.302,-1.665 0.744,-2.152c0.35,-0.385 0.789,-0.598 1.256,-0.598c0.192,0 0.379,0.036 0.557,0.105c0.374,-1.003 0.971,-2.078 1.782,-3.064l-0.691,-0.686c-0.196,-0.195 -0.197,-0.512 -0.003,-0.707c0.195,-0.196 0.512,-0.197 0.707,-0.003l0.658,0.653c0.359,-0.362 0.751,-0.705 1.175,-1.018l-0.614,-1.023c-0.142,-0.236 -0.065,-0.544 0.172,-0.686c0.236,-0.142 0.544,-0.065 0.686,0.172l0.591,0.985c0.43,-0.252 0.887,-0.474 1.37,-0.656l-0.354,-0.886c-0.103,-0.257 0.022,-0.548 0.278,-0.65c0.257,-0.103 0.548,0.022 0.65,0.278l0.383,0.957c0.524,-0.132 1.075,-0.219 1.653,-0.254l0,-1.017c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l0,1.008c0.59,0.023 1.191,0.093 1.79,0.209l0.217,-1.299c0.045,-0.272 0.303,-0.457 0.575,-0.411c0.272,0.045 0.457,0.303 0.411,0.575l-0.227,1.365c0.358,0.1 0.714,0.217 1.064,0.351l0.18,-0.896c0.054,-0.271 0.317,-0.446 0.588,-0.392c0.271,0.054 0.446,0.317 0.392,0.588l-0.221,1.106c0.489,0.238 0.962,0.511 1.409,0.819l0.375,-0.747c0.124,-0.247 0.425,-0.347 0.671,-0.223c0.247,0.124 0.347,0.425 0.223,0.671l-0.46,0.917c0.913,0.776 1.679,1.721 2.211,2.833c0.248,-0.146 0.52,-0.224 0.802,-0.224c0.467,-0 0.906,0.213 1.256,0.598c0.442,0.487 0.744,1.267 0.744,2.152c0,0.885 -0.302,1.665 -0.744,2.152c-0.35,0.385 -0.789,0.598 -1.256,0.598c-0.095,0 -0.188,-0.009 -0.28,-0.026c-0.381,1.237 -1.132,2.47 -2.156,3.577c-2.321,2.512 -6.032,4.378 -9.767,4.199c-2.417,-0.115 -4.245,-0.93 -5.524,-2.205c-1.123,-1.119 -1.827,-2.601 -2.118,-4.303Zm0.105,-4.426c-0.082,-0.042 -0.169,-0.066 -0.26,-0.066c-0.258,0 -0.475,0.183 -0.644,0.437c-0.224,0.335 -0.356,0.801 -0.356,1.313c0,0.512 0.132,0.978 0.356,1.313c0.169,0.254 0.386,0.437 0.644,0.437c0.011,0 0.023,0 0.033,-0.001c-0.068,-0.899 -0.031,-1.847 0.109,-2.825c0.028,-0.195 0.067,-0.398 0.118,-0.608Zm2.781,-3.327c-0.848,1.052 -1.428,2.211 -1.724,3.235c-0.003,0.012 -0.006,0.024 -0.009,0.037c-0.081,0.28 -0.139,0.55 -0.176,0.805c-0.148,1.033 -0.173,2.029 -0.069,2.963c0.002,0.012 0.002,0.025 0.003,0.037c0.195,1.699 0.819,3.181 1.913,4.271c1.124,1.12 2.741,1.813 4.866,1.914c3.438,0.165 6.848,-1.566 8.985,-3.879c1.04,-1.125 1.782,-2.387 2.055,-3.637c0.002,-0.013 0.004,-0.026 0.007,-0.039c0.085,-0.404 0.122,-0.806 0.103,-1.201c-0.039,-0.818 -0.197,-1.572 -0.453,-2.261c-0.432,-1.16 -1.138,-2.135 -2.015,-2.927l-0.08,0.161c-0.124,0.246 -0.425,0.346 -0.671,0.222c-0.247,-0.124 -0.347,-0.424 -0.223,-0.671l0.176,-0.351c-0.369,-0.262 -0.758,-0.497 -1.162,-0.706l-0.077,0.386c-0.054,0.271 -0.317,0.446 -0.588,0.392c-0.271,-0.054 -0.446,-0.317 -0.392,-0.588l0.123,-0.615c-0.339,-0.135 -0.684,-0.252 -1.032,-0.352l-0.108,0.647c-0.045,0.272 -0.303,0.457 -0.575,0.411c-0.272,-0.045 -0.457,-0.303 -0.411,-0.575l0.119,-0.717c-0.545,-0.106 -1.091,-0.17 -1.626,-0.192l0,0.741c0,0.276 -0.224,0.5 -0.5,0.5c-0.276,0 -0.5,-0.224 -0.5,-0.5l0,-0.731c-0.444,0.03 -0.87,0.094 -1.278,0.189l0.242,0.606c0.103,0.257 -0.022,0.548 -0.278,0.65c-0.257,0.103 -0.548,-0.022 -0.65,-0.278l-0.275,-0.688c-0.434,0.161 -0.844,0.358 -1.23,0.582l0.398,0.663c0.142,0.236 0.065,0.544 -0.172,0.686c-0.236,0.142 -0.544,0.065 -0.686,-0.172l-0.374,-0.624c-0.355,0.266 -0.684,0.555 -0.987,0.86l0.642,0.639c0.196,0.194 0.197,0.511 0.003,0.707c-0.195,0.196 -0.512,0.197 -0.707,0.002l-0.607,-0.602Zm16.901,5.508c0.019,0.002 0.038,0.003 0.058,0.003c0.258,0 0.475,-0.183 0.644,-0.437c0.224,-0.335 0.356,-0.801 0.356,-1.313c0,-0.512 -0.132,-0.978 -0.356,-1.313c-0.169,-0.254 -0.386,-0.437 -0.644,-0.437c-0.155,0 -0.295,0.067 -0.42,0.175c0.231,0.701 0.375,1.459 0.414,2.272c0.017,0.346 -0.002,0.697 -0.052,1.05Zm-12.026,6.03c-0.153,0.23 -0.464,0.292 -0.693,0.139c-0.23,-0.153 -0.292,-0.464 -0.139,-0.693c0.843,-1.264 3.187,-2.742 5.471,-3.475c1.578,-0.507 3.123,-0.644 4.142,-0.208c0.254,0.109 0.371,0.403 0.263,0.657c-0.109,0.254 -0.403,0.371 -0.657,0.263c-0.848,-0.364 -2.129,-0.181 -3.442,0.24c-2.052,0.659 -4.188,1.941 -4.945,3.077Zm1.154,-6.376c-0.45,0.663 -1.209,1.099 -2.07,1.099c-1.38,0 -2.5,-1.12 -2.5,-2.5c0,-1.38 1.12,-2.5 2.5,-2.5c0.677,0 1.291,0.27 1.742,0.707c0.511,-1.299 1.778,-2.22 3.258,-2.22c1.932,0 3.5,1.568 3.5,3.5c0,1.931 -1.568,3.5 -3.5,3.5c-1.225,0 -2.304,-0.631 -2.93,-1.586Zm-2.07,-2.901c-0.828,0 -1.5,0.672 -1.5,1.5c0,0.828 0.672,1.5 1.5,1.5c0.828,0 1.5,-0.672 1.5,-1.5c0,-0.828 -0.672,-1.5 -1.5,-1.5Zm0,0.75c0.414,0 0.75,0.336 0.75,0.75c0,0.414 -0.336,0.75 -0.75,0.75c-0.414,0 -0.75,-0.336 -0.75,-0.75c0,-0.414 0.336,-0.75 0.75,-0.75Zm5,-2.263c-1.38,0 -2.5,1.12 -2.5,2.5c0,1.379 1.12,2.5 2.5,2.5c1.38,0 2.5,-1.121 2.5,-2.5c0,-1.38 -1.12,-2.5 -2.5,-2.5Zm0,1.5c0.272,0 0.518,0.109 0.698,0.285c0.186,0.181 0.302,0.435 0.302,0.715c0,0.552 -0.448,1 -1,1c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1Z"
        style={{ fill: '#222020' }}
      />

      <defs>
        <linearGradient
          id="icon-not-found-gradient"
          x1="0"
          y1="0"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" style={{ stopColor: '#FB89EF', stopOpacity: '1' }} />
          <stop offset="0.4" style={{ stopColor: '#FB8989', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const support = () => {
  return (
    <svg
      className="icon icon-support"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M1.005,24.542c-0.001,-0.014 -0.001,-0.028 -0.001,-0.042c0.002,-2.778 2.005,-3.882 2.413,-4.16c0.164,-0.112 0.431,-0.193 0.782,-0.22c0.567,-0.044 1.428,0.027 2.303,0.016c0.567,-0.008 1.142,-0.05 1.631,-0.201c0.399,-0.124 0.74,-0.319 0.925,-0.669c0.129,-0.244 0.432,-0.337 0.676,-0.208c0.244,0.129 0.337,0.432 0.208,0.676c-0.252,0.475 -0.654,0.805 -1.154,1.023c-0.644,0.281 -1.463,0.368 -2.273,0.379c-0.738,0.009 -1.465,-0.042 -2.01,-0.03c-0.154,0.003 -0.291,0.012 -0.405,0.031c-0.049,0.009 -0.092,0.01 -0.12,0.029c-0.328,0.224 -1.975,1.095 -1.976,3.334c-0,0.014 -0.001,0.028 -0.002,0.042c0.001,0.013 0.002,0.027 0.002,0.041c-0,0.921 0.693,1.74 1.792,2.431c0.36,0.226 0.763,0.436 1.203,0.627l-0.001,-2.104c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l0.002,2.463c-0,0.008 -0.001,0.016 -0.001,0.024c1.683,0.563 3.759,0.893 6.003,0.893c2.242,-0 4.316,-0.329 5.999,-0.892c-0.001,-0.008 -0.001,-0.017 -0.001,-0.025l-0,-2.481c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l-0,2.124c0.442,-0.192 0.846,-0.403 1.207,-0.629c1.1,-0.691 1.793,-1.51 1.793,-2.431c-0,-0.014 0.001,-0.028 0.002,-0.041c-0.001,-0.014 -0.002,-0.028 -0.002,-0.042c-0.002,-2.239 -1.649,-3.11 -1.977,-3.334c-0.027,-0.019 -0.07,-0.02 -0.119,-0.029c-0.115,-0.019 -0.252,-0.028 -0.406,-0.031c-0.544,-0.012 -1.272,0.039 -2.01,0.03c-0.809,-0.011 -1.629,-0.098 -2.273,-0.379c-0.5,-0.218 -0.902,-0.548 -1.153,-1.023c-0.129,-0.244 -0.036,-0.547 0.208,-0.676c0.243,-0.129 0.546,-0.036 0.675,0.208c0.186,0.35 0.527,0.545 0.925,0.669c0.49,0.151 1.064,0.193 1.631,0.201c0.876,0.011 1.736,-0.06 2.304,-0.016c0.351,0.027 0.618,0.108 0.782,0.22c0.407,0.278 2.411,1.382 2.413,4.16c0,0.014 -0.001,0.028 -0.002,0.042c0.001,0.013 0.002,0.027 0.002,0.041c-0,1.214 -0.812,2.368 -2.261,3.278c-1.972,1.238 -5.15,2.056 -8.737,2.056c-3.587,-0 -6.766,-0.818 -8.738,-2.056c-1.449,-0.91 -2.26,-2.064 -2.26,-3.278c-0,-0.014 0,-0.028 0.001,-0.041Zm3.495,-12.926l-1,0c-0.398,0 -0.779,-0.158 -1.061,-0.439c-0.281,-0.282 -0.439,-0.663 -0.439,-1.061l-0,-2.471c-0,-0.397 0.158,-0.779 0.439,-1.06c0.163,-0.163 0.36,-0.285 0.574,-0.358c0.208,-3.244 2.852,-6.227 8.987,-6.227c6.137,-0 8.78,2.983 8.987,6.227c0.214,0.073 0.411,0.195 0.574,0.358c0.281,0.281 0.439,0.663 0.439,1.06l-0,2.471c0,0.398 -0.158,0.779 -0.439,1.061c-0.16,0.159 -0.352,0.279 -0.561,0.353l-0,1.97c0,0.398 -0.158,0.779 -0.439,1.061c-0.282,0.281 -0.663,0.439 -1.061,0.439l-4.585,0c-0.207,0.582 -0.762,1 -1.415,1c-0.828,-0 -1.5,-0.672 -1.5,-1.5c-0,-0.828 0.672,-1.5 1.5,-1.5c0.653,0 1.208,0.418 1.415,1l4.585,0c0.133,0 0.26,-0.053 0.354,-0.146c0.093,-0.094 0.146,-0.221 0.146,-0.354l-0,-1.884l-0.5,0c-0.276,0 -0.5,-0.224 -0.5,-0.5l-0,-4.471c0,-0.276 0.224,-0.5 0.5,-0.5l0.477,0c-0.257,-2.769 -2.691,-5.145 -7.977,-5.145c-5.284,-0 -7.719,2.377 -7.977,5.145l0.477,0c0.276,0 0.5,0.224 0.5,0.5l-0,4.471c-0,0.276 -0.224,0.5 -0.5,0.5Zm-1,-4.471c-0.133,0 -0.26,0.053 -0.354,0.147c-0.093,0.094 -0.146,0.221 -0.146,0.353l-0,2.471c-0,0.133 0.053,0.26 0.146,0.354c0.094,0.093 0.221,0.146 0.354,0.146l0.5,0l-0,-3.471l-0.5,0Zm10,6.855c-0.276,0 -0.5,0.224 -0.5,0.5c-0,0.276 0.224,0.5 0.5,0.5c0.276,-0 0.5,-0.224 0.5,-0.5c-0,-0.276 -0.224,-0.5 -0.5,-0.5Zm6.5,-3.384l0.5,0c0.133,0 0.26,-0.053 0.354,-0.146c0.093,-0.094 0.146,-0.221 0.146,-0.354l-0,-2.471c0,-0.132 -0.053,-0.259 -0.146,-0.353c-0.094,-0.094 -0.221,-0.147 -0.354,-0.147l-0.5,0l0,3.471Zm-6.728,6.067c0.246,-0.125 0.547,-0.028 0.673,0.218c0.126,0.245 0.028,0.547 -0.217,0.673c-0.536,0.274 -1.114,0.426 -1.728,0.426c-1.522,0 -2.827,-0.942 -3.821,-2.36c-1.394,-1.989 -2.179,-4.913 -2.179,-7.098c-0,-2.339 1.102,-6.542 6,-6.542c4.898,0 6,4.203 6,6.542c-0,1.236 -0.249,2.711 -0.72,4.117c-0.088,0.262 -0.372,0.403 -0.633,0.315c-0.262,-0.088 -0.403,-0.371 -0.315,-0.633c0.434,-1.298 0.668,-2.658 0.668,-3.799c-0,-1.967 -0.881,-5.542 -5,-5.542c-4.119,0 -5,3.575 -5,5.542c-0,2.008 0.717,4.696 1.998,6.524c0.79,1.128 1.792,1.934 3.002,1.934c0.453,-0 0.877,-0.115 1.272,-0.317Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const internalResource = () => {
  return (
    <svg
      className="icon icon-internal-resource"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M12,3c6.623,0 12,5.377 12,12c0,6.623 -5.377,12 -12,12c-6.623,0 -12,-5.377 -12,-12c-0,-6.623 5.377,-12 12,-12Zm0,1c-6.071,0 -11,4.929 -11,11c-0,6.071 4.929,11 11,11c6.071,0 11,-4.929 11,-11c0,-6.071 -4.929,-11 -11,-11Zm0.9,14c-0,0.497 -0.403,0.9 -0.9,0.9c-0.497,-0 -0.9,-0.403 -0.9,-0.9c0,-1.985 1.006,-3.055 2.054,-3.98c0.326,-0.288 0.653,-0.559 0.927,-0.857c0.292,-0.317 0.519,-0.667 0.519,-1.163c0,-1.087 -1.027,-2.105 -2.597,-2.1c-1.539,0.005 -2.603,0.582 -2.603,1.6c-0,0.497 -0.403,0.9 -0.9,0.9c-0.497,-0 -0.9,-0.403 -0.9,-0.9c0,-1.853 1.597,-3.391 4.397,-3.4c2.765,-0.009 4.403,1.986 4.403,3.9c-0,1.285 -0.665,2.124 -1.528,2.905c-0.842,0.761 -1.972,1.433 -1.972,3.095Zm0.1,2.5l-0,1c-0,0.276 -0.224,0.5 -0.5,0.5l-1,0c-0.276,-0 -0.5,-0.224 -0.5,-0.5l-0,-1c0,-0.276 0.224,-0.5 0.5,-0.5l1,0c0.276,0 0.5,0.224 0.5,0.5Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const docs = () => {
  return (
    <svg
      className="icon icon-docs"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M0.776,11.919c-0.494,-0.458 -0.788,-1.109 -0.788,-1.807l-0,-0.318c-0,-0.716 0.358,-1.384 0.954,-1.78c0.115,-0.076 0.236,-0.141 0.361,-0.193c0.004,-0.001 0.007,-0.003 0.011,-0.004c0.124,-0.051 0.252,-0.09 0.382,-0.116l12.636,-3.776c0.507,-0.206 1.08,-0.211 1.598,-0.004l7.756,3.102c0.246,0.099 0.371,0.372 0.289,0.62c-0.038,0.167 -0.162,0.31 -0.337,0.365l-1.65,0.519l-0,1.865l1.685,0.675c0.14,0.056 0.247,0.171 0.292,0.314c0.035,0.11 0.03,0.228 -0.013,0.336c-0.056,0.139 -0.171,0.246 -0.314,0.291l-2,0.629l-9.625,3.026l1.344,0.422l7.68,-3.224c0.659,-0.277 1.413,-0.205 2.009,0.191c0.596,0.396 0.954,1.063 0.954,1.779l0,0.318c-0,0.7 -0.296,1.351 -0.79,1.81l0.488,0.195l-0.002,0.004c0.13,0.056 0.235,0.165 0.281,0.31c0.083,0.263 -0.064,0.544 -0.327,0.627l-1.65,0.519l0,1.866l1.685,0.674c0.14,0.056 0.247,0.171 0.292,0.314c0.035,0.111 0.03,0.228 -0.013,0.336c-0.056,0.139 -0.171,0.246 -0.314,0.291l-2,0.629l-11,3.458c-0.113,0.036 -0.235,0.03 -0.344,-0.016l-8.796,-3.698c-0.915,-0.385 -1.51,-1.281 -1.51,-2.274l0,-0.317c-0,-0.716 0.358,-1.384 0.954,-1.78c0.596,-0.396 1.351,-0.467 2.011,-0.19l7.678,3.229l1.361,-0.428l-9.654,-3.035l-2,-0.628c-0.143,-0.046 -0.258,-0.152 -0.314,-0.292c-0.043,-0.107 -0.048,-0.225 -0.013,-0.335c0.045,-0.144 0.152,-0.259 0.292,-0.315l1.685,-0.674l0,-1.866l-1.65,-0.518c-0.263,-0.083 -0.41,-0.364 -0.327,-0.627c0.047,-0.149 0.157,-0.261 0.291,-0.315c0.003,-0.001 0.006,-0.002 0.009,-0.003l0.453,-0.181Zm0.894,-3.167c-0.061,0.026 -0.119,0.058 -0.175,0.095c-0.317,0.211 -0.507,0.566 -0.507,0.947l-0,0.318c-0,0.59 0.354,1.123 0.898,1.352l8.102,3.402l-0,-3.003l-7.424,-3.117c-0.202,-0.085 -0.421,-0.108 -0.631,-0.072l-0.263,0.078Zm12.973,-3.876l-10.933,3.267l6.921,2.906l11.437,-3.596l-6.51,-2.604c-0.28,-0.112 -0.59,-0.107 -0.863,0.008c-0.017,0.008 -0.034,0.014 -0.052,0.019Zm6.345,5.846l-0,-1.881l-10,3.144l-0,2.952l10,-3.144c-0,-0 -0,-1.065 -0,-1.071Zm-7.988,6.3l-10,-3.144c0,-0 0,1.892 -0,1.898l-0,1.053l10,3.144l0,-2.951Zm1,2.88l8.101,-3.401c0.545,-0.229 0.899,-0.761 0.899,-1.352l-0,-0.318c-0,-0.381 -0.19,-0.736 -0.507,-0.947c-0.317,-0.21 -0.719,-0.248 -1.069,-0.101l-7.424,3.117l0,3.002Zm7,0.907l-0,-1.881l-10,3.144l-0,2.952l10,-3.144c-0,-0 -0,-1.065 -0,-1.071Zm-11,4.144l-0,-3.003l-7.423,-3.121c-0.351,-0.148 -0.752,-0.11 -1.069,0.101c-0.317,0.211 -0.508,0.566 -0.508,0.947l0,0.317c-0,0.59 0.354,1.123 0.898,1.352l8.102,3.407Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const apiDocs = () => {
  return (
    <svg
      className="icon icon-api-docs"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M6,20c-3.311,0 -6,-2.689 -6,-6c-0,-3.311 2.689,-6 6,-6c0.306,0 0.606,0.023 0.899,0.067c1.201,-2.129 3.485,-3.567 6.101,-3.567c3.704,0 6.74,2.883 6.984,6.526c2.256,0.242 4.016,2.154 4.016,4.474c-0,2.484 -2.016,4.5 -4.5,4.5l-1,0c-0.276,0 -0.5,-0.224 -0.5,-0.5c-0,-0.276 0.224,-0.5 0.5,-0.5l1,0c1.932,0 3.5,-1.568 3.5,-3.5c-0,-1.932 -1.568,-3.5 -3.5,-3.5c-0.276,0 -0.5,-0.224 -0.5,-0.5c0,-3.311 -2.689,-6 -6,-6c-2.359,0 -4.402,1.365 -5.382,3.347c-0.101,0.205 -0.33,0.315 -0.554,0.267c-0.343,-0.075 -0.699,-0.114 -1.064,-0.114c-2.76,0 -5,2.24 -5,5c-0,2.76 2.24,5 5,5l0.624,0c0.276,0 0.5,0.224 0.5,0.5c-0,0.276 -0.224,0.5 -0.5,0.5l-0.624,0Zm3.145,5.837l-2.967,-2.984c-0.195,-0.196 -0.194,-0.513 0.002,-0.708c0.196,-0.194 0.513,-0.193 0.707,0.002l2.113,2.126l0,-9.773c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l-0,9.773l2.113,-2.126c0.194,-0.195 0.511,-0.196 0.707,-0.002c0.196,0.195 0.197,0.512 0.002,0.708l-2.967,2.984l-0.002,0.002c-0.096,0.095 -0.222,0.146 -0.353,0.146c-0.131,-0 -0.257,-0.051 -0.353,-0.146l-0.002,-0.002Zm4.888,-10.125l-2.113,2.125c-0.195,0.196 -0.512,0.197 -0.708,0.002c-0.195,-0.194 -0.196,-0.511 -0.002,-0.707l2.968,-2.984l0.002,-0.003c0.095,-0.094 0.222,-0.145 0.352,-0.145c0.131,0 0.258,0.051 0.353,0.145l0.002,0.003l2.968,2.984c0.194,0.196 0.193,0.513 -0.002,0.707c-0.196,0.195 -0.513,0.194 -0.708,-0.002l-2.113,-2.125l0,9.773c0,0.276 -0.224,0.5 -0.5,0.5c-0.275,-0 -0.5,-0.224 -0.5,-0.5l0.001,-9.773Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const password = () => {
  return (
    <svg
      className="icon icon-password"
      width="100%"
      height="100%"
      viewBox="0 0 21 31"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M15.515,11.016c0.799,0 1.447,0.649 1.447,1.448c0,0.799 -0.648,1.448 -1.447,1.448c-0.799,-0 -1.448,-0.649 -1.448,-1.448c0,-0.37 0.14,-0.708 0.368,-0.964c0.266,-0.297 0.651,-0.484 1.08,-0.484Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M13.495,18.579c-1.821,-0.784 -2.995,-2.56 -2.995,-4.667c-0,-2.825 2.184,-4.918 5.009,-4.918c2.824,-0 4.991,2.093 4.991,4.918c-0,2.107 -1.179,3.883 -3,4.667l-0,1.915l-1.5,1l1.5,1.5l-1.5,1l1.5,1.5l-1.5,1l1.5,1.5c-0,-0 -0.264,0.207 -1.739,1.365c-0.077,0.06 -0.155,0.117 -0.252,0.12c-0.098,0.004 -0.192,-0.032 -0.261,-0.1c-0.345,-0.335 -0.946,-0.921 -1.347,-1.312c-0.26,-0.253 -0.406,-0.6 -0.406,-0.962l0,-8.526Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M13.495,18.664c-0.904,0.339 -1.895,0.42 -2.85,0.222l-1.047,1.492l-2.111,-0.017l0.502,1.816l-1.878,0.147l0.496,1.811l-1.872,0.151l0.494,1.803c-0,-0 -1.472,0.359 -2.172,0.53c-0.095,0.023 -0.194,0.006 -0.276,-0.046c-0.082,-0.053 -0.138,-0.137 -0.156,-0.232c-0.09,-0.472 -0.246,-1.297 -0.35,-1.847c-0.067,-0.356 0.012,-0.724 0.22,-1.021l4.842,-6.906c-1.041,-1.688 -1.047,-3.904 0.163,-5.629c1.391,-1.985 3.94,-2.68 6.097,-1.813"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M9.522,7c-0.014,-0.172 -0.022,-0.346 -0.022,-0.521c-0,-3.303 2.712,-5.985 6.015,-5.985c3.303,-0 5.985,2.682 5.985,5.985c0,2.804 -1.933,5.16 -4.538,5.808"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
    </svg>
  );
};

const profile = () => {
  return (
    <svg
      className="icon icon-profile"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M10.65,7.5l-8.443,0c-0.942,0 -1.707,0.765 -1.707,1.707l0,12.586c0,0.942 0.765,1.707 1.707,1.707l19.586,0c0.942,0 1.707,-0.765 1.707,-1.707l-0,-12.586c-0,-0.942 -0.765,-1.707 -1.707,-1.707l-8.439,0"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M6.408,16.725c-0.576,-0.386 -0.96,-1.064 -1.187,-1.788c-0.015,0.012 -0.03,0.02 -0.046,0.023c-0.133,0.025 -0.378,-0.257 -0.439,-0.629c-0.061,-0.372 0.084,-0.694 0.217,-0.719c0.005,-0.001 0.01,-0.001 0.015,-0.001c-0.01,-0.137 -0.015,-0.27 -0.015,-0.396c0,-1.499 0.691,-2.715 2.547,-2.715c1.856,0 2.547,1.216 2.547,2.715c-0,0.126 -0.005,0.259 -0.015,0.396c0.005,-0 0.01,0 0.015,0.001c0.133,0.025 0.278,0.347 0.217,0.719c-0.061,0.372 -0.306,0.654 -0.439,0.629c-0.014,-0.003 -0.027,-0.009 -0.039,-0.018c-0.225,0.733 -0.608,1.417 -1.193,1.799c0.133,1.448 2.289,2.131 3.34,2.379c0.33,0.076 0.566,0.388 0.566,0.748c0.001,0.316 0.001,0.632 0.001,0.632l-10,0c0,0 0,-0.316 0.001,-0.632c-0,-0.36 0.236,-0.672 0.566,-0.748c1.055,-0.249 3.223,-0.936 3.341,-2.395Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M13.677,5.066c0.019,-0.143 -0.025,-0.287 -0.12,-0.395c-0.095,-0.109 -0.232,-0.171 -0.376,-0.171c-0.661,0 -1.741,0 -2.389,0c-0.136,-0 -0.266,0.059 -0.356,0.161c-0.09,0.103 -0.131,0.239 -0.113,0.375c0.106,0.8 0.304,2.293 0.402,3.03c0.033,0.248 0.245,0.434 0.495,0.434c0.442,0 1.121,0 1.563,0c0.251,0 0.463,-0.186 0.496,-0.434c0.097,-0.728 0.291,-2.193 0.398,-3Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path d="M13.5,13.987l7,-0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path d="M13.5,17l7,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
    </svg>
  );
};

const signout = () => {
  return (
    <svg
      className="icon icon-signout"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M15.5,6.5c4.105,1.433 7,5.353 7,9.959c0,5.818 -4.705,10.541 -10.5,10.541c-5.795,0 -10.5,-4.723 -10.5,-10.541c-0,-4.606 2.895,-8.526 7,-9.959"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path d="M12,3l-0,11.987" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
    </svg>
  );
};

const katalogueSmall = () => {
  return (
    <svg
      className="icon icon-katalogue-small"
      width="100%"
      height="100%"
      viewBox="0 0 37 37"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        className="dark"
        d="M18.166,0c10.027,0 18.167,8.14 18.167,18.166c-0,10.027 -8.14,18.167 -18.167,18.167c-10.026,-0 -18.166,-8.14 -18.166,-18.167c0,-10.026 8.14,-18.166 18.166,-18.166Zm2.556,9.472c0.055,0.115 0.315,0.939 -0.439,1.964c-0.806,1.094 -1.385,1.116 -1.582,1.072c-0.172,-0.038 -0.351,-0.058 -0.535,-0.058c-1.072,-0 -1.985,0.686 -2.324,1.643c-0.066,0.188 -0.281,0.466 -0.527,0.466c-0.267,0 -0.48,-0.289 -0.551,-0.483c-0.225,-0.621 -0.693,-1.127 -1.289,-1.402c-0.005,-0.002 -0.06,-0.026 -0.083,-0.036c-0.167,-0.07 -0.579,-0.35 -0.579,-0.987c-0,-0.623 0.425,-0.902 0.583,-0.968c0.02,-0.009 0.074,-0.033 0.079,-0.035c0.847,-0.39 1.436,-1.246 1.436,-2.239c-0,-1.36 -1.104,-2.464 -2.464,-2.464c-1.36,-0 -2.464,1.104 -2.464,2.464c-0,1.03 0.634,1.914 1.532,2.281c0.186,0.076 0.578,0.356 0.578,0.961c-0,0.62 -0.402,0.909 -0.576,0.98c-0.899,0.367 -1.534,1.252 -1.534,2.283c-0,1.031 0.634,1.915 1.534,2.282c0.173,0.071 0.576,0.356 0.576,0.96c-0,0.622 -0.4,0.908 -0.577,0.981c-0.899,0.367 -1.533,1.251 -1.533,2.282c-0,1.031 0.634,1.915 1.534,2.282c0.178,0.073 0.576,0.357 0.576,0.96c-0,0.622 -0.398,0.908 -0.577,0.981c-0.899,0.368 -1.533,1.251 -1.533,2.282c-0,1.36 1.104,2.464 2.464,2.464c1.36,0 2.464,-1.104 2.464,-2.464c-0,-0.981 -0.575,-1.828 -1.405,-2.225c-0.015,-0.007 -0.09,-0.041 -0.112,-0.05c-0.166,-0.069 -0.581,-0.349 -0.581,-0.988c-0,-0.621 0.411,-0.896 0.581,-0.967c0.022,-0.009 0.121,-0.054 0.148,-0.068c0.811,-0.403 1.369,-1.241 1.369,-2.207c-0,-0.986 -0.581,-1.838 -1.419,-2.232c-0.01,-0.004 -0.062,-0.025 -0.098,-0.043c-0.166,-0.081 -0.581,-0.349 -0.581,-0.988c-0,-0.621 0.413,-0.897 0.581,-0.967c0.022,-0.009 0.076,-0.033 0.081,-0.036c0.593,-0.273 1.059,-0.774 1.286,-1.391c0.071,-0.194 0.283,-0.483 0.554,-0.483c0.249,-0 0.459,0.265 0.531,0.465c0.341,0.952 1.252,1.634 2.32,1.634c0.201,-0 0.395,-0.024 0.582,-0.069c0.19,-0.046 0.716,-0.034 1.175,0.586c0.48,0.648 0.308,1.187 0.22,1.337c-0.215,0.365 -0.339,0.791 -0.339,1.246c0,1.287 0.989,2.344 2.247,2.454c0.23,0.021 0.713,0.232 1.082,1.363c0.379,1.165 0.069,1.66 -0.084,1.798c-0.501,0.451 -0.816,1.105 -0.816,1.831c-0,1.36 1.104,2.464 2.464,2.464c1.36,0 2.464,-1.104 2.464,-2.464c0,-1.277 -0.974,-2.329 -2.219,-2.452c-0.172,-0.017 -0.744,-0.231 -1.125,-1.4c-0.375,-1.152 -0.044,-1.632 0.11,-1.772c0.495,-0.451 0.805,-1.101 0.805,-1.822c0,-1.36 -1.104,-2.464 -2.464,-2.464c-0.185,-0 -0.365,0.02 -0.538,0.059c-0.136,0.03 -0.717,0.084 -1.228,-0.606c-0.449,-0.606 -0.338,-1.094 -0.226,-1.28c0.225,-0.372 0.354,-0.807 0.354,-1.273c0,-0.46 -0.126,-0.891 -0.346,-1.26c-0.101,-0.169 -0.2,-0.734 0.578,-1.792c0.732,-0.994 1.696,-1.012 1.883,-0.997c0.066,0.005 0.133,0.008 0.2,0.008c1.36,-0 2.464,-1.104 2.464,-2.464c0,-1.36 -1.104,-2.464 -2.464,-2.464c-1.36,-0 -2.464,1.104 -2.464,2.464c0,0.38 0.087,0.741 0.241,1.063Z"
        style={{ fill: 'url(#gradient-katalogue-small)' }}
      />

      <path
        className="light"
        d="M18.166,-0c-10.026,-0 -18.166,8.14 -18.166,18.166c0,10.027 8.14,18.167 18.166,18.167c10.027,-0 18.167,-8.14 18.167,-18.167c-0,-10.026 -8.14,-18.166 -18.167,-18.166Zm0,0.609c9.691,-0 17.558,7.867 17.558,17.557c0,9.691 -7.867,17.558 -17.558,17.558c-9.69,0 -17.557,-7.867 -17.557,-17.558c-0,-9.69 7.867,-17.557 17.557,-17.557Zm2.513,9.008c-0.152,-0.316 -0.237,-0.671 -0.237,-1.045c0,-1.337 1.086,-2.423 2.423,-2.423c1.337,0 2.423,1.086 2.423,2.423c-0,1.337 -1.086,2.423 -2.423,2.423c-0.066,-0 -0.132,-0.003 -0.196,-0.008c-0.184,-0.015 -1.133,0.004 -1.852,0.981c-0.765,1.04 -0.668,1.595 -0.568,1.762c0.216,0.362 0.34,0.786 0.34,1.238c0,0.458 -0.127,0.887 -0.348,1.252c-0.111,0.183 -0.22,0.663 0.222,1.258c0.502,0.679 1.074,0.626 1.207,0.597c0.17,-0.039 0.348,-0.059 0.53,-0.059c1.337,0 2.422,1.086 2.422,2.423c0,0.71 -0.305,1.348 -0.792,1.791c-0.151,0.138 -0.476,0.611 -0.107,1.743c0.374,1.15 0.936,1.36 1.105,1.377c1.225,0.121 2.182,1.155 2.182,2.411c0,1.337 -1.085,2.422 -2.422,2.422c-1.338,0 -2.423,-1.085 -2.423,-2.422c-0,-0.715 0.31,-1.357 0.802,-1.801c0.151,-0.136 0.456,-0.622 0.083,-1.767c-0.363,-1.113 -0.838,-1.321 -1.064,-1.34c-1.237,-0.109 -2.209,-1.149 -2.209,-2.414c-0,-0.447 0.121,-0.866 0.332,-1.225c0.087,-0.147 0.257,-0.678 -0.215,-1.314c-0.452,-0.611 -0.969,-0.622 -1.156,-0.577c-0.183,0.045 -0.375,0.068 -0.572,0.068c-1.05,0 -1.946,-0.67 -2.281,-1.606c-0.071,-0.197 -0.277,-0.458 -0.523,-0.458c-0.265,0 -0.474,0.285 -0.544,0.475c-0.223,0.607 -0.681,1.1 -1.264,1.368c-0.005,0.002 -0.059,0.026 -0.08,0.035c-0.165,0.069 -0.572,0.341 -0.572,0.951c0,0.629 0.409,0.892 0.572,0.972c0.036,0.017 0.086,0.037 0.096,0.042c0.824,0.387 1.395,1.225 1.395,2.194c0,0.951 -0.548,1.774 -1.345,2.171c-0.027,0.013 -0.125,0.058 -0.146,0.066c-0.167,0.07 -0.572,0.341 -0.572,0.952c0,0.628 0.409,0.903 0.572,0.971c0.022,0.009 0.095,0.042 0.11,0.049c0.817,0.39 1.381,1.223 1.381,2.188c0,1.337 -1.085,2.422 -2.423,2.422c-1.337,0 -2.422,-1.085 -2.422,-2.422c-0,-1.014 0.623,-1.883 1.508,-2.244c0.175,-0.072 0.567,-0.353 0.567,-0.964c-0,-0.594 -0.392,-0.873 -0.567,-0.944c-0.884,-0.362 -1.508,-1.231 -1.508,-2.245c-0,-1.013 0.623,-1.882 1.508,-2.243c0.174,-0.072 0.567,-0.353 0.567,-0.965c-0,-0.593 -0.396,-0.874 -0.567,-0.944c-0.884,-0.361 -1.508,-1.23 -1.508,-2.244c-0,-1.014 0.624,-1.883 1.509,-2.244c0.171,-0.07 0.566,-0.354 0.566,-0.964c-0,-0.595 -0.385,-0.87 -0.568,-0.944c-0.884,-0.362 -1.507,-1.231 -1.507,-2.244c-0,-1.337 1.085,-2.423 2.422,-2.423c1.338,0 2.423,1.086 2.423,2.423c0,0.977 -0.579,1.819 -1.411,2.202c-0.005,0.002 -0.058,0.026 -0.078,0.034c-0.156,0.065 -0.574,0.339 -0.574,0.952c0,0.626 0.406,0.902 0.57,0.971c0.023,0.009 0.077,0.033 0.082,0.035c0.586,0.27 1.046,0.767 1.267,1.379c0.069,0.19 0.279,0.475 0.541,0.475c0.243,-0 0.454,-0.274 0.519,-0.458c0.333,-0.941 1.231,-1.616 2.285,-1.616c0.181,-0 0.357,0.019 0.526,0.057c0.193,0.043 0.763,0.022 1.555,-1.055c0.742,-1.007 0.486,-1.818 0.432,-1.931Z"
        style={{ fill: 'url(#gradient-katalogue-small)' }}
      />

      <defs>
        <linearGradient
          id="gradient-katalogue-small"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const katalogue = ({ className }) => {
  return (
    <svg
      className={'icon icon-katalogue ' + (className || '')}
      width="100%"
      height="100%"
      viewBox="0 0 179 239"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 2,
      }}
    >
      <path
        id="logo-filled"
        className="dark katalogue-icon"
        d="M89.293,0c49.282,0 89.292,40.011 89.292,89.293c0,49.282 -40.01,89.292 -89.292,89.292c-49.282,0 -89.293,-40.01 -89.293,-89.292c0,-49.282 40.011,-89.293 89.293,-89.293Zm12.56,46.555c0.272,0.566 1.547,4.618 -2.158,9.654c-3.96,5.381 -6.808,5.485 -7.773,5.272c-0.847,-0.188 -1.726,-0.287 -2.629,-0.287c-5.271,-0 -9.761,3.374 -11.423,8.079c-0.326,0.921 -1.383,2.29 -2.595,2.29c-1.31,-0 -2.359,-1.422 -2.704,-2.374c-1.106,-3.056 -3.407,-5.542 -6.338,-6.892c-0.022,-0.01 -0.294,-0.131 -0.406,-0.177c-0.822,-0.343 -2.85,-1.722 -2.85,-4.851c0,-3.065 2.09,-4.434 2.867,-4.759c0.101,-0.042 0.366,-0.16 0.389,-0.171c4.164,-1.917 7.057,-6.127 7.057,-11.008c-0,-6.685 -5.427,-12.112 -12.112,-12.112c-6.684,0 -12.111,5.427 -12.111,12.112c-0,5.065 3.116,9.408 7.534,11.215c0.913,0.374 2.839,1.747 2.839,4.723c-0,3.047 -1.973,4.468 -2.829,4.817c-4.423,1.804 -7.544,6.15 -7.544,11.219c-0,5.068 3.119,9.413 7.541,11.219c0.854,0.348 2.832,1.751 2.832,4.719c-0,3.055 -1.964,4.464 -2.835,4.82c-4.42,1.806 -7.538,6.15 -7.538,11.217c-0,5.068 3.119,9.413 7.541,11.218c0.876,0.358 2.832,1.751 2.832,4.719c-0,3.055 -1.956,4.461 -2.835,4.821c-4.42,1.806 -7.538,6.15 -7.538,11.216c-0,6.685 5.427,12.112 12.111,12.112c6.685,-0 12.112,-5.427 12.112,-12.112c-0,-4.821 -2.823,-8.987 -6.905,-10.936c-0.073,-0.035 -0.442,-0.201 -0.548,-0.245c-0.817,-0.341 -2.86,-1.715 -2.86,-4.856c0,-3.053 2.023,-4.406 2.857,-4.754c0.107,-0.045 0.598,-0.268 0.73,-0.333c3.985,-1.984 6.726,-6.1 6.726,-10.85c-0,-4.848 -2.855,-9.035 -6.973,-10.969c-0.051,-0.024 -0.304,-0.127 -0.48,-0.213c-0.817,-0.4 -2.86,-1.714 -2.86,-4.855c0,-3.053 2.034,-4.411 2.857,-4.755c0.107,-0.044 0.376,-0.164 0.399,-0.175c2.914,-1.341 5.206,-3.807 6.319,-6.839c0.35,-0.952 1.395,-2.374 2.723,-2.374c1.227,0 2.259,1.303 2.612,2.287c1.677,4.679 6.153,8.03 11.406,8.03c0.984,-0 1.941,-0.118 2.857,-0.34c0.933,-0.226 3.519,-0.168 5.779,2.882c2.358,3.184 1.509,5.835 1.077,6.57c-1.057,1.797 -1.663,3.891 -1.663,6.125c0,6.324 4.858,11.523 11.043,12.065c1.131,0.099 3.506,1.137 5.318,6.699c1.866,5.726 0.341,8.157 -0.412,8.835c-2.463,2.218 -4.012,5.43 -4.012,9.001c0,6.685 5.427,12.112 12.112,12.112c6.685,-0 12.112,-5.427 12.112,-12.112c-0,-6.278 -4.788,-11.447 -10.909,-12.052c-0.843,-0.083 -3.655,-1.134 -5.527,-6.88c-1.844,-5.662 -0.217,-8.025 0.538,-8.713c2.433,-2.216 3.96,-5.408 3.96,-8.955c0,-6.684 -5.427,-12.111 -12.111,-12.111c-0.91,-0 -1.796,0.1 -2.648,0.29c-0.666,0.149 -3.522,0.414 -6.036,-2.979c-2.207,-2.98 -1.661,-5.378 -1.109,-6.291c1.106,-1.827 1.742,-3.968 1.742,-6.258c0,-2.261 -0.621,-4.379 -1.702,-6.191c-0.497,-0.833 -0.985,-3.608 2.842,-8.808c3.595,-4.886 8.336,-4.977 9.256,-4.903c0.324,0.026 0.651,0.039 0.982,0.039c6.684,0 12.111,-5.427 12.111,-12.111c0,-6.685 -5.427,-12.112 -12.111,-12.112c-6.685,0 -12.112,5.427 -12.112,12.112c0,1.87 0.425,3.642 1.183,5.224Z"
        style={{ fill: 'url(#gradient-katalogue)' }}
      />
      <path
        id="logo"
        className="light katalogue-icon"
        d="M89.293,0c-49.282,0 -89.293,40.011 -89.293,89.293c0,49.282 40.011,89.292 89.293,89.292c49.282,0 89.292,-40.01 89.292,-89.292c0,-49.282 -40.01,-89.293 -89.292,-89.293Zm-0,2.992c47.63,0 86.3,38.67 86.3,86.301c-0,47.63 -38.67,86.3 -86.3,86.3c-47.631,-0 -86.301,-38.67 -86.301,-86.3c0,-47.631 38.67,-86.301 86.301,-86.301Zm12.35,44.279c-0.746,-1.555 -1.163,-3.297 -1.163,-5.136c-0,-6.573 5.336,-11.909 11.908,-11.909c6.573,-0 11.909,5.336 11.909,11.909c-0,6.572 -5.336,11.908 -11.909,11.908c-0.325,0 -0.647,-0.013 -0.965,-0.038c-0.905,-0.073 -5.566,0.017 -9.101,4.82c-3.763,5.113 -3.283,7.842 -2.795,8.66c1.063,1.782 1.674,3.864 1.674,6.088c0,2.251 -0.626,4.357 -1.712,6.153c-0.544,0.898 -1.08,3.256 1.09,6.185c2.471,3.337 5.279,3.076 5.934,2.93c0.839,-0.187 1.71,-0.286 2.604,-0.286c6.572,0 11.909,5.336 11.909,11.909c-0,3.487 -1.503,6.626 -3.894,8.805c-0.743,0.676 -2.343,3 -0.529,8.567c1.84,5.65 4.605,6.682 5.434,6.764c6.018,0.595 10.726,5.677 10.726,11.851c-0,6.572 -5.336,11.908 -11.909,11.908c-6.572,0 -11.908,-5.336 -11.908,-11.908c-0,-3.512 1.523,-6.67 3.944,-8.851c0.74,-0.667 2.239,-3.057 0.405,-8.687c-1.781,-5.469 -4.117,-6.489 -5.229,-6.587c-6.081,-0.532 -10.858,-5.644 -10.858,-11.862c0,-2.197 0.596,-4.255 1.635,-6.023c0.425,-0.723 1.259,-3.329 -1.059,-6.459c-2.222,-3 -4.765,-3.056 -5.681,-2.834c-0.902,0.218 -1.843,0.334 -2.81,0.334c-5.165,-0 -9.566,-3.295 -11.215,-7.895c-0.347,-0.968 -1.362,-2.249 -2.568,-2.249c-1.306,0 -2.334,1.398 -2.677,2.334c-1.095,2.982 -3.348,5.406 -6.214,6.725c-0.022,0.01 -0.287,0.128 -0.392,0.172c-0.81,0.338 -2.809,1.673 -2.809,4.674c0,3.089 2.009,4.381 2.812,4.774c0.173,0.085 0.422,0.186 0.472,0.21c4.049,1.902 6.856,6.018 6.856,10.785c0,4.671 -2.695,8.717 -6.614,10.668c-0.129,0.064 -0.612,0.284 -0.717,0.327c-0.82,0.343 -2.809,1.673 -2.809,4.675c0,3.089 2.009,4.439 2.812,4.774c0.104,0.044 0.467,0.207 0.539,0.242c4.013,1.916 6.789,6.013 6.789,10.753c0,6.572 -5.336,11.908 -11.909,11.908c-6.572,0 -11.908,-5.336 -11.908,-11.908c-0,-4.982 3.065,-9.253 7.411,-11.029c0.865,-0.353 2.788,-1.736 2.788,-4.74c-0,-2.918 -1.923,-4.288 -2.784,-4.64c-4.348,-1.775 -7.415,-6.047 -7.415,-11.03c-0,-4.982 3.065,-9.253 7.411,-11.029c0.857,-0.35 2.788,-1.736 2.788,-4.74c-0,-2.918 -1.945,-4.297 -2.784,-4.639c-4.348,-1.775 -7.415,-6.048 -7.415,-11.031c-0,-4.984 3.069,-9.257 7.418,-11.031c0.841,-0.343 2.781,-1.74 2.781,-4.737c-0,-2.926 -1.893,-4.276 -2.791,-4.643c-4.344,-1.777 -7.408,-6.047 -7.408,-11.027c-0,-6.573 5.336,-11.909 11.908,-11.909c6.573,-0 11.909,5.336 11.909,11.909c0,4.799 -2.845,8.938 -6.939,10.823c-0.022,0.011 -0.283,0.127 -0.382,0.168c-0.764,0.32 -2.819,1.666 -2.819,4.679c0,3.077 1.994,4.433 2.803,4.77c0.109,0.046 0.376,0.165 0.398,0.175c2.882,1.326 5.145,3.771 6.232,6.776c0.339,0.936 1.37,2.334 2.659,2.334c1.192,-0 2.231,-1.346 2.551,-2.251c1.635,-4.626 6.049,-7.944 11.232,-7.944c0.887,-0 1.753,0.097 2.585,0.282c0.948,0.21 3.749,0.107 7.642,-5.184c3.644,-4.951 2.39,-8.935 2.123,-9.492Z"
        style={{ fill: 'url(#gradient-katalogue)' }}
      />
      <path
        id="text"
        d="M2.314,230.575c-0.247,-0 -0.446,-0.075 -0.595,-0.223c-0.148,-0.149 -0.223,-0.347 -0.223,-0.595l0,-24.772c0,-0.248 0.075,-0.446 0.223,-0.595c0.149,-0.149 0.348,-0.223 0.595,-0.223l0.633,0c0.248,0 0.446,0.074 0.595,0.223c0.149,0.149 0.223,0.347 0.223,0.595l0,13.167l8.294,-6.286c0.397,-0.297 0.67,-0.477 0.819,-0.539c0.148,-0.062 0.396,-0.093 0.743,-0.093l0.707,-0c0.223,-0 0.403,0.068 0.539,0.204c0.137,0.137 0.205,0.317 0.205,0.54c0,0.273 -0.161,0.521 -0.483,0.744l-9.411,7.141l10.34,9.224c0.323,0.298 0.484,0.546 0.484,0.744c-0,0.223 -0.068,0.403 -0.205,0.539c-0.136,0.137 -0.316,0.205 -0.539,0.205l-0.595,-0c-0.372,-0 -0.645,-0.031 -0.818,-0.093c-0.174,-0.062 -0.422,-0.242 -0.744,-0.539l-9.336,-8.295l0,8.109c0,0.248 -0.074,0.446 -0.223,0.595c-0.149,0.148 -0.347,0.223 -0.595,0.223l-0.633,-0Zm24.214,0.372c-1.19,-0 -2.294,-0.248 -3.31,-0.744c-1.017,-0.496 -1.829,-1.166 -2.437,-2.009c-0.607,-0.843 -0.911,-1.773 -0.911,-2.789c0,-1.637 0.663,-2.976 1.99,-4.017c1.327,-1.042 3.118,-1.711 5.374,-2.009l5.877,-0.818l0,-1.302c0,-1.363 -0.415,-2.411 -1.246,-3.143c-0.831,-0.731 -2.101,-1.097 -3.812,-1.097c-1.265,0 -2.3,0.248 -3.106,0.744c-0.806,0.496 -1.32,1.091 -1.544,1.785c-0.099,0.298 -0.216,0.503 -0.353,0.614c-0.136,0.112 -0.316,0.168 -0.539,0.168l-0.521,-0c-0.223,-0 -0.415,-0.081 -0.576,-0.242c-0.162,-0.161 -0.242,-0.354 -0.242,-0.577c-0,-0.545 0.248,-1.184 0.744,-1.915c0.496,-0.732 1.264,-1.37 2.306,-1.916c1.041,-0.545 2.318,-0.818 3.831,-0.818c2.653,-0 4.537,0.632 5.653,1.897c1.116,1.264 1.674,2.802 1.674,4.612l-0,12.386c-0,0.248 -0.074,0.446 -0.223,0.595c-0.149,0.148 -0.347,0.223 -0.595,0.223l-0.633,-0c-0.248,-0 -0.446,-0.075 -0.595,-0.223c-0.148,-0.149 -0.223,-0.347 -0.223,-0.595l0,-1.749c-0.545,0.819 -1.345,1.513 -2.399,2.083c-1.054,0.571 -2.448,0.856 -4.184,0.856Zm0.372,-2.158c1.81,0 3.298,-0.595 4.463,-1.785c1.165,-1.19 1.748,-2.901 1.748,-5.133l0,-1.264l-4.798,0.669c-2.033,0.273 -3.571,0.744 -4.612,1.414c-1.041,0.669 -1.562,1.512 -1.562,2.529c-0,1.14 0.477,2.021 1.432,2.641c0.955,0.62 2.064,0.929 3.329,0.929Zm22.8,1.786c-1.835,-0 -3.137,-0.552 -3.906,-1.655c-0.768,-1.104 -1.153,-2.623 -1.153,-4.557l0,-10.972l-2.901,0c-0.248,0 -0.446,-0.074 -0.595,-0.223c-0.149,-0.149 -0.223,-0.347 -0.223,-0.595l-0,-0.521c-0,-0.248 0.074,-0.446 0.223,-0.595c0.149,-0.149 0.347,-0.223 0.595,-0.223l2.901,-0l0,-6.249c0,-0.248 0.075,-0.446 0.223,-0.595c0.149,-0.149 0.348,-0.223 0.596,-0.223l0.632,0c0.248,0 0.446,0.074 0.595,0.223c0.149,0.149 0.223,0.347 0.223,0.595l0,6.249l4.612,-0c0.248,-0 0.446,0.074 0.595,0.223c0.149,0.149 0.223,0.347 0.223,0.595l0,0.521c0,0.248 -0.074,0.446 -0.223,0.595c-0.149,0.149 -0.347,0.223 -0.595,0.223l-4.612,0l0,10.824c0,1.388 0.229,2.436 0.688,3.143c0.459,0.706 1.221,1.06 2.288,1.06l2.008,-0c0.248,-0 0.446,0.074 0.595,0.223c0.149,0.148 0.223,0.347 0.223,0.595l0,0.521c0,0.248 -0.074,0.446 -0.223,0.595c-0.149,0.148 -0.347,0.223 -0.595,0.223l-2.194,-0Zm14.171,0.372c-1.191,-0 -2.294,-0.248 -3.311,-0.744c-1.016,-0.496 -1.828,-1.166 -2.436,-2.009c-0.607,-0.843 -0.911,-1.773 -0.911,-2.789c-0,-1.637 0.663,-2.976 1.99,-4.017c1.326,-1.042 3.118,-1.711 5.374,-2.009l5.877,-0.818l-0,-1.302c-0,-1.363 -0.415,-2.411 -1.246,-3.143c-0.831,-0.731 -2.102,-1.097 -3.812,-1.097c-1.265,0 -2.3,0.248 -3.106,0.744c-0.806,0.496 -1.321,1.091 -1.544,1.785c-0.099,0.298 -0.217,0.503 -0.353,0.614c-0.136,0.112 -0.316,0.168 -0.539,0.168l-0.521,-0c-0.223,-0 -0.415,-0.081 -0.577,-0.242c-0.161,-0.161 -0.241,-0.354 -0.241,-0.577c-0,-0.545 0.248,-1.184 0.744,-1.915c0.495,-0.732 1.264,-1.37 2.306,-1.916c1.041,-0.545 2.318,-0.818 3.831,-0.818c2.653,-0 4.537,0.632 5.653,1.897c1.116,1.264 1.674,2.802 1.674,4.612l-0,12.386c-0,0.248 -0.075,0.446 -0.223,0.595c-0.149,0.148 -0.348,0.223 -0.596,0.223l-0.632,-0c-0.248,-0 -0.446,-0.075 -0.595,-0.223c-0.149,-0.149 -0.223,-0.347 -0.223,-0.595l-0,-1.749c-0.546,0.819 -1.345,1.513 -2.399,2.083c-1.054,0.571 -2.449,0.856 -4.184,0.856Zm0.372,-2.158c1.81,0 3.297,-0.595 4.463,-1.785c1.165,-1.19 1.748,-2.901 1.748,-5.133l-0,-1.264l-4.798,0.669c-2.033,0.273 -3.571,0.744 -4.612,1.414c-1.042,0.669 -1.562,1.512 -1.562,2.529c-0,1.14 0.477,2.021 1.432,2.641c0.954,0.62 2.064,0.929 3.329,0.929Zm17.332,1.786c-0.248,-0 -0.446,-0.075 -0.595,-0.223c-0.149,-0.149 -0.223,-0.347 -0.223,-0.595l-0,-24.772c-0,-0.248 0.074,-0.446 0.223,-0.595c0.149,-0.149 0.347,-0.223 0.595,-0.223l0.632,0c0.248,0 0.447,0.074 0.595,0.223c0.149,0.149 0.223,0.347 0.223,0.595l0,24.772c0,0.248 -0.074,0.446 -0.223,0.595c-0.148,0.148 -0.347,0.223 -0.595,0.223l-0.632,-0Zm16.998,0.372c-2.579,-0 -4.581,-0.788 -6.007,-2.362c-1.426,-1.575 -2.176,-3.614 -2.25,-6.118l-0.038,-1.563l0.038,-1.562c0.074,-2.504 0.824,-4.544 2.25,-6.118c1.426,-1.575 3.428,-2.362 6.007,-2.362c2.578,-0 4.581,0.787 6.006,2.362c1.426,1.574 2.176,3.614 2.251,6.118c0.024,0.248 0.037,0.769 0.037,1.562c-0,0.794 -0.013,1.315 -0.037,1.563c-0.075,2.504 -0.825,4.543 -2.251,6.118c-1.425,1.574 -3.428,2.362 -6.006,2.362Zm-0,-2.158c1.76,0 3.18,-0.564 4.258,-1.692c1.079,-1.128 1.655,-2.734 1.73,-4.816c0.025,-0.248 0.037,-0.707 0.037,-1.377c0,-0.669 -0.012,-1.128 -0.037,-1.376c-0.075,-2.083 -0.651,-3.688 -1.73,-4.816c-1.078,-1.129 -2.498,-1.693 -4.258,-1.693c-1.761,0 -3.181,0.564 -4.259,1.693c-1.079,1.128 -1.655,2.733 -1.73,4.816l-0.037,1.376l0.037,1.377c0.075,2.082 0.651,3.688 1.73,4.816c1.078,1.128 2.498,1.692 4.259,1.692Zm22.725,9.968c-1.934,0 -3.484,-0.334 -4.649,-1.004c-1.166,-0.669 -1.978,-1.432 -2.436,-2.287c-0.459,-0.856 -0.688,-1.606 -0.688,-2.25c-0,-0.224 0.08,-0.41 0.241,-0.558c0.161,-0.149 0.354,-0.223 0.577,-0.223l0.521,-0c0.223,-0 0.402,0.055 0.539,0.167c0.136,0.111 0.254,0.316 0.353,0.614c0.769,2.256 2.554,3.384 5.356,3.384c2.133,0 3.707,-0.44 4.724,-1.32c1.016,-0.88 1.525,-2.449 1.525,-4.705l-0,-2.567c-1.438,1.959 -3.521,2.939 -6.249,2.939c-2.678,-0 -4.655,-0.874 -5.932,-2.622c-1.277,-1.749 -1.953,-3.875 -2.027,-6.379l-0.038,-1.042l0.038,-1.041c0.074,-2.504 0.75,-4.631 2.027,-6.379c1.277,-1.748 3.254,-2.622 5.932,-2.622c2.703,-0 4.786,0.979 6.249,2.938l-0,-1.711c-0,-0.248 0.074,-0.452 0.223,-0.613c0.149,-0.161 0.347,-0.242 0.595,-0.242l0.595,-0c0.248,-0 0.453,0.081 0.614,0.242c0.161,0.161 0.242,0.365 0.242,0.613l-0,18.114c-0,2.653 -0.62,4.742 -1.86,6.267c-1.24,1.525 -3.397,2.287 -6.472,2.287Zm0.037,-9.968c1.959,0 3.435,-0.65 4.426,-1.952c0.992,-1.302 1.525,-2.833 1.6,-4.594c0.025,-0.248 0.037,-0.694 0.037,-1.339c-0,-0.644 -0.012,-1.091 -0.037,-1.339c-0.075,-1.76 -0.608,-3.291 -1.6,-4.593c-0.991,-1.302 -2.467,-1.953 -4.426,-1.953c-2.008,0 -3.477,0.639 -4.407,1.916c-0.93,1.277 -1.432,2.919 -1.507,4.928l-0.037,1.041l0.037,1.042c0.075,2.008 0.577,3.651 1.507,4.928c0.93,1.277 2.399,1.915 4.407,1.915Zm23.73,2.158c-2.43,-0 -4.277,-0.769 -5.542,-2.306c-1.264,-1.538 -1.897,-3.546 -1.897,-6.026l0,-10.563c0,-0.248 0.075,-0.446 0.223,-0.595c0.149,-0.149 0.348,-0.223 0.596,-0.223l0.632,-0c0.248,-0 0.446,0.074 0.595,0.223c0.149,0.149 0.223,0.347 0.223,0.595l0,10.377c0,4.24 1.847,6.36 5.542,6.36c1.785,0 3.205,-0.564 4.259,-1.692c1.054,-1.128 1.58,-2.684 1.58,-4.668l0,-10.377c0,-0.248 0.075,-0.446 0.224,-0.595c0.148,-0.149 0.347,-0.223 0.595,-0.223l0.632,-0c0.248,-0 0.446,0.074 0.595,0.223c0.149,0.149 0.223,0.347 0.223,0.595l0,17.705c0,0.248 -0.074,0.446 -0.223,0.595c-0.149,0.148 -0.347,0.223 -0.595,0.223l-0.632,-0c-0.248,-0 -0.447,-0.075 -0.595,-0.223c-0.149,-0.149 -0.224,-0.347 -0.224,-0.595l0,-1.749c-0.743,0.967 -1.593,1.699 -2.547,2.195c-0.955,0.496 -2.176,0.744 -3.664,0.744Zm23.804,-0c-2.355,-0 -4.252,-0.775 -5.69,-2.325c-1.439,-1.55 -2.257,-3.626 -2.455,-6.23l-0.037,-1.488l0.037,-1.487c0.198,-2.579 1.01,-4.65 2.436,-6.212c1.426,-1.562 3.329,-2.343 5.709,-2.343c2.579,-0 4.594,0.855 6.044,2.566c1.451,1.711 2.176,4.055 2.176,7.03l0,0.558c0,0.248 -0.08,0.446 -0.242,0.595c-0.161,0.149 -0.365,0.223 -0.613,0.223l-13.241,0l-0,0.372c0.049,1.166 0.316,2.25 0.799,3.255c0.484,1.004 1.159,1.81 2.027,2.417c0.868,0.608 1.885,0.911 3.05,0.911c1.364,0 2.474,-0.266 3.329,-0.799c0.856,-0.533 1.469,-1.073 1.841,-1.618c0.223,-0.298 0.391,-0.484 0.502,-0.558c0.112,-0.074 0.316,-0.112 0.614,-0.112l0.595,0c0.223,0 0.409,0.062 0.558,0.186c0.149,0.124 0.223,0.286 0.223,0.484c0,0.521 -0.328,1.147 -0.985,1.878c-0.658,0.732 -1.569,1.364 -2.734,1.897c-1.166,0.533 -2.48,0.8 -3.943,0.8Zm5.951,-11.196l0,-0.148c0,-1.91 -0.539,-3.484 -1.618,-4.724c-1.078,-1.24 -2.523,-1.86 -4.333,-1.86c-1.81,0 -3.242,0.62 -4.296,1.86c-1.053,1.24 -1.58,2.814 -1.58,4.724l-0,0.148l11.827,0Z"
        style={{ fill: 'url(#gradient-katalogue)' }}
      />

      <defs>
        <linearGradient
          id="gradient-katalogue"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>

        <radialGradient
          id="gradient-katalogue-loading"
          fx="50%"
          fy="30%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#222020">
            <animate
              attributeName="stop-color"
              values="#222020;#FBE9B7;#E4C3FF;#B6CFFF;#222020;#222020"
              dur="2s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="100%" stopColor="#222020">
            <animate
              attributeName="stop-color"
              values="#222020;#222020;#FBE9B7;#E4C3FF;#B6CFFF;#222020"
              dur="2s"
              repeatCount="indefinite"
            />
          </stop>
        </radialGradient>

        <radialGradient
          id="gradient-katalogue-loading-dark"
          fx="50%"
          fy="30%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#EBDAF0">
            <animate
              attributeName="stop-color"
              values="#EBDAF0;#FBE9B7CC;#E4C3FF99;#B6CFFF77;#fff;#EBDAF0"
              dur="2s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="100%" stopColor="#CFDFFC">
            <animate
              attributeName="stop-color"
              values="#CFDFFC;#fff;#FBE9B7CC;#E4C3FF99;#B6CFFF77;#CFDFFC"
              dur="2s"
              repeatCount="indefinite"
            />
          </stop>
        </radialGradient>
      </defs>
    </svg>
  );
};

const home = () => {
  return (
    <svg
      className="icon icon-home"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M21.5,14.587l-0,12.913l-6,-0l-0,-10l-7,-0l-0,10l-6,0l-0,-12.913"
        style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1px' }}
      />
      <path
        d="M19.5,9.674l-0,-5.174l-3,0l-0,2.304"
        style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1px' }}
      />
      <path
        d="M23.5,13.5l0,3l-11.5,-11l-11.5,11l0,-3l11.5,-11l11.5,11Z"
        style={{ fill: 'none', stroke: 'rgb(224,218,212)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M22,17c0,-0.137 -0.056,-0.269 -0.156,-0.363l-9.5,-9c-0.193,-0.183 -0.495,-0.183 -0.688,0l-9.5,9c-0.1,0.094 -0.156,0.226 -0.156,0.363l-0,10.5c-0,0.276 0.224,0.5 0.5,0.5l6,-0c0.276,0 0.5,-0.224 0.5,-0.5l-0,-9.5c-0,-0 6,-0 6,-0c-0,0 -0,9.5 -0,9.5c0,0.276 0.224,0.5 0.5,0.5l6,-0c0.276,0 0.5,-0.224 0.5,-0.5l-0,-10.5Zm2,-3.5c0,-0.136 -0.056,-0.267 -0.154,-0.361l-11.5,-11c-0.194,-0.185 -0.498,-0.185 -0.692,-0l-11.5,11c-0.098,0.094 -0.154,0.225 -0.154,0.361l0,3c0,0.2 0.119,0.381 0.304,0.46c0.184,0.078 0.397,0.04 0.542,-0.099c-0,0 8.325,-7.963 10.808,-10.339c0.194,-0.184 0.498,-0.184 0.692,0c2.483,2.376 10.808,10.339 10.808,10.339c0.145,0.139 0.358,0.177 0.542,0.099c0.185,-0.079 0.304,-0.26 0.304,-0.46l0,-3Zm-4.846,-5.769c0.145,0.138 0.358,0.177 0.542,0.098c0.185,-0.078 0.304,-0.259 0.304,-0.459l-0,-2.87c-0,-0.276 -0.224,-0.5 -0.5,-0.5l-3,0c-0.204,0 -0.388,0.124 -0.464,0.314c-0.076,0.189 -0.029,0.406 0.118,0.547l3,2.87Z"
        style={{ fill: 'url(#gradient-home)' }}
      />

      <defs>
        <linearGradient
          id="gradient-home"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#F0C6EC', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#DAC3FF', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const datasets = () => {
  return (
    <svg
      className="icon icon-datasets"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M12,0.482c6.347,-0 11.5,1.571 11.5,3.507c0,1.935 -5.153,3.506 -11.5,3.506c-6.347,0 -11.5,-1.571 -11.5,-3.506c0,-1.936 5.153,-3.507 11.5,-3.507"
        style={{ fill: 'none', stroke: 'url(#gradient-datasets)', strokeWidth: '1px' }}
      />
      <path
        d="M23.5,3.989l0,20.825c0,2.174 -5.153,3.668 -11.5,3.668c-6.347,-0 -11.5,-1.494 -11.5,-3.668l0,-20.825"
        style={{ fill: 'none', stroke: 'url(#gradient-datasets)', strokeWidth: '1px' }}
      />
      <path
        d="M23.5,11.019c0,1.911 -5.153,3.463 -11.5,3.463c-6.347,-0 -11.5,-1.552 -11.5,-3.463"
        style={{ fill: 'none', stroke: 'url(#gradient-datasets)', strokeWidth: '1px' }}
      />
      <path
        d="M23.5,17.995c0,1.932 -5.153,3.5 -11.5,3.5c-6.347,0 -11.5,-1.568 -11.5,-3.5"
        style={{ fill: 'none', stroke: 'url(#gradient-datasets)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M23.982,20.779c-0.182,0.158 -0.388,0.311 -0.618,0.459c-2.131,1.365 -6.42,2.321 -11.364,2.321c-4.944,-0 -9.233,-0.956 -11.364,-2.321c-0.23,-0.148 -0.436,-0.301 -0.618,-0.459l0,4.539c0,0.793 0.516,1.544 1.513,2.161c1.963,1.215 5.914,1.99 10.469,1.99c4.555,-0 8.506,-0.775 10.469,-1.99c0.997,-0.617 1.513,-1.368 1.513,-2.161l-0,-4.539Zm-0,-7.066c-0.182,0.157 -0.388,0.31 -0.619,0.457c-2.13,1.36 -6.419,2.313 -11.363,2.313c-4.944,-0 -9.233,-0.953 -11.363,-2.313c-0.231,-0.147 -0.437,-0.3 -0.619,-0.457l0,5.469c0.068,0.247 0.23,0.483 0.46,0.712c0.38,0.379 0.938,0.726 1.634,1.042c2.23,1.01 5.831,1.658 9.888,1.658c4.057,-0 7.658,-0.648 9.888,-1.658c0.696,-0.316 1.254,-0.663 1.634,-1.042c0.23,-0.229 0.392,-0.465 0.46,-0.712l-0,-5.469Zm-0,-7.241c-0.178,0.165 -0.38,0.326 -0.604,0.482c-2.133,1.479 -6.427,2.515 -11.378,2.515c-4.951,-0 -9.245,-1.036 -11.378,-2.515c-0.224,-0.156 -0.426,-0.317 -0.604,-0.482l0,5.647c0.068,0.246 0.23,0.48 0.46,0.709c0.38,0.377 0.937,0.723 1.634,1.038c2.23,1.006 5.831,1.651 9.888,1.651c4.057,0 7.658,-0.645 9.888,-1.651c0.697,-0.315 1.254,-0.661 1.634,-1.038c0.23,-0.229 0.392,-0.463 0.46,-0.709l-0,-5.647Zm-16.191,-5.726c-2.285,0.299 -4.251,0.834 -5.666,1.527c-0.696,0.342 -1.254,0.718 -1.634,1.128c-0.14,0.152 -0.256,0.307 -0.341,0.466l-0.011,0.025c-0.076,0.194 -0.119,0.393 -0.121,0.601l0,0.29c0.069,0.278 0.235,0.545 0.473,0.802c0.38,0.41 0.938,0.787 1.634,1.129c2.227,1.091 5.823,1.791 9.875,1.791c4.052,-0 7.648,-0.7 9.875,-1.791c0.696,-0.342 1.254,-0.719 1.634,-1.129c0.238,-0.257 0.404,-0.524 0.473,-0.802l-0,-0.29c-0,-0.209 -0.044,-0.419 -0.132,-0.626c-0.085,-0.159 -0.201,-0.314 -0.341,-0.466c-0.38,-0.41 -0.938,-0.786 -1.634,-1.128c-1.416,-0.694 -3.384,-1.229 -5.672,-1.527c-1.305,-0.156 -2.722,-0.241 -4.203,-0.241c-1.483,-0 -2.902,0.086 -4.209,0.241Z"
        style={{ fill: 'url(#gradient-datasets)' }}
      />

      <defs>
        <linearGradient
          id="gradient-datasets"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
        <linearGradient
          id="gradient-datasets-yellow"
          x1="60%"
          y1="60%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#EFD68F', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#F0C6EC', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const attributes = () => {
  return (
    <svg
      className="icon icon-attributes"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M7.5,24.5l6,-0"
        style={{ fill: 'none', stroke: 'url(#gradient-attributes)', strokeWidth: '1px' }}
      />
      <path
        d="M7.5,19.5l12,-0"
        style={{ fill: 'none', stroke: 'url(#gradient-attributes)', strokeWidth: '1px' }}
      />
      <path
        d="M7.5,14.5l12,-0"
        style={{ fill: 'none', stroke: 'url(#gradient-attributes)', strokeWidth: '1px' }}
      />
      <path
        d="M7.5,9.5l12,0"
        style={{ fill: 'none', stroke: 'url(#gradient-attributes)', strokeWidth: '1px' }}
      />
      <path
        d="M23.5,6.5c-0,-1.656 -1.344,-3 -3,-3l-14,-0c-1.656,-0 -3,1.344 -3,3l0,20c0,1.656 1.344,3 3,3l14,-0c1.656,-0 3,-1.344 3,-3l-0,-20Z"
        style={{ fill: 'none', stroke: 'url(#gradient-attributes)', strokeWidth: '1px' }}
      />
      <path
        d="M19.749,1.515c-0.549,-0.622 -1.354,-1.015 -2.249,-1.015l-14,0c-1.656,0 -3,1.343 -3,2.998l0,19.988c0,0.887 0.386,1.684 1,2.233"
        style={{ fill: 'none', stroke: 'url(#gradient-attributes)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M24,6.5c-0,-1.932 -1.568,-3.5 -3.5,-3.5l-14,-0c-1.932,-0 -3.5,1.568 -3.5,3.5l-0,20c-0,1.932 1.568,3.5 3.5,3.5l14,-0c1.932,-0 3.5,-1.568 3.5,-3.5c-0,-0 -0,-20 -0,-20Zm-3.876,-5.316c-0.641,-0.726 -1.58,-1.184 -2.624,-1.184l-14,0c-1.932,0 -3.5,1.568 -3.5,3.498l-0,19.988c-0,1.035 0.451,1.965 1.167,2.606c0.205,0.184 0.522,0.167 0.706,-0.039c0.184,-0.206 0.166,-0.522 -0.04,-0.706c-0.511,-0.458 -0.833,-1.122 -0.833,-1.861l-0,-19.988c-0,-1.379 1.12,-2.498 2.5,-2.498l14,0c0.746,0 1.416,0.328 1.875,0.846c0.182,0.207 0.499,0.227 0.705,0.044c0.207,-0.183 0.227,-0.499 0.044,-0.706Zm-12.624,23.816l6,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-6,-0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm0,-5l12,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-12,-0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm0,-5l12,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-12,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm-0,-5l12,0c0.276,0 0.5,-0.224 0.5,-0.5c-0,-0.276 -0.224,-0.5 -0.5,-0.5l-12,-0c-0.276,0 -0.5,0.224 -0.5,0.5c-0,0.276 0.224,0.5 0.5,0.5Z"
        style={{ fill: 'url(#gradient-attributes)' }}
      />

      <defs>
        <linearGradient
          id="gradient-attributes"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
        <linearGradient
          id="gradient-attributes-purple"
          x1="0%"
          y1="0%"
          x2="70%"
          y2="70%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#F0C6EC', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#DAC3FF', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const attributeSensitivity = ({ label, value, values, showTooltip, hideTooltip }) => {
  const item = value && values[value];

  // This occurs in the changelog if a previous attribute sensitivity has been deleted
  if (!(item && label)) {
    return <span className="pill pill-wide pill-grey">Unknown</span>;
  }

  // Normal case
  return values && Object.keys(values).length > 0 ? (
    <span
      className={item?.className ? item.className : ''}
      onMouseEnter={() => showTooltip({ title: item.label || label, bodyText: item.tooltip })}
      onMouseLeave={() => hideTooltip()}
    >
      {item.label || label}
    </span>
  ) : null;
};

const glossaries = () => {
  return (
    <svg
      className="icon icon-glossaries"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M4,21.5l18.5,0c0.552,0 1,-0.448 1,-1l-0,-19c-0,-0.552 -0.448,-1 -1,-1l-19.368,0"
        style={{ fill: 'none', stroke: 'url(#gradient-glossaries)', strokeWidth: '1px' }}
      />
      <path
        d="M3.5,2.5l-0,17"
        style={{ fill: 'none', stroke: 'url(#gradient-glossaries)', strokeWidth: '1px' }}
      />
      <path
        d="M22,24l-18.868,0"
        style={{ fill: 'none', stroke: 'url(#gradient-glossaries)', strokeWidth: '1px' }}
      />
      <path
        d="M7.5,26.5l-4.5,0c-1.38,0 -2.5,-1.12 -2.5,-2.5c0,-1.38 1.12,-2.5 2.5,-2.5l19.879,0l0.121,-0.134c-1.004,1.056 -1,2.29 -1,2.634c0,1.38 0.672,2.058 1.5,2.5l-11,0"
        style={{ fill: 'none', stroke: 'url(#gradient-glossaries)', strokeWidth: '1px' }}
      />
      <path
        d="M0.5,24.099l0,-21.099c0,-1.38 1.12,-2.5 2.5,-2.5"
        style={{ fill: 'none', stroke: 'url(#gradient-glossaries)', strokeWidth: '1px' }}
      />
      <path
        d="M19.5,9.5l-0,-5l-12,-0l-0,5l12,-0Z"
        style={{ fill: 'none', stroke: 'url(#gradient-glossaries)', strokeWidth: '1px' }}
      />
      <path
        d="M12.5,24l-5,-0l0,5.5l2.5,-2l2.5,2l0,-5.5Z"
        style={{ fill: 'none', stroke: 'url(#gradient-glossaries)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M22.5,-0l0.292,0.029c0.088,0.017 0.172,0.042 0.253,0.073l0.189,0.09l0.155,0.1l0.166,0.143l0.186,0.222l0.133,0.241l0.047,0.12l0.051,0.193l0.028,0.289c-0,0 -0,19 -0,19c-0,0.524 -0.269,0.985 -0.676,1.254l-0.067,0.073c-0.765,0.889 -0.757,1.887 -0.757,2.173c0,1.136 0.554,1.695 1.235,2.059c0.203,0.108 0.306,0.34 0.25,0.562c-0.056,0.223 -0.256,0.379 -0.485,0.379l-9,-0c-0.276,0 -0.5,-0.224 -0.5,-0.5c-0,-0.276 0.224,-0.5 0.5,-0.5c-0,-0 7.601,0 7.6,-0c-0.294,-0.386 -0.502,-0.871 -0.573,-1.5l-8.527,-0l-0,5c0,0.192 -0.11,0.367 -0.283,0.451c-0.174,0.083 -0.379,0.06 -0.529,-0.061c-0,0 -2.188,-1.75 -2.188,-1.75l-2.188,1.75c-0.15,0.121 -0.355,0.144 -0.529,0.061c-0.173,-0.084 -0.283,-0.259 -0.283,-0.451l0,-5l-3.868,-0c-0.276,0 -0.5,-0.224 -0.5,-0.5c0,-0.276 0.224,-0.5 0.5,-0.5l18.39,-0c0.037,-0.391 0.141,-0.932 0.426,-1.5l-18.948,-0c-1.104,-0 -2,0.896 -2,2c0,1.104 0.896,2 2,2l2.5,-0c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-2.5,0c-1.6,0 -2.909,-1.255 -2.995,-2.833c-0.003,-0.023 -0.005,-0.045 -0.005,-0.068l0,-21.099l0.056,-0.577l0.136,-0.48l0.106,-0.247l0.115,-0.216l0.299,-0.42l0.283,-0.291l0.327,-0.256l0.241,-0.146l0.452,-0.201l0.365,-0.102l0.336,-0.051l0.319,-0.012l0.031,0.003l0.066,-0.004l19.368,-0Zm-19.5,2.5l-0,17c-0,0.276 0.224,0.5 0.5,0.5c0.276,0 0.5,-0.224 0.5,-0.5l-0,-17c-0,-0.276 -0.224,-0.5 -0.5,-0.5c-0.276,-0 -0.5,0.224 -0.5,0.5Zm5.5,2.5c-0.133,0 -0.26,0.053 -0.354,0.146c-0.093,0.094 -0.146,0.221 -0.146,0.354c0,0.769 0,2.231 0,3c-0,0.133 0.053,0.26 0.146,0.354c0.094,0.093 0.221,0.146 0.354,0.146c1.832,-0 8.168,-0 10,-0c0.133,-0 0.26,-0.053 0.354,-0.146c0.093,-0.094 0.146,-0.221 0.146,-0.354c-0,-0.769 -0,-2.231 -0,-3c0,-0.133 -0.053,-0.26 -0.146,-0.354c-0.094,-0.093 -0.221,-0.146 -0.354,-0.146c-1.832,0 -8.168,0 -10,0Z"
        style={{ fill: 'url(#gradient-glossaries)' }}
      />

      <defs>
        <linearGradient
          id="gradient-glossaries"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
        <linearGradient
          id="gradient-glossaries-blue"
          x1="0%"
          y1="0%"
          x2="50%"
          y2="50%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#DAC3FF', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#B6CFFF', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const users = () => {
  return (
    <svg
      className="icon icon-users"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M22.499,24.667c-0,2.667 -4.705,4.833 -10.499,4.833c-5.794,0 -10.498,-2.166 -10.498,-4.833"
        style={{ fill: 'none', stroke: 'url(#gradient-users)', strokeWidth: '1px' }}
      />
      <path
        d="M6,20.5l0,8"
        style={{ fill: 'none', stroke: 'url(#gradient-users)', strokeWidth: '1px' }}
      />
      <path
        d="M18,20.5l-0,8"
        style={{ fill: 'none', stroke: 'url(#gradient-users)', strokeWidth: '1px' }}
      />
      <path
        d="M1.502,24.667c0.002,-2.009 -0.171,-7.334 2.196,-8.951c0.748,-0.511 4.541,0.615 5.53,-1.253"
        style={{ fill: 'none', stroke: 'url(#gradient-users)', strokeWidth: '1px' }}
      />
      <path
        d="M22.499,24.667c-0.003,-2.009 0.155,-7.334 -2.212,-8.951c-0.748,-0.511 -4.541,0.615 -5.53,-1.253"
        style={{ fill: 'none', stroke: 'url(#gradient-users)', strokeWidth: '1px' }}
      />
      <path
        d="M12,0.5c3.624,0 4.421,3.111 4.421,4.833c-0,2.796 -1.676,7.167 -4.421,7.167c-2.744,-0 -4.42,-4.371 -4.42,-7.167c-0,-1.722 0.797,-4.833 4.42,-4.833Z"
        style={{ fill: 'none', stroke: 'url(#gradient-users)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M5.5,28.99l0,-8.49c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l0,8.801c1.612,0.443 3.492,0.699 5.5,0.699c2.008,0 3.888,-0.256 5.5,-0.699l0,-8.801c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l-0,8.49c0.852,-0.297 1.607,-0.65 2.238,-1.046c1.449,-0.91 2.261,-2.064 2.261,-3.277l-0,-0.001c-0.002,-1.228 0.051,-3.673 -0.424,-5.789c-0.341,-1.519 -0.967,-2.864 -2.006,-3.574c-0.166,-0.113 -0.426,-0.193 -0.764,-0.22c-0.493,-0.04 -1.207,0.015 -1.953,0.017l-0.233,-0.001c-0.531,-0.008 -1.069,-0.049 -1.531,-0.199c-0.378,-0.124 -0.704,-0.322 -0.889,-0.671c-0.087,-0.164 -0.252,-0.26 -0.425,-0.266l-0.024,-0c-0.077,0.001 -0.154,0.02 -0.227,0.058l-0.075,0.049l-0.05,0.046c-0.715,0.555 -1.519,0.884 -2.398,0.884c-0.902,-0 -1.725,-0.347 -2.453,-0.928l-0.001,0.005l-0.084,-0.056l-0.082,-0.034l-0.072,-0.018l-0.093,-0.006l-0.083,0.009l-0.08,0.023l-0.076,0.036l-0.07,0.049l-0.061,0.061l-0.05,0.072l-0.009,0.016c-0.185,0.349 -0.51,0.547 -0.889,0.671c-0.462,0.15 -1,0.191 -1.531,0.199l-0.155,0.001c-0.542,0.001 -1.07,-0.028 -1.513,-0.032l-0.518,0.015c-0.338,0.027 -0.598,0.107 -0.763,0.22c-1.04,0.71 -1.664,2.055 -2.002,3.575c-0.47,2.115 -0.411,4.56 -0.413,5.788l0,0.001c0,1.213 0.812,2.367 2.261,3.277c0.631,0.396 1.385,0.749 2.237,1.046Zm6.5,-28.99c-4.079,0 -5,3.426 -5,5.333c0,1.767 0.648,4.129 1.799,5.737c0.832,1.163 1.927,1.93 3.201,1.93c1.274,-0 2.369,-0.767 3.201,-1.93c1.151,-1.608 1.799,-3.97 1.799,-5.737c-0,-1.907 -0.921,-5.333 -5,-5.333Z"
        style={{ fill: 'url(#gradient-users)' }}
      />

      <defs>
        <linearGradient
          id="gradient-users"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const userGroups = () => {
  return (
    <svg
      className="icon icon-user-groups"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M18.499,25.498c-0,2.208 -3.871,4 -8.638,4c-4.767,0 -8.638,-1.792 -8.638,-4"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M4.924,22.05l-0,6.621"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M14.797,22.05l0,6.621"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M1.223,25.498c0.002,-1.662 -0.141,-6.069 1.807,-7.406c0.616,-0.423 3.736,0.508 4.55,-1.037"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M18.499,25.498c-0.002,-1.662 0.128,-6.069 -1.82,-7.406c-0.615,-0.423 -3.736,0.508 -4.55,-1.037"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M9.861,5.5c2.981,0 3.637,2.575 3.637,4c-0,2.313 -1.379,5.93 -3.637,5.93c-2.258,0 -3.637,-3.617 -3.637,-5.93c-0,-1.425 0.655,-4 3.637,-4Z"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />

      <path
        d="M4.75,12.5c-2.345,-0 -4.249,-0.896 -4.249,-2"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M2.321,8.776l0,3.31"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M0.501,10.5c0.001,-0.831 -0.069,-3.035 0.889,-3.704c0.303,-0.211 1.838,0.255 2.238,-0.518"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M4.75,0.5c1.467,0 1.789,1.288 1.789,2c0,1.157 -0.678,2.966 -1.789,2.966c-1.111,-0 -1.789,-1.809 -1.789,-2.966c-0,-0.712 0.322,-2 1.789,-2Z"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />

      <path
        d="M23.499,18.417c0,1.082 -1.448,2.009 -3.499,2.393"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M21.095,16.19l-0,4.276"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M14.5,13.566c0.723,0.019 1.587,-0.001 1.906,-0.603"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M23.499,18.417c-0.001,-1.074 0.084,-3.92 -1.182,-4.784c-0.4,-0.273 -2.427,0.328 -2.955,-0.67"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />
      <path
        d="M17.888,5.5c1.937,0 2.363,1.663 2.363,2.583c-0,1.495 -0.896,3.831 -2.363,3.831c-1.467,-0 -2.362,-2.336 -2.362,-3.831c-0,-0.92 0.425,-2.583 2.362,-2.583Z"
        style={{ fill: 'none', stroke: 'url(#gradient-user-groups)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M4.424,29.136l-0,-7.086c-0,-0.275 0.224,-0.5 0.5,-0.5c0.275,0 0.5,0.225 0.5,0.5l-0,7.397c1.308,0.349 2.822,0.551 4.437,0.551c1.614,0 3.128,-0.202 4.436,-0.551l0,-7.397c0,-0.275 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.225 0.5,0.5l0,7.087c0.684,-0.244 1.291,-0.532 1.801,-0.854c1.222,-0.772 1.9,-1.755 1.9,-2.785c0,0 0,0 0,-0c-0.001,-1.02 0.043,-3.051 -0.35,-4.809c-0.286,-1.28 -0.815,-2.411 -1.686,-3.01c-0.145,-0.099 -0.372,-0.173 -0.668,-0.197c-0.446,-0.037 -1.111,0.021 -1.791,0.013c-0.426,-0.005 -0.859,-0.036 -1.231,-0.154c-0.291,-0.093 -0.544,-0.237 -0.69,-0.5c-0.072,-0.13 -0.199,-0.221 -0.345,-0.248c-0.147,-0.028 -0.297,0.012 -0.412,0.107c-0.578,0.482 -1.235,0.78 -1.964,0.78c-0.733,-0 -1.392,-0.301 -1.973,-0.787c-0.115,-0.095 -0.266,-0.135 -0.413,-0.107c-0.147,0.027 -0.274,0.119 -0.346,0.25c-0.145,0.264 -0.397,0.41 -0.688,0.503c-0.372,0.119 -0.805,0.15 -1.231,0.156c-0.681,0.008 -1.348,-0.05 -1.794,-0.013c-0.297,0.024 -0.524,0.098 -0.669,0.197c-0.872,0.599 -1.399,1.73 -1.683,3.011c-0.388,1.757 -0.34,3.788 -0.341,4.808c-0,0 -0,0 -0,0c-0,1.03 0.678,2.013 1.9,2.785c0.51,0.322 1.117,0.61 1.801,0.853Zm16.17,-7.951l0,-4.995c0,-0.276 0.225,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l0,4.69c0.714,-0.268 1.294,-0.609 1.696,-0.991c0.467,-0.444 0.709,-0.952 0.709,-1.477c-0,-0 -0,-0 -0,-0.001c-0.001,-0.668 0.026,-1.999 -0.235,-3.151c-0.199,-0.877 -0.576,-1.645 -1.183,-2.055c-0.099,-0.067 -0.24,-0.122 -0.424,-0.146c-0.229,-0.03 -0.565,-0.015 -0.934,-0.006c-0.323,0.008 -0.677,0.012 -0.987,-0.055c-0.201,-0.044 -0.388,-0.108 -0.483,-0.277c-0.073,-0.128 -0.198,-0.218 -0.343,-0.245c-0.145,-0.027 -0.295,0.011 -0.409,0.105c-0.35,0.286 -0.745,0.466 -1.187,0.466c-0.443,0 -0.84,-0.182 -1.191,-0.47c-0.115,-0.094 -0.265,-0.133 -0.41,-0.105c-0.146,0.027 -0.272,0.118 -0.344,0.247c-0.081,0.145 -0.232,0.211 -0.398,0.257c-0.258,0.072 -0.557,0.083 -0.844,0.081c-0.202,-0.001 -0.385,0.119 -0.464,0.305c-0.229,0.543 -0.503,1.057 -0.818,1.507c-0.047,0.067 -0.095,0.133 -0.143,0.197c-0.161,0.212 -0.128,0.512 0.075,0.684c0.091,0.077 0.174,0.164 0.248,0.259c0.073,0.094 0.177,0.159 0.293,0.183c0.219,0.046 0.462,0.05 0.699,0.053c0.714,0.008 1.413,-0.048 1.88,-0.009c0.568,0.047 0.994,0.222 1.272,0.413c1.102,0.757 1.838,2.149 2.199,3.768c0.028,0.126 0.055,0.254 0.079,0.383c0.025,0.135 0.104,0.253 0.219,0.327c0.114,0.075 0.255,0.099 0.388,0.067c0.013,-0.003 0.027,-0.006 0.04,-0.009Zm-10.733,-16.185c-3.372,0 -4.137,2.889 -4.137,4.5c-0,1.476 0.53,3.451 1.471,4.796c0.692,0.988 1.606,1.634 2.666,1.634c1.059,0 1.973,-0.646 2.665,-1.634c0.941,-1.345 1.472,-3.32 1.472,-4.796c-0,-1.611 -0.766,-4.5 -4.137,-4.5Zm-8.04,7.489l0,-3.549c0,-0.367 0.224,-0.664 0.5,-0.664c0.276,-0 0.5,0.297 0.5,0.664l0,3.854c0.575,0.129 1.215,0.203 1.89,0.206c0.159,0.001 0.309,-0.075 0.404,-0.202c0.094,-0.128 0.123,-0.294 0.076,-0.446c-0.304,-0.983 -0.467,-1.992 -0.467,-2.852c-0,-0.736 0.135,-1.678 0.512,-2.577c0.065,-0.156 0.048,-0.334 -0.047,-0.474c-0.095,-0.14 -0.255,-0.222 -0.424,-0.219c-0.005,-0 -0.01,-0 -0.015,-0c-0.3,-0 -0.567,-0.131 -0.804,-0.333c-0.115,-0.098 -0.268,-0.138 -0.416,-0.111c-0.149,0.028 -0.277,0.121 -0.348,0.254c-0.054,0.099 -0.164,0.129 -0.277,0.155c-0.221,0.05 -0.474,0.046 -0.704,0.04c-0.285,-0.008 -0.545,-0.019 -0.722,0.006c-0.163,0.022 -0.288,0.074 -0.376,0.135c-0.474,0.332 -0.775,0.95 -0.929,1.66c-0.195,0.899 -0.173,1.938 -0.174,2.46c0,0 0,0 0,0c0,0.587 0.36,1.157 1.041,1.598c0.225,0.146 0.487,0.278 0.78,0.395Zm16.067,-7.489c-2.328,0 -2.863,1.977 -2.863,3.083c0,0.986 0.357,2.304 0.989,3.201c0.485,0.688 1.131,1.13 1.874,1.13c0.742,-0 1.389,-0.442 1.874,-1.13c0.632,-0.897 0.988,-2.215 0.988,-3.201c0,-1.106 -0.535,-3.083 -2.862,-3.083Zm-13.138,-5c-1.853,0 -2.289,1.6 -2.289,2.5c-0,0.782 0.277,1.827 0.767,2.538c0.393,0.57 0.921,0.928 1.522,0.928c0.601,-0 1.129,-0.358 1.521,-0.928c0.49,-0.711 0.768,-1.756 0.768,-2.538c-0,-0.9 -0.437,-2.5 -2.289,-2.5Z"
        style={{ fill: 'url(#gradient-user-groups)' }}
      />

      <defs>
        <linearGradient
          id="gradient-user-groups"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const connections = () => {
  return (
    <svg
      className="icon icon-connections"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M12.5,21.5l0,5c0,1.656 -1.344,3 -3,3c-1.656,0 -3,-1.344 -3,-3l-0,-1.5c0,-1.932 -1.568,-3.485 -3.5,-3.485l-2.5,-0.009"
        style={{ fill: 'none', stroke: 'url(#gradient-connections)', strokeWidth: '1px' }}
      />
      <path
        d="M10.5,19.5l4,0l0,2l-3.991,-0l-0.009,-2Z"
        style={{ fill: 'none', stroke: 'url(#gradient-connections)', strokeWidth: '1px' }}
      />
      <path
        d="M9.5,15.5l6,0l-0,0.001l-6,-0.001Z"
        style={{ fill: 'none', stroke: 'url(#gradient-connections)', strokeWidth: '1px' }}
      />
      <path
        d="M10.5,12.5l4,0l-0,0l-4,0Z"
        style={{ fill: 'none', stroke: 'url(#gradient-connections)', strokeWidth: '1px' }}
      />
      <path
        d="M5.5,10.5l-1,0l0,-3l16,0l0,3l-1,0c0,0 0,2.596 0,5c0,1.061 -0.421,2.078 -1.172,2.828c-0.75,0.751 -1.767,1.172 -2.828,1.172c-1.881,0 -4.119,0 -6,0c-2.209,0 -4,-1.791 -4,-4c0,-2.404 0,-5 0,-5Z"
        style={{ fill: 'none', stroke: 'url(#gradient-connections)', strokeWidth: '1px' }}
      />
      <path
        d="M7.5,1.499c0,-0.552 0.448,-0.999 0.999,-0.999c0.001,0 0.001,0 0.001,0c0.552,0 1,0.448 1,1c-0,1.934 -0,6 -0,6l-2,-0c-0,-0 0,-4.067 0,-6.001Z"
        style={{ fill: 'none', stroke: 'url(#gradient-connections)', strokeWidth: '1px' }}
      />
      <path
        d="M15.5,1.526c0,-0.265 0.106,-0.52 0.293,-0.707c0.187,-0.187 0.441,-0.292 0.706,-0.292c0.001,-0 0.001,-0 0.001,-0c0.266,-0 0.52,0.105 0.707,0.292c0.188,0.188 0.293,0.442 0.293,0.708c0,1.928 -0,5.973 -0,5.973l-2,-0c0,-0 0,-4.046 0,-5.974Z"
        style={{ fill: 'none', stroke: 'url(#gradient-connections)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M12,23.5c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l0,3c0,1.932 -1.568,3.5 -3.5,3.5c-1.932,0 -3.5,-1.568 -3.5,-3.5l-0,-1.5c-0,-1.655 -1.345,-2.985 -3,-2.985l-0.002,0l-2.5,-0.009c-0.276,-0.001 -0.499,-0.226 -0.498,-0.502c0.001,-0.276 0.226,-0.499 0.502,-0.498l2.498,0.009c2.208,0 4,1.777 4,3.985l0,1.5c0,1.38 1.12,2.5 2.5,2.5c1.38,0 2.5,-1.12 2.5,-2.5l0,-3Zm-1.998,-3.5l-0.502,0c-2.485,0 -4.5,-2.015 -4.5,-4.5l-0,-4.5l-0.5,0c-0.276,0 -0.5,-0.224 -0.5,-0.5l0,-3c0,-0.133 0.053,-0.26 0.147,-0.354c0.093,-0.093 0.221,-0.146 0.353,-0.146l16,0c0.276,0 0.5,0.224 0.5,0.5l-0,3c0,0.133 -0.053,0.26 -0.146,0.354c-0.094,0.093 -0.221,0.146 -0.354,0.146c-0,0 -0.5,0 -0.5,0l0,4.5c0,1.193 -0.474,2.338 -1.318,3.182c-0.844,0.844 -1.988,1.318 -3.182,1.318l-0.5,0l0,1.5c0,0.133 -0.053,0.26 -0.146,0.354c-0.094,0.093 -0.221,0.146 -0.354,0.146l-3.991,-0c-0.276,-0 -0.499,-0.223 -0.5,-0.498l-0.007,-1.502Zm0.498,-8c-0.276,-0 -0.5,0.224 -0.5,0.5c-0,0.276 0.224,0.5 0.5,0.5l3.94,0c0.028,0.002 0.05,0 0.06,0c0.276,0 0.5,-0.224 0.5,-0.5c-0,-0.127 -0.047,-0.242 -0.125,-0.33c-0.092,-0.104 -0.226,-0.17 -0.375,-0.17l-4,0Zm-1,3c-0.276,-0 -0.5,0.224 -0.5,0.5c-0,0.276 0.224,0.5 0.5,0.5l5.942,0c0.028,0.002 0.048,0 0.058,0c0.276,0 0.5,-0.224 0.5,-0.5c-0,-0.163 -0.078,-0.307 -0.199,-0.399c-0.084,-0.063 -0.188,-0.101 -0.301,-0.101l-6,0Zm-2.5,-9l0,-4.501c0,-0.828 0.672,-1.499 1.5,-1.499c0.398,0 0.779,0.158 1.061,0.439c0.281,0.282 0.439,0.663 0.439,1.061l-0,4.5l-3,0Zm8,0l0,-4.478c0,-0.398 0.158,-0.779 0.439,-1.06c0.282,-0.281 0.663,-0.439 1.061,-0.439c0.398,-0 0.78,0.158 1.061,0.439c0.281,0.282 0.439,0.663 0.439,1.061l0,4.477l-3,0Z"
        style={{ fill: 'url(#gradient-connections)' }}
      />

      <defs>
        <linearGradient
          id="gradient-connections"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const customProperties = () => {
  return (
    <svg
      className="icon icon-custom-properties"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M8,3.5c1.381,0 2.5,1.119 2.5,2.5l0,5c-0,1.381 -1.119,2.5 -2.5,2.5l-5,0c-1.381,-0 -2.5,-1.119 -2.5,-2.5l0,-5c0,-1.381 1.119,-2.5 2.5,-2.5l5,0Z"
        style={{ fill: 'none', stroke: 'url(#gradient-custom-properties)', strokeWidth: '1px' }}
      />
      <path
        d="M21,16.5c1.381,0 2.5,1.119 2.5,2.5l0,5c-0,1.381 -1.119,2.5 -2.5,2.5l-5,-0c-1.381,-0 -2.5,-1.119 -2.5,-2.5l0,-5c0,-1.381 1.119,-2.5 2.5,-2.5l5,-0Z"
        style={{ fill: 'none', stroke: 'url(#gradient-custom-properties)', strokeWidth: '1px' }}
      />
      <path
        d="M8,16.5c1.381,0 2.5,1.119 2.5,2.5l0,5c-0,1.381 -1.119,2.5 -2.5,2.5l-5,0c-1.381,-0 -2.5,-1.119 -2.5,-2.5l0,-5c0,-1.381 1.119,-2.5 2.5,-2.5l5,0Z"
        style={{ fill: 'none', stroke: 'url(#gradient-custom-properties)', strokeWidth: '1px' }}
      />
      <path
        d="M18.5,4.5l0,8"
        style={{ fill: 'none', stroke: 'url(#gradient-custom-properties)', strokeWidth: '1px' }}
      />
      <path
        d="M22.5,8.5l-8,-0"
        style={{ fill: 'none', stroke: 'url(#gradient-custom-properties)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M8,3c1.657,0 3,1.343 3,3l-0,5c-0,1.657 -1.343,3 -3,3l-5,-0c-1.657,-0 -3,-1.343 -3,-3l0,-5c0,-1.657 1.343,-3 3,-3l5,0Zm13,13c1.657,0 3,1.343 3,3l-0,5c-0,1.657 -1.343,3 -3,3l-5,-0c-1.657,-0 -3,-1.343 -3,-3l0,-5c0,-1.657 1.343,-3 3,-3l5,0Zm-13,0c1.657,0 3,1.343 3,3l-0,5c-0,1.657 -1.343,3 -3,3l-5,-0c-1.657,-0 -3,-1.343 -3,-3l0,-5c0,-1.657 1.343,-3 3,-3l5,0Zm10,-7l-3.5,-0c-0.276,-0 -0.5,-0.224 -0.5,-0.5c0,-0.276 0.224,-0.5 0.5,-0.5l3.5,-0l0,-3.5c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l0,3.5l3.5,-0c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-3.5,-0l0,3.5c0,0.276 -0.224,0.5 -0.5,0.5c-0.276,0 -0.5,-0.224 -0.5,-0.5l0,-3.5Z"
        style={{ fill: 'url(#gradient-custom-properties)' }}
      />

      <defs>
        <linearGradient
          id="gradient-custom-properties"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const tasks = () => {
  return (
    <svg
      className="icon icon-tasks"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M10.846,9.809l0,-1.745c0,-0.319 0.259,-0.577 0.577,-0.577l1.154,-0c0.318,-0 0.577,0.258 0.577,0.577l-0,1.745c0.663,0.148 1.28,0.42 1.824,0.79l1.212,-1.213c0.225,-0.225 0.591,-0.225 0.816,0l0.816,0.816c0.225,0.225 0.225,0.591 0,0.816l-1.262,1.262c0.283,0.475 0.494,0.998 0.617,1.553l1.746,-0c0.318,-0 0.577,0.258 0.577,0.577l0,1.154c0,0.318 -0.259,0.576 -0.577,0.576l-1.746,0c-0.135,0.61 -0.376,1.18 -0.701,1.691l1.346,1.346c0.225,0.225 0.225,0.591 0,0.816l-0.816,0.816c-0.225,0.225 -0.591,0.225 -0.816,-0l-1.346,-1.346c-0.511,0.325 -1.081,0.565 -1.69,0.701l-0,1.746c-0,0.318 -0.259,0.577 -0.577,0.577l-1.154,-0c-0.318,-0 -0.577,-0.259 -0.577,-0.577l0,-1.746c-0.555,-0.124 -1.078,-0.334 -1.553,-0.617l-1.262,1.262c-0.225,0.225 -0.59,0.225 -0.815,-0l-0.816,-0.816c-0.226,-0.225 -0.226,-0.591 -0,-0.816l1.212,-1.213c-0.37,-0.544 -0.642,-1.16 -0.789,-1.824l-1.746,0c-0.318,0 -0.577,-0.258 -0.577,-0.576l-0,-1.154c-0,-0.319 0.259,-0.577 0.577,-0.577l1.746,-0c0.135,-0.61 0.376,-1.18 0.701,-1.69l-1.124,-1.125c-0.226,-0.225 -0.226,-0.591 -0,-0.816l0.816,-0.816c0.225,-0.225 0.59,-0.225 0.815,0l1.125,1.125c0.511,-0.326 1.081,-0.566 1.69,-0.702Zm1.154,2.87c1.274,-0 2.308,1.034 2.308,2.308c-0,1.273 -1.034,2.307 -2.308,2.307c-1.274,0 -2.308,-1.034 -2.308,-2.307c0,-1.274 1.034,-2.308 2.308,-2.308Z"
        style={{ fill: 'none', stroke: 'url(#gradient-tasks)', strokeWidth: '1px' }}
      />
      <path
        d="M22.227,9.718c2.231,4.328 1.534,9.779 -2.091,13.405c-4.488,4.488 -11.775,4.488 -16.264,-0"
        style={{ fill: 'none', stroke: 'url(#gradient-tasks)', strokeWidth: '1px' }}
      />
      <path
        d="M1.782,20.264c-2.232,-4.328 -1.535,-9.78 2.09,-13.405c4.489,-4.488 11.776,-4.488 16.264,0"
        style={{ fill: 'none', stroke: 'url(#gradient-tasks)', strokeWidth: '1px' }}
      />
      <path
        d="M20.136,3.859l-0,3l-3,0"
        style={{ fill: 'none', stroke: 'url(#gradient-tasks)', strokeWidth: '1px' }}
      />
      <path
        d="M6.787,23.037l-3,-0l-0,3"
        style={{ fill: 'none', stroke: 'url(#gradient-tasks)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M5.077,23.537l1.71,-0c0.276,-0 0.5,-0.224 0.5,-0.5c-0,-0.276 -0.224,-0.5 -0.5,-0.5l-3,-0c-0.276,-0 -0.5,0.224 -0.5,0.5l-0,3c-0,0.276 0.224,0.5 0.5,0.5c0.276,-0 0.5,-0.224 0.5,-0.5l-0,-1.856c4.713,3.965 11.768,3.73 16.202,-0.705c3.783,-3.783 4.511,-9.471 2.182,-13.987c-0.126,-0.245 -0.428,-0.342 -0.673,-0.215c-0.246,0.126 -0.342,0.428 -0.216,0.673c2.135,4.14 1.468,9.354 -2,12.822c-4.019,4.019 -10.386,4.276 -14.705,0.768Zm5.269,-14.114c-0.388,0.115 -0.76,0.271 -1.11,0.461c-0,-0 -0.851,-0.851 -0.851,-0.851c-0.42,-0.421 -1.103,-0.421 -1.523,-0c-0,-0 -0.816,0.816 -0.816,0.816c-0.42,0.42 -0.42,1.102 0,1.523c0,-0 0.851,0.851 0.851,0.851c-0.19,0.349 -0.345,0.721 -0.461,1.11c0,-0 -1.359,-0 -1.359,-0c-0.594,-0 -1.077,0.482 -1.077,1.077c0,-0 0,1.154 0,1.154c0,0.594 0.483,1.076 1.077,1.076c-0,0 1.359,0 1.359,0c0.132,0.442 0.314,0.862 0.541,1.253c-0,-0 -0.931,0.93 -0.931,0.93c-0.42,0.421 -0.42,1.103 0,1.523c0,0 0.816,0.816 0.816,0.816c0.42,0.421 1.103,0.421 1.523,0c-0,0 0.996,-0.996 0.996,-0.996c0.307,0.156 0.63,0.285 0.965,0.384c0,0 0,1.36 0,1.36c0,0.594 0.483,1.077 1.077,1.077c0,-0 1.154,-0 1.154,-0c0.594,-0 1.077,-0.483 1.077,-1.077c-0,-0 -0,-1.36 -0,-1.36c0.388,-0.115 0.76,-0.27 1.11,-0.46c0,-0 1.073,1.072 1.073,1.072c0.42,0.421 1.103,0.421 1.523,0l0.816,-0.816c0.42,-0.42 0.42,-1.102 -0,-1.523c-0,0 -1.073,-1.072 -1.073,-1.072c0.19,-0.35 0.345,-0.722 0.461,-1.111c-0,0 1.359,0 1.359,0c0.594,0 1.077,-0.482 1.077,-1.076l0,-1.154c0,-0.595 -0.483,-1.077 -1.077,-1.077c0,-0 -1.359,-0 -1.359,-0c-0.1,-0.335 -0.229,-0.658 -0.385,-0.965c0,-0 0.997,-0.996 0.997,-0.996c0.42,-0.421 0.42,-1.103 -0,-1.523c-0,-0 -0.816,-0.816 -0.816,-0.816c-0.42,-0.421 -1.103,-0.421 -1.523,-0c-0,-0 -0.931,0.93 -0.931,0.93c-0.391,-0.226 -0.81,-0.409 -1.252,-0.54c-0,-0 -0,-1.359 -0,-1.359c-0,-0.595 -0.483,-1.077 -1.077,-1.077l-1.154,-0c-0.594,-0 -1.077,0.482 -1.077,1.077l0,1.359Zm8.478,-3.064l-1.688,0c-0.276,0 -0.5,0.224 -0.5,0.5c-0,0.276 0.224,0.5 0.5,0.5l3,0c0.085,0 0.166,-0.021 0.236,-0.059c0.034,-0.018 0.066,-0.04 0.096,-0.067l0.042,-0.042c0.027,-0.03 0.049,-0.062 0.067,-0.096c0.038,-0.07 0.059,-0.151 0.059,-0.236l-0,-3c-0,-0.276 -0.224,-0.5 -0.5,-0.5c-0.276,0 -0.5,0.224 -0.5,0.5l-0,1.87c-4.712,-3.89 -11.709,-3.632 -16.117,0.777c-3.783,3.782 -4.51,9.471 -2.182,13.987c0.127,0.245 0.429,0.341 0.674,0.215c0.245,-0.126 0.342,-0.428 0.215,-0.674c-2.134,-4.139 -1.468,-9.354 2,-12.821c3.986,-3.987 10.282,-4.271 14.598,-0.854Zm-6.824,6.32c1.274,-0 2.308,1.034 2.308,2.308c-0,1.273 -1.034,2.307 -2.308,2.307c-1.274,0 -2.308,-1.034 -2.308,-2.307c0,-1.274 1.034,-2.308 2.308,-2.308Z"
        style={{ fill: 'url(#gradient-tasks)' }}
      />

      <defs>
        <linearGradient
          id="gradient-tasks"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const schedules = () => {
  return (
    <svg
      className="icon icon-schedules"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M19,4l1.5,-0c1.933,0 3.5,1.567 3.5,3.5l-0,15c-0,1.933 -1.567,3.5 -3.5,3.5l-17,-0c-1.933,-0 -3.5,-1.567 -3.5,-3.5l0,-15c0,-1.933 1.567,-3.5 3.5,-3.5l1.5,-0l0,-1.5c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l-0,1.5l12,-0l0,-1.5c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l0,1.5Zm-14,1l-1.5,-0c-1.381,0 -2.5,1.119 -2.5,2.5l0,0.5l22,-0l-0,-0.5c-0,-1.381 -1.119,-2.5 -2.5,-2.5l-1.5,0l0,1.5c0,0.276 -0.224,0.5 -0.5,0.5c-0.276,0 -0.5,-0.224 -0.5,-0.5l0,-1.5l-12,0l-0,1.5c0,0.276 -0.224,0.5 -0.5,0.5c-0.276,0 -0.5,-0.224 -0.5,-0.5l0,-1.5Zm-4,4l0,13.5c0,1.381 1.119,2.5 2.5,2.5l17,0c1.381,-0 2.5,-1.119 2.5,-2.5l-0,-13.5l-22,-0Zm4,7.503l-0,0.994c-0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l0,-0.994c0,-0.278 0.225,-0.503 0.503,-0.503l0.994,0c0.278,0 0.503,0.225 0.503,0.503Zm4,4l0,0.994c0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l0,-0.994c0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Zm8,-8l-0,0.994c-0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l-0,-0.994c-0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Zm-12,8l-0,0.994c-0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l0,-0.994c0,-0.278 0.225,-0.503 0.503,-0.503l0.994,0c0.278,0 0.503,0.225 0.503,0.503Zm8,-8l0,0.994c0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l0,-0.994c0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Zm-4,-0l0,0.994c0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l0,-0.994c0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Zm8,4l-0,0.994c-0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l-0,-0.994c-0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Zm-8,-0l0,0.994c0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l0,-0.994c0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Zm4,4l0,0.994c0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l0,-0.994c0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Zm0,-4l0,0.994c0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l0,-0.994c0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Zm8,-4l-0,0.994c-0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l-0,-0.994c-0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Zm-0,4l-0,0.994c-0,0.278 -0.225,0.503 -0.503,0.503l-0.994,-0c-0.278,-0 -0.503,-0.225 -0.503,-0.503l-0,-0.994c-0,-0.278 0.225,-0.503 0.503,-0.503l0.994,-0c0.278,-0 0.503,0.225 0.503,0.503Z"
        style={{ stroke: 'none', fill: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />

      <path
        d="M20,4l0.5,-0c1.933,0 3.5,1.567 3.5,3.5l-0,0.5l-24,-0l0,-0.5c0,-1.933 1.567,-3.5 3.5,-3.5l0.5,-0l0,1.5c0,0.828 0.672,1.5 1.5,1.5c0.828,-0 1.5,-0.672 1.5,-1.5l-0,-1.5l10,-0l0,1.5c0,0.828 0.672,1.5 1.5,1.5c0.828,-0 1.5,-0.672 1.5,-1.5l0,-1.5Zm4,5l-0,13.5c-0,1.933 -1.567,3.5 -3.5,3.5l-17,-0c-1.933,-0 -3.5,-1.567 -3.5,-3.5l0,-13.5l24,0Zm-15,3.503c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm4,-0c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm-4,4c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm-4,-0c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,0c-0.278,0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm4,4c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm4,-0c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm8,-4c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm-16,4c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,0c-0.278,0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm12,-8c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm4,-0c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm-8,4c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm4,-0c0,-0.278 -0.225,-0.503 -0.503,-0.503l-0.994,-0c-0.278,-0 -0.503,0.225 -0.503,0.503l0,0.994c0,0.278 0.225,0.503 0.503,0.503l0.994,-0c0.278,-0 0.503,-0.225 0.503,-0.503l0,-0.994Zm1,-14.003c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l0,3c0,0.276 -0.224,0.5 -0.5,0.5c-0.276,0 -0.5,-0.224 -0.5,-0.5l0,-3Zm-13,-0c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l0,3c0,0.276 -0.224,0.5 -0.5,0.5c-0.276,0 -0.5,-0.224 -0.5,-0.5l-0,-3Z"
        className="fill"
        style={{ stroke: 'none', fill: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />

      <defs>
        <linearGradient
          id="gradient-schedules"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#1a3626', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const settings = () => {
  return (
    <svg
      className="icon icon-settings"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M3.5,11.5l0,14"
        style={{ fill: 'none', stroke: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />
      <path
        d="M3.5,4.5l0,3.5"
        style={{ fill: 'none', stroke: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />
      <path
        d="M11.5,4.5l0,15"
        style={{ fill: 'none', stroke: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />
      <path
        d="M11.5,23l-0,2.5"
        style={{ fill: 'none', stroke: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />
      <path
        d="M19.5,4.5l0,8.987"
        style={{ fill: 'none', stroke: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />
      <path
        d="M19.5,16.487l0,9.013"
        style={{ fill: 'none', stroke: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />
      <path
        d="M6.5,9.502c0,-0.266 -0.106,-0.521 -0.293,-0.709c-0.188,-0.187 -0.443,-0.293 -0.709,-0.293c-1.122,0 -2.927,0 -4.033,0c-0.256,-0 -0.501,0.102 -0.682,0.283c-0.181,0.181 -0.283,0.426 -0.283,0.682c0,0.326 0,0.692 0,1.022c0,0.56 0.453,1.013 1.013,1.013c1.125,0 2.927,0 4.029,0c0.529,0 0.958,-0.429 0.958,-0.958c0,-0.332 0,-0.705 0,-1.04Z"
        style={{ fill: 'none', stroke: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />
      <path
        d="M14.5,20.502c0,-0.266 -0.106,-0.521 -0.293,-0.709c-0.188,-0.187 -0.443,-0.293 -0.709,-0.293c-1.122,-0 -2.927,-0 -4.033,-0c-0.256,-0 -0.501,0.102 -0.682,0.283c-0.181,0.181 -0.283,0.426 -0.283,0.682c-0,0.326 -0,0.692 -0,1.022c0,0.56 0.453,1.013 1.013,1.013c1.125,-0 2.927,-0 4.029,-0c0.529,0 0.958,-0.429 0.958,-0.958c-0,-0.332 -0,-0.705 -0,-1.04Z"
        style={{ fill: 'none', stroke: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />
      <path
        d="M22.5,14.488c0,-0.265 -0.106,-0.52 -0.293,-0.708c-0.188,-0.188 -0.443,-0.293 -0.709,-0.293c-1.122,-0 -2.927,-0 -4.033,-0c-0.256,-0 -0.501,0.101 -0.682,0.282c-0.181,0.181 -0.283,0.427 -0.283,0.682c0,0.327 0,0.693 0,1.023c0,0.559 0.453,1.013 1.013,1.013c1.125,-0 2.927,-0 4.029,-0c0.529,-0 0.958,-0.429 0.958,-0.958c0,-0.332 0,-0.705 0,-1.041Z"
        style={{ fill: 'none', stroke: 'url(#gradient-settings)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M11,24.5l-0,1c-0,0.276 0.224,0.5 0.5,0.5c0.276,-0 0.5,-0.224 0.5,-0.5l-0,-1c-0,-0.276 -0.224,-0.5 -0.5,-0.5c-0.276,-0 -0.5,0.224 -0.5,0.5Zm8,-6l0,7c0,0.276 0.224,0.5 0.5,0.5c0.276,-0 0.5,-0.224 0.5,-0.5l0,-7c0,-0.276 -0.224,-0.5 -0.5,-0.5c-0.276,-0 -0.5,0.224 -0.5,0.5Zm-16,-5.013l0,12.013c-0,0.276 0.224,0.5 0.5,0.5c0.276,0 0.5,-0.224 0.5,-0.5l0,-12.013c-0,-0.276 -0.224,-0.5 -0.5,-0.5c-0.276,-0 -0.5,0.224 -0.5,0.5Zm12,7.015c0,-0.398 -0.158,-0.78 -0.44,-1.062c-0.282,-0.282 -0.664,-0.44 -1.062,-0.44c-1.122,-0 -2.927,-0 -4.033,-0c-0.389,-0 -0.761,0.154 -1.036,0.429c-0.275,0.275 -0.429,0.647 -0.429,1.036l0,1.022c0,0.836 0.677,1.513 1.513,1.513l4.029,-0c0.805,0 1.458,-0.653 1.458,-1.458l-0,-1.04Zm-4,-16.002l0,13c0,0.276 0.224,0.5 0.5,0.5c0.276,-0 0.5,-0.224 0.5,-0.5l0,-13c0,-0.276 -0.224,-0.5 -0.5,-0.5c-0.276,-0 -0.5,0.224 -0.5,0.5Zm12,9.988c0,-0.398 -0.158,-0.78 -0.44,-1.061c-0.282,-0.282 -0.664,-0.44 -1.062,-0.44c-1.122,-0 -2.927,-0 -4.033,-0c-0.389,-0 -0.761,0.154 -1.036,0.429c-0.275,0.274 -0.429,0.647 -0.429,1.035l0,1.023c0,0.835 0.677,1.513 1.513,1.513l4.029,-0c0.805,-0 1.458,-0.653 1.458,-1.458l0,-1.041Zm-4,-9.988l0,7c0,0.276 0.224,0.5 0.5,0.5c0.276,0 0.5,-0.224 0.5,-0.5l0,-7c0,-0.276 -0.224,-0.5 -0.5,-0.5c-0.276,-0 -0.5,0.224 -0.5,0.5Zm-12,5.002c0,-0.398 -0.158,-0.78 -0.44,-1.062c-0.282,-0.282 -0.664,-0.44 -1.062,-0.44c-1.122,0 -2.927,0 -4.033,0c-0.389,-0 -0.761,0.154 -1.036,0.429c-0.275,0.275 -0.429,0.647 -0.429,1.036l0,1.022c0,0.836 0.677,1.513 1.513,1.513l4.029,0c0.805,0 1.458,-0.653 1.458,-1.458l0,-1.04Zm-4,-5.002l0,2c-0,0.276 0.224,0.5 0.5,0.5c0.276,0 0.5,-0.224 0.5,-0.5l0,-2c-0,-0.276 -0.224,-0.5 -0.5,-0.5c-0.276,0 -0.5,0.224 -0.5,0.5Z"
        style={{ fill: 'url(#gradient-settings)' }}
      />

      <defs>
        <linearGradient
          id="gradient-settings"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const toggleVisibility = () => {
  return (
    <svg
      className="icon icon-toggle-visibility"
      width="100%"
      height="100%"
      viewBox="0 0 17 14"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 2,
      }}
    >
      <path
        className="hide-icon"
        d="M1.809,0.853c-0.195,-0.195 -0.195,-0.512 0,-0.707c0.196,-0.195 0.512,-0.195 0.708,0l12.36,12.361c0.195,0.195 0.195,0.512 0,0.707c-0.195,0.195 -0.512,0.195 -0.707,0l-12.361,-12.361Zm6.251,3.847c-0.273,0.039 -0.527,-0.151 -0.565,-0.425c-0.039,-0.273 0.151,-0.526 0.424,-0.565c0.139,-0.02 0.28,-0.03 0.424,-0.03c1.656,0 3,1.344 3,3c0,0.144 -0.01,0.286 -0.029,0.424c-0.039,0.273 -0.293,0.464 -0.566,0.425c-0.273,-0.039 -0.463,-0.292 -0.425,-0.565c0.014,-0.093 0.02,-0.188 0.02,-0.284c0,-1.104 -0.896,-2 -2,-2c-0.096,0 -0.191,0.007 -0.283,0.02Zm0.567,3.96c0.273,-0.038 0.526,0.152 0.565,0.425c0.039,0.273 -0.151,0.527 -0.425,0.565c-0.138,0.02 -0.28,0.03 -0.424,0.03c-1.655,0 -3,-1.344 -3,-3c0,-0.144 0.011,-0.285 0.03,-0.424c0.039,-0.273 0.292,-0.463 0.566,-0.425c0.273,0.039 0.463,0.292 0.424,0.566c-0.013,0.092 -0.02,0.187 -0.02,0.283c0,1.104 0.897,2 2,2c0.097,0 0.191,-0.007 0.284,-0.02Zm7.059,-1.979c-0.888,-0.992 -3.833,-4.001 -7.343,-4.001c-0.571,0 -1.127,0.082 -1.661,0.221c-0.267,0.07 -0.541,-0.09 -0.61,-0.357c-0.07,-0.267 0.09,-0.541 0.357,-0.61c0.616,-0.161 1.256,-0.254 1.914,-0.254c3.865,0 7.136,3.268 8.098,4.345c0.002,0.003 0.004,0.006 0.007,0.009c0.318,0.372 0.318,0.92 0.001,1.293c-0.003,0.003 -0.005,0.006 -0.008,0.009c-0.449,0.502 -1.404,1.493 -2.688,2.399c-0.226,0.159 -0.538,0.105 -0.697,-0.12c-0.159,-0.226 -0.105,-0.538 0.12,-0.697c1.195,-0.842 2.084,-1.763 2.51,-2.237Zm-14.685,-0.002c0.887,0.993 3.832,4.001 7.342,4.001c0.572,0 1.128,-0.081 1.662,-0.221c0.267,-0.07 0.54,0.091 0.61,0.358c0.07,0.267 -0.09,0.54 -0.357,0.61c-0.616,0.16 -1.257,0.253 -1.915,0.253c-3.864,0 -7.135,-3.267 -8.097,-4.345c-0.002,-0.003 -0.005,-0.005 -0.007,-0.008c-0.318,-0.372 -0.319,-0.921 -0.001,-1.293c0.002,-0.003 0.005,-0.006 0.008,-0.009c0.449,-0.503 1.403,-1.494 2.688,-2.4c0.226,-0.159 0.538,-0.105 0.697,0.121c0.159,0.225 0.105,0.538 -0.121,0.697c-1.194,0.842 -2.084,1.762 -2.509,2.236Z"
        style={{ fill: '#B5B1B7' }}
      />

      <path
        className="show-icon"
        d="M8.343,1.68c3.737,0 6.919,2.924 7.922,3.978c0.005,0.005 0.01,0.011 0.016,0.017c0.517,0.57 0.518,1.439 0.001,2.01c-0.005,0.006 -0.011,0.012 -0.017,0.018c-1.003,1.054 -4.185,3.977 -7.922,3.977c-3.736,0 -6.918,-2.924 -7.921,-3.977c-0.005,-0.006 -0.011,-0.012 -0.016,-0.017c-0.518,-0.57 -0.518,-1.44 -0.001,-2.011c0.005,-0.006 0.011,-0.012 0.017,-0.018c1.003,-1.053 4.185,-3.977 7.921,-3.977Zm0,1c-3.405,0 -6.283,2.707 -7.197,3.667c-0.172,0.189 -0.171,0.477 0,0.666c0.914,0.96 3.792,3.667 7.197,3.667c3.405,0 6.284,-2.707 7.198,-3.666c0.171,-0.19 0.171,-0.478 -0,-0.667c-0.914,-0.96 -3.793,-3.667 -7.198,-3.667Zm0,1c1.656,0 3,1.344 3,3c0,1.656 -1.344,3 -3,3c-1.655,0 -3,-1.344 -3,-3c0,-1.656 1.345,-3 3,-3Zm0,1c-1.103,0 -2,0.896 -2,2c0,1.104 0.897,2 2,2c1.104,0 2,-0.896 2,-2c0,-1.104 -0.896,-2 -2,-2Z"
        style={{ fill: '#B5B1B7' }}
      />
    </svg>
  );
};

const capsLock = () => {
  return (
    <svg
      className="icon icon-caps_lock"
      width="100%"
      height="100%"
      viewBox="0 0 20 20"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 2,
      }}
    >
      <path
        d="M20,3.35l0,13.3c0,1.849 -1.501,3.35 -3.35,3.35l-13.3,-0c-1.849,-0 -3.35,-1.501 -3.35,-3.35l0,-13.3c0,-1.849 1.501,-3.35 3.35,-3.35l13.3,-0c1.849,-0 3.35,1.501 3.35,3.35Zm-7.188,9.444l0,-2.792c0,-0.276 0.224,-0.5 0.5,-0.5l2.981,-0c0.202,-0 0.385,-0.122 0.462,-0.309c0.077,-0.187 0.035,-0.402 -0.108,-0.545l-5.94,-5.941c-0.187,-0.187 -0.442,-0.293 -0.707,-0.293c-0.265,0 -0.52,0.106 -0.707,0.293l-5.94,5.941c-0.143,0.143 -0.185,0.358 -0.108,0.545c0.077,0.187 0.26,0.309 0.462,0.309l2.965,-0c0.276,-0 0.5,0.224 0.5,0.5l-0,2.792c-0,0.276 0.224,0.5 0.5,0.5l4.641,0c0.276,0 0.5,-0.224 0.5,-0.5Zm-0.5,2.382l-4.626,0c-0.132,0 -0.259,0.053 -0.353,0.147c-0.094,0.094 -0.147,0.221 -0.146,0.353l-0,1.824c-0.001,0.133 0.052,0.26 0.146,0.354c0.094,0.093 0.221,0.146 0.353,0.146l4.626,0c0.132,-0 0.259,-0.053 0.353,-0.146c0.094,-0.094 0.147,-0.221 0.147,-0.354l-0.001,-1.824c0.001,-0.132 -0.052,-0.259 -0.146,-0.353c-0.094,-0.094 -0.221,-0.147 -0.353,-0.147Z"
        style={{ fill: '#B5B1B7' }}
      />
    </svg>
  );
};
const attributeUnits = () => {
  return (
    <svg
      className="icon icon-attribute-units"
      width="100%"
      height="100%"
      viewBox="0 0 24 30"
      preserveAspectRatio="xMidYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M21.329,15.288c-0.094,-0.466 -0.504,-0.801 -0.98,-0.801c-2.09,-0 -7.608,-0 -9.698,-0c-0.476,-0 -0.886,0.335 -0.98,0.801c-0.418,2.066 -1.545,7.634 -2.193,10.839c-0.118,0.579 0.031,1.18 0.405,1.637c0.374,0.457 0.934,0.723 1.524,0.723c3.223,-0 8.906,-0 12.148,-0c0.6,-0 1.168,-0.27 1.548,-0.734c0.38,-0.465 0.531,-1.075 0.412,-1.663c-0.65,-3.211 -1.77,-8.744 -2.186,-10.802Z"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />
      <path
        d="M11.444,24.167c-0.053,-0 -0.099,-0.019 -0.136,-0.056c-0.037,-0.038 -0.056,-0.083 -0.056,-0.136l-0,-5.208c-0,-0.059 0.017,-0.107 0.052,-0.144c0.035,-0.038 0.081,-0.056 0.14,-0.056l0.72,-0c0.059,-0 0.107,0.018 0.144,0.056c0.037,0.037 0.056,0.085 0.056,0.144l-0,1.872l1.784,-1.928c0.08,-0.096 0.184,-0.144 0.312,-0.144l0.816,-0c0.043,-0 0.081,0.016 0.116,0.048c0.035,0.032 0.052,0.072 0.052,0.12c-0,0.037 -0.013,0.069 -0.04,0.096l-2.176,2.416l2.336,2.648c0.021,0.037 0.032,0.072 0.032,0.104c-0,0.048 -0.016,0.088 -0.048,0.12c-0.032,0.032 -0.069,0.048 -0.112,0.048l-0.84,-0c-0.112,-0 -0.192,-0.023 -0.24,-0.068c-0.048,-0.046 -0.075,-0.071 -0.08,-0.076l-1.912,-2.112l-0,2.064c-0,0.053 -0.019,0.098 -0.056,0.136c-0.037,0.037 -0.085,0.056 -0.144,0.056l-0.72,-0Zm3.448,-4.789l0.024,-0.027l-0.024,0.027Z"
        style={{ fill: 'url(#gradient-attribute-units)' }}
      />
      <path
        d="M15.87,22.766c-0.032,-0.158 -0.051,-0.329 -0.058,-0.511l-0.008,-0.216l0.008,-0.216c0.021,-0.571 0.173,-1.03 0.456,-1.376c0.283,-0.347 0.699,-0.52 1.248,-0.52c0.283,-0 0.531,0.053 0.744,0.16c0.213,0.106 0.389,0.25 0.528,0.432l-0,-0.312c-0,-0.059 0.02,-0.107 0.06,-0.144c0.04,-0.038 0.087,-0.056 0.14,-0.056l0.656,-0c0.053,-0 0.099,0.018 0.136,0.056c0.037,0.037 0.056,0.085 0.056,0.144l0.008,3.856c-0,0.592 -0.165,1.05 -0.496,1.376c-0.331,0.325 -0.829,0.488 -1.496,0.488c-0.475,-0 -0.859,-0.079 -1.152,-0.236c-0.293,-0.158 -0.501,-0.338 -0.624,-0.54c-0.123,-0.203 -0.184,-0.376 -0.184,-0.52c-0,-0.054 0.019,-0.098 0.056,-0.132c0.037,-0.035 0.083,-0.052 0.136,-0.052l0.712,-0c0.048,-0 0.089,0.012 0.124,0.036c0.035,0.024 0.063,0.068 0.084,0.132c0.059,0.138 0.147,0.257 0.264,0.356c0.117,0.098 0.299,0.148 0.544,0.148c0.32,-0 0.555,-0.071 0.704,-0.212c0.149,-0.142 0.224,-0.388 0.224,-0.74l-0,-0.544c-0.288,0.341 -0.696,0.512 -1.224,0.512c-0.555,-0 -0.972,-0.168 -1.252,-0.504c-0.192,-0.231 -0.323,-0.519 -0.394,-0.865l0.015,-0.014l-0.015,0.014Zm1.284,0.24c0.153,0.187 0.375,0.281 0.666,0.281c0.299,-0 0.523,-0.096 0.672,-0.288c0.149,-0.192 0.232,-0.427 0.248,-0.704c0.005,-0.043 0.008,-0.128 0.008,-0.256c-0,-0.128 -0.003,-0.214 -0.008,-0.256c-0.016,-0.278 -0.099,-0.512 -0.248,-0.704c-0.149,-0.192 -0.373,-0.288 -0.672,-0.288c-0.576,-0 -0.88,0.352 -0.912,1.056l-0.008,0.192c-0,0.286 0.036,0.523 0.109,0.711l-0.08,0.076l0.08,-0.076c0.036,0.092 0.08,0.172 0.133,0.24c-0.032,0.01 -0.061,0.017 -0.089,0.023l0.013,0.012c0.03,-0.005 0.059,-0.012 0.088,-0.019Z"
        style={{ fill: 'url(#gradient-attribute-units)' }}
      />
      <path
        d="M18.5,12.487c-0,-1.657 -1.343,-3 -3,-3c-1.657,-0 -3,1.343 -3,3c-0,1.062 -0,2 -0,2l6,-0l0,-2Z"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />
      <path
        d="M16.5,12.521c-0,-0.564 -0.457,-1.021 -1.021,-1.021c-0,0 -0.001,0 -0.001,0c-0.54,0 -0.978,0.438 -0.978,0.978c-0,0.871 -0,2.009 -0,2.009l2,-0l0,-1.966Z"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />
      <path
        d="M17.5,7.5l-0,-1.706c-0,-2.515 3,-5.294 3,-5.294l-20,0l0,24c-0,0.796 0.316,1.559 0.879,2.121c0.562,0.563 1.325,0.879 2.121,0.879c0.826,-0 1.5,-0 1.5,-0"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />
      <path
        d="M2.5,4.5l5,-0"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />
      <path
        d="M2.5,10.5l5,0"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />
      <path
        d="M2.5,16.5l5,0"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />
      <path
        d="M3.5,7.5l3,0"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />
      <path
        d="M3.5,13.5l3,0"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />
      <path
        d="M3.5,19.5l3,0"
        style={{ fill: 'none', stroke: 'url(#gradient-attribute-units)', strokeWidth: '1px' }}
      />

      <path
        className="fill"
        d="M21.819,15.189c-0.142,-0.699 -0.756,-1.202 -1.47,-1.202l-9.698,-0c-0.714,-0 -1.328,0.503 -1.47,1.202c-0.418,2.066 -1.545,7.633 -2.194,10.839c-0.146,0.726 0.04,1.479 0.509,2.053c0.469,0.573 1.171,0.906 1.911,0.906c3.223,-0 8.906,-0 12.148,-0c0.75,-0 1.46,-0.337 1.935,-0.918c0.475,-0.58 0.664,-1.343 0.515,-2.078l-2.186,-10.802Zm-3.819,-7.689l-0,-1.706c-0,-0.916 0.452,-1.864 0.977,-2.68c0.828,-1.288 1.863,-2.247 1.863,-2.247c0.151,-0.14 0.201,-0.358 0.126,-0.549c-0.076,-0.192 -0.26,-0.318 -0.466,-0.318l-20,0c-0.276,0 -0.5,0.224 -0.5,0.5l0,24c-0,0.928 0.369,1.818 1.025,2.475c0.657,0.656 1.547,1.025 2.475,1.025c0.826,-0 1.5,-0 1.5,-0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5c0,-0 -0.674,-0 -1.5,0c-0.663,0 -1.299,-0.263 -1.768,-0.732c-0.469,-0.469 -0.732,-1.105 -0.732,-1.768l0,-23.5c0,0 18.333,0 18.333,0c-0.368,0.418 -0.804,0.962 -1.198,1.574c-0.627,0.976 -1.135,2.124 -1.135,3.22l-0,1.706c-0,0.276 0.224,0.5 0.5,0.5c0.276,-0 0.5,-0.224 0.5,-0.5Zm-0.148,18.427c-0.475,-0 -0.859,-0.079 -1.152,-0.236c-0.293,-0.158 -0.501,-0.338 -0.624,-0.54c-0.123,-0.203 -0.184,-0.376 -0.184,-0.52c-0,-0.054 0.019,-0.098 0.056,-0.132c0.037,-0.035 0.083,-0.052 0.136,-0.052l0.712,-0c0.048,-0 0.089,0.012 0.124,0.036c0.035,0.024 0.063,0.068 0.084,0.132c0.059,0.138 0.147,0.257 0.264,0.356c0.117,0.098 0.299,0.148 0.544,0.148c0.32,-0 0.555,-0.071 0.704,-0.212c0.149,-0.142 0.224,-0.388 0.224,-0.74l-0,-0.544c-0.288,0.341 -0.696,0.512 -1.224,0.512c-0.555,-0 -0.972,-0.168 -1.252,-0.504c-0.28,-0.336 -0.431,-0.795 -0.452,-1.376l-0.008,-0.216l0.008,-0.216c0.021,-0.571 0.173,-1.03 0.456,-1.376c0.283,-0.347 0.699,-0.52 1.248,-0.52c0.283,-0 0.531,0.053 0.744,0.16c0.213,0.106 0.389,0.25 0.528,0.432l-0,-0.312c-0,-0.059 0.02,-0.107 0.06,-0.144c0.04,-0.038 0.087,-0.056 0.14,-0.056l0.656,-0c0.053,-0 0.099,0.018 0.136,0.056c0.037,0.037 0.056,0.085 0.056,0.144l0.008,3.856c-0,0.592 -0.165,1.05 -0.496,1.376c-0.331,0.325 -0.829,0.488 -1.496,0.488Zm-6.408,-1.76c-0.053,-0 -0.099,-0.019 -0.136,-0.056c-0.037,-0.038 -0.056,-0.083 -0.056,-0.136l-0,-5.208c-0,-0.059 0.017,-0.107 0.052,-0.144c0.035,-0.038 0.081,-0.056 0.14,-0.056l0.72,-0c0.059,-0 0.107,0.018 0.144,0.056c0.037,0.037 0.056,0.085 0.056,0.144l-0,1.872l1.784,-1.928c0.08,-0.096 0.184,-0.144 0.312,-0.144l0.816,-0c0.043,-0 0.081,0.016 0.116,0.048c0.035,0.032 0.052,0.072 0.052,0.12c-0,0.037 -0.013,0.069 -0.04,0.096l-2.176,2.416l2.336,2.648c0.021,0.037 0.032,0.072 0.032,0.104c-0,0.048 -0.016,0.088 -0.048,0.12c-0.032,0.032 -0.069,0.048 -0.112,0.048l-0.84,-0c-0.112,-0 -0.192,-0.023 -0.24,-0.068c-0.048,-0.046 -0.075,-0.071 -0.08,-0.076l-1.912,-2.112l-0,2.064c-0,0.053 -0.019,0.098 -0.056,0.136c-0.037,0.037 -0.085,0.056 -0.144,0.056l-0.72,-0Zm6.376,-0.88c0.299,-0 0.523,-0.096 0.672,-0.288c0.149,-0.192 0.232,-0.427 0.248,-0.704c0.005,-0.043 0.008,-0.128 0.008,-0.256c-0,-0.128 -0.003,-0.214 -0.008,-0.256c-0.016,-0.278 -0.099,-0.512 -0.248,-0.704c-0.149,-0.192 -0.373,-0.288 -0.672,-0.288c-0.576,-0 -0.88,0.352 -0.912,1.056l-0.008,0.192c-0,0.832 0.307,1.248 0.92,1.248Zm-14.32,-3.287l3,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-3,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm-1,-3l5,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-5,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm1,-3l3,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-3,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm8.48,-0.013l2.02,-0l-0,-1.509c-0,-0.816 0.662,-1.478 1.478,-1.478l0.001,-0c0.84,0 1.521,0.681 1.521,1.521l-0,1.466l2,-0l-0,-1.5c-0,-1.933 -1.567,-3.5 -3.5,-3.5c0,-0 -0.276,-0 -0,-0c-1.945,-0 -3.45,1.593 -3.5,3.5c-0.013,0.499 -0.03,1 -0.02,1.5Zm-9.48,-2.987l5,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-5,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm1,-3l3,0c0.276,-0 0.5,-0.224 0.5,-0.5c-0,-0.276 -0.224,-0.5 -0.5,-0.5l-3,0c-0.276,-0 -0.5,0.224 -0.5,0.5c-0,0.276 0.224,0.5 0.5,0.5Zm-1,-3l5,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-5,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Z"
        style={{ fill: 'url(#gradient-attribute-units)' }}
      />

      <defs>
        <linearGradient
          id="gradient-attribute-units"
          x1="50%"
          y1="50%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" style={{ stopColor: '#2C1736', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#132139', stopOpacity: '1' }} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const descriptions = () => {
  return (
    <svg
      className="icon icon-descriptions"
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1.5,
      }}
    >
      <path
        d="M23.5,7.5c0,-1.105 -0.895,-2 -2,-2c-4.01,-0 -12.99,-0 -17,-0c-1.105,0 -2,0.895 -2,2c0,2.871 0,8.129 0,11c-0,1.105 0.895,2 2,2c4.01,-0 12.99,-0 17,-0c1.105,-0 2,-0.895 2,-2c0,-2.871 0,-8.129 0,-11Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M20,3.5l-17.5,0c-0.53,-0 -1.039,0.211 -1.414,0.586c-0.375,0.375 -0.586,0.884 -0.586,1.414c-0,3.758 -0,11.5 -0,11.5"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />

      <path d="M5.5,17.5l7,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path d="M5.5,14.5l15,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path d="M5.5,11.5l15,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path d="M5.5,8.5l15,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />

      <path
        className="fill"
        d="M24,7.5l0,11c0,1.381 -1.119,2.5 -2.5,2.5l-17,-0c-1.381,-0 -2.5,-1.119 -2.5,-2.5l0,-11c-0,-1.381 1.119,-2.5 2.5,-2.5l17,0c1.381,0 2.5,1.119 2.5,2.5Zm-18.5,7.5l15,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-15,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm0,-6l15,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-15,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm0,9l7,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-7,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm0,-6l15,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-15,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm14.5,-9c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-17.5,-0c-0.398,-0 -0.779,0.158 -1.061,0.439c-0.281,0.282 -0.439,0.663 -0.439,1.061l-0,11.5c-0,0.276 -0.224,0.5 -0.5,0.5c-0.276,-0 -0.5,-0.224 -0.5,-0.5l-0,-11.5c-0,-0.663 0.263,-1.299 0.732,-1.768c0.469,-0.469 1.105,-0.732 1.768,-0.732l17.5,0Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const descriptionsSmall = () => {
  return (
    <svg
      className="icon icon-small_descriptions"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1.5,
      }}
    >
      <path
        d="M11.5,1.5c0.53,-0 1.039,0.211 1.414,0.586c0.375,0.375 0.586,0.884 0.586,1.414c0,1.986 0,5.014 0,7c0,0.53 -0.211,1.039 -0.586,1.414c-0.375,0.375 -0.884,0.586 -1.414,0.586c-2.444,-0 -6.556,-0 -9,-0c-0.53,0 -1.039,-0.211 -1.414,-0.586c-0.375,-0.375 -0.586,-0.884 -0.586,-1.414c-0,-1.986 -0,-5.014 -0,-7c-0,-0.53 0.211,-1.039 0.586,-1.414c0.375,-0.375 0.884,-0.586 1.414,-0.586c2.444,-0 6.556,-0 9,-0Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />

      <path d="M3.5,5.5l7,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path d="M3.5,8.5l4,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />

      <path
        className="fill"
        d="M11.5,1c0.663,-0 1.299,0.263 1.768,0.732c0.469,0.469 0.732,1.105 0.732,1.768l-0,7c0,0.663 -0.263,1.299 -0.732,1.768c-0.469,0.469 -1.105,0.732 -1.768,0.732l-9,-0c-0.663,0 -1.299,-0.263 -1.768,-0.732c-0.469,-0.469 -0.732,-1.105 -0.732,-1.768l0,-7c-0,-0.663 0.263,-1.299 0.732,-1.768c0.469,-0.469 1.105,-0.732 1.768,-0.732l9,-0Zm-8,8l4,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-4,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Zm0,-3l7,0c0.276,0 0.5,-0.224 0.5,-0.5c0,-0.276 -0.224,-0.5 -0.5,-0.5l-7,0c-0.276,0 -0.5,0.224 -0.5,0.5c0,0.276 0.224,0.5 0.5,0.5Z"
        style={{ fill: '#222020', stroke: 'none', strokeWidth: '1px' }}
      />
    </svg>
  );
};

const statistics = () => {
  return (
    <svg
      className="icon icon-statistics"
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1.5,
      }}
    >
      <path
        d="M18.5,4.5l0,3l-15,0l-0,-3l15,0Zm4,6l-0,3l-19,0l-0,-3l19,0Zm-10,6l0,3l-9,0l-0,-3l9,0Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path d="M1.5,2.5l0,19" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />

      <path
        className="fill"
        d="M19,4.5l0,3c0,0.276 -0.224,0.5 -0.5,0.5l-15,0c-0.276,-0 -0.5,-0.224 -0.5,-0.5l-0,-3c-0,-0.276 0.224,-0.5 0.5,-0.5l15,0c0.276,0 0.5,0.224 0.5,0.5Zm4,6l0,3c0,0.276 -0.224,0.5 -0.5,0.5l-19,0c-0.276,0 -0.5,-0.224 -0.5,-0.5l-0,-3c-0,-0.276 0.224,-0.5 0.5,-0.5l19,0c0.276,0 0.5,0.224 0.5,0.5Zm-10,6l0,3c0,0.276 -0.224,0.5 -0.5,0.5l-9,0c-0.276,0 -0.5,-0.224 -0.5,-0.5l-0,-3c-0,-0.276 0.224,-0.5 0.5,-0.5l9,0c0.276,0 0.5,0.224 0.5,0.5Zm-12,-14c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l-0,19c-0,0.276 -0.224,0.5 -0.5,0.5c-0.276,-0 -0.5,-0.224 -0.5,-0.5l0,-19Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

/**
 * The tree option of the asset tables' list mode control: a row with two indented rows below it,
 * joined by the same connector the table draws. Outlined, with a filled twin shown when the option
 * is the active one.
 */
const tree = () => {
  return (
    <svg
      className="icon icon-tree"
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1.5,
      }}
    >
      <path
        className="fill"
        d="M8,19.5l0,-7c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l0,1.5l1.5,0c0.276,0 0.5,0.224 0.5,0.5c-0,0.276 -0.224,0.5 -0.5,0.5l-1.5,0l0,4l1.5,0c0.276,0 0.5,0.224 0.5,0.5c-0,0.276 -0.224,0.5 -0.5,0.5l-2,-0c-0.276,0 -0.5,-0.224 -0.5,-0.5Zm9,-10c0,0.828 -0.672,1.5 -1.5,1.5l-7,0c-0.828,0 -1.5,-0.672 -1.5,-1.5c-0,-0.828 0.672,-1.5 1.5,-1.5l7,0c0.828,0 1.5,0.672 1.5,1.5Zm5,5c-0,0.828 -0.672,1.5 -1.5,1.5l-7,0c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5l7,0c0.828,0 1.5,0.672 1.5,1.5Zm-0,5c-0,0.828 -0.672,1.5 -1.5,1.5l-7,0c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5l7,0c0.828,0 1.5,0.672 1.5,1.5Zm-10,-15c0,0.828 -0.672,1.5 -1.5,1.5l-7,0c-0.828,0 -1.5,-0.672 -1.5,-1.5c-0,-0.828 0.672,-1.5 1.5,-1.5l7,0c0.828,0 1.5,0.672 1.5,1.5Zm-9,3c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l0,1.5l1.5,0c0.276,0 0.5,0.224 0.5,0.5c-0,0.276 -0.224,0.5 -0.5,0.5l-2,0c-0.276,0 -0.5,-0.224 -0.5,-0.5l0,-2Z"
        style={{ fill: '#222020', stroke: 'none' }}
      />
      <path
        d="M16.5,9.5c0,0.552 -0.448,1 -1,1l-7,0c-0.552,0 -1,-0.448 -1,-1c-0,-0.552 0.448,-1 1,-1l7,0c0.552,0 1,0.448 1,1Zm5,5c0,0.552 -0.448,1 -1,1l-7,0c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1l7,0c0.552,0 1,0.448 1,1Zm0,5c0,0.552 -0.448,1 -1,1l-7,0c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1l7,0c0.552,0 1,0.448 1,1Zm-10,-15c0,0.552 -0.448,1 -1,1l-7,0c-0.552,0 -1,-0.448 -1,-1c-0,-0.552 0.448,-1 1,-1l7,0c0.552,0 1,0.448 1,1Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path d="M3.5,7.5l0,2l2,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path d="M8.5,12.5l0,2l2,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path
        d="M8.5,14.5l-0,5l2,0"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
    </svg>
  );
};

/**
 * The flat option of the asset tables' list mode control: the same three rows as the tree icon, all
 * at one level. Outlined, with a filled twin shown when the option is the active one.
 */
const flat = () => {
  return (
    <svg
      className="icon icon-flat"
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      preserveAspectRatio="xMinYMid meet"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1.5,
      }}
    >
      <path
        className="fill"
        d="M3.5,3c0.828,0 1.5,0.672 1.5,1.5c0,0.828 -0.672,1.5 -1.5,1.5c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5Zm18.5,1.5c0,0.828 -0.672,1.5 -1.5,1.5l-12,0c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5l12,0c0.828,0 1.5,0.672 1.5,1.5Zm0,5c0,0.828 -0.672,1.5 -1.5,1.5l-12,0c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5l12,0c0.828,0 1.5,0.672 1.5,1.5Zm0,5c0,0.828 -0.672,1.5 -1.5,1.5l-12,0c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5l12,0c0.828,0 1.5,0.672 1.5,1.5Zm0,5c0,0.828 -0.672,1.5 -1.5,1.5l-12,0c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5l12,0c0.828,0 1.5,0.672 1.5,1.5Zm-18.5,-11.5c0.828,0 1.5,0.672 1.5,1.5c0,0.828 -0.672,1.5 -1.5,1.5c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5Zm0,5c0.828,0 1.5,0.672 1.5,1.5c0,0.828 -0.672,1.5 -1.5,1.5c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5Zm0,5c0.828,0 1.5,0.672 1.5,1.5c0,0.828 -0.672,1.5 -1.5,1.5c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5Z"
        style={{ fill: '#222020', stroke: 'none' }}
      />
      <path
        d="M3.5,3.5c0.552,0 1,0.448 1,1c0,0.552 -0.448,1 -1,1c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1Zm18,1c0,0.552 -0.448,1 -1,1l-12,0c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1l12,0c0.552,0 1,0.448 1,1Zm0,5c0,0.552 -0.448,1 -1,1l-12,0c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1l12,0c0.552,0 1,0.448 1,1Zm0,5c0,0.552 -0.448,1 -1,1l-12,0c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1l12,0c0.552,0 1,0.448 1,1Zm0,5c0,0.552 -0.448,1 -1,1l-12,0c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1l12,0c0.552,0 1,0.448 1,1Zm-18,-11c0.552,0 1,0.448 1,1c0,0.552 -0.448,1 -1,1c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1Zm0,5c0.552,0 1,0.448 1,1c0,0.552 -0.448,1 -1,1c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1Zm0,5c0.552,0 1,0.448 1,1c0,0.552 -0.448,1 -1,1c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
    </svg>
  );
};

const editorBold = () => {
  return (
    <svg
      className="icon-editor"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        id="Bold"
        d="M2.057,14c-0.133,-0 -0.247,-0.047 -0.34,-0.14c-0.093,-0.093 -0.14,-0.207 -0.14,-0.34l0,-13.02c0,-0.147 0.043,-0.267 0.13,-0.36c0.087,-0.093 0.203,-0.14 0.35,-0.14l5.8,0c1.56,0 2.727,0.343 3.5,1.03c0.773,0.687 1.16,1.623 1.16,2.81c0,0.693 -0.17,1.283 -0.51,1.77c-0.34,0.487 -0.737,0.837 -1.19,1.05c0.56,0.253 1.033,0.667 1.42,1.24c0.387,0.573 0.58,1.227 0.58,1.96c0,1.24 -0.42,2.24 -1.26,3c-0.84,0.76 -2.013,1.14 -3.52,1.14l-5.98,-0Zm5.44,-8.26c0.68,-0 1.203,-0.167 1.57,-0.5c0.367,-0.333 0.55,-0.78 0.55,-1.34c0,-0.56 -0.18,-0.993 -0.54,-1.3c-0.36,-0.307 -0.887,-0.46 -1.58,-0.46l-3.1,0l0,3.6l3.1,-0Zm0.2,6.12c0.693,-0 1.233,-0.19 1.62,-0.57c0.387,-0.38 0.58,-0.863 0.58,-1.45c0,-0.587 -0.197,-1.07 -0.59,-1.45c-0.393,-0.38 -0.93,-0.57 -1.61,-0.57l-3.3,-0l0,4.04l3.3,-0Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const editorItalic = () => {
  return (
    <svg
      className="icon-editor"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        id="Italic"
        d="M7.193,1.628l-0.492,0.002c-0.133,0 -0.243,-0.04 -0.33,-0.12c-0.087,-0.08 -0.13,-0.187 -0.13,-0.32l0,-0.733c0,-0.147 0.04,-0.26 0.12,-0.34c0.08,-0.08 0.193,-0.12 0.34,-0.12l2.839,-0.01c0.133,-0 0.243,0.043 0.33,0.13c0.087,0.086 0.13,0.196 0.13,0.33l-0,0.733c-0,0.133 -0.043,0.24 -0.13,0.32c-0.087,0.08 -0.197,0.12 -0.33,0.12l-0.425,0.001l-2.299,10.737l0.483,-0.001c0.133,-0 0.243,0.043 0.33,0.13c0.087,0.086 0.13,0.196 0.13,0.33l-0,0.733c-0,0.133 -0.043,0.24 -0.13,0.32c-0.087,0.08 -0.197,0.12 -0.33,0.12l-1.223,0.004c-0.017,0.002 -0.034,0.003 -0.052,0.003l-0.593,-0l-0.971,0.003c-0.133,-0 -0.243,-0.04 -0.33,-0.12c-0.087,-0.08 -0.13,-0.187 -0.13,-0.32l-0,-0.733c-0,-0.147 0.04,-0.26 0.12,-0.34c0.08,-0.08 0.193,-0.12 0.34,-0.12l0.435,-0.002l2.298,-10.737Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const editorUnderline = () => {
  return (
    <svg
      className="icon-editor"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M7.009,12c-1.506,-0 -2.667,-0.392 -3.484,-1.175c-0.816,-0.783 -1.225,-1.969 -1.225,-3.557l0,-6.862c0,-0.124 0.035,-0.223 0.105,-0.296c0.07,-0.073 0.164,-0.11 0.28,-0.11l0.84,-0c0.129,-0 0.231,0.037 0.307,0.11c0.076,0.073 0.114,0.172 0.114,0.296l-0,6.895c-0,1.105 0.265,1.927 0.796,2.468c0.531,0.541 1.287,0.811 2.267,0.811c0.968,0 1.718,-0.273 2.249,-0.819c0.531,-0.547 0.796,-1.367 0.796,-2.46l0,-6.895c0,-0.124 0.036,-0.223 0.106,-0.296c0.07,-0.073 0.169,-0.11 0.297,-0.11l0.84,0c0.117,0 0.213,0.037 0.289,0.11c0.076,0.073 0.114,0.172 0.114,0.296l-0,6.862c-0,1.588 -0.406,2.774 -1.217,3.557c-0.811,0.783 -1.969,1.175 -3.474,1.175Z"
        style={{ fill: '#222020' }}
      />
      <path d="M1,13l12,0l0,1.003l-12,0l-0,-1.003Z" style={{ fill: '#222020' }} />
    </svg>
  );
};

const editorOl = () => {
  return (
    <svg
      className="icon-editor"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M1.427,3.965c-0.037,-0 -0.07,-0.014 -0.099,-0.043c-0.028,-0.028 -0.042,-0.061 -0.042,-0.099l-0,-2.651l-0.759,0.584c-0.03,0.019 -0.059,0.028 -0.085,0.028c-0.046,0 -0.085,-0.023 -0.119,-0.068l-0.351,-0.453c-0.015,-0.026 -0.023,-0.055 -0.023,-0.085c0,-0.053 0.021,-0.094 0.062,-0.124l1.314,-1.014c0.038,-0.027 0.082,-0.04 0.131,-0.04l0.747,-0c0.042,-0 0.077,0.013 0.105,0.04c0.028,0.026 0.043,0.06 0.043,0.102l-0,3.681c-0,0.042 -0.015,0.076 -0.043,0.102c-0.028,0.027 -0.063,0.04 -0.105,0.04l-0.776,-0Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M0.096,9.014c-0.037,-0 -0.071,-0.015 -0.099,-0.043c-0.028,-0.028 -0.042,-0.061 -0.042,-0.099l-0,-0.425c-0,-0.113 0.047,-0.208 0.141,-0.283l0.527,-0.515c0.359,-0.272 0.63,-0.484 0.813,-0.635c0.183,-0.151 0.323,-0.288 0.419,-0.41c0.096,-0.123 0.144,-0.239 0.144,-0.349c0,-0.279 -0.139,-0.419 -0.419,-0.419c-0.147,0 -0.26,0.044 -0.34,0.133c-0.079,0.089 -0.132,0.199 -0.158,0.331c-0.023,0.087 -0.087,0.131 -0.193,0.131l-0.747,-0c-0.034,-0 -0.064,-0.013 -0.088,-0.037c-0.025,-0.025 -0.037,-0.052 -0.037,-0.082c0.008,-0.249 0.076,-0.475 0.207,-0.677c0.13,-0.202 0.312,-0.363 0.546,-0.481c0.234,-0.119 0.504,-0.179 0.81,-0.179c0.317,0 0.591,0.051 0.822,0.153c0.23,0.102 0.405,0.245 0.523,0.431c0.119,0.185 0.179,0.4 0.179,0.645c-0,0.272 -0.084,0.518 -0.252,0.737c-0.168,0.219 -0.424,0.456 -0.768,0.713l-0.47,0.47l1.433,0c0.042,0 0.076,0.014 0.102,0.04c0.027,0.026 0.04,0.06 0.04,0.102l-0,0.606c-0,0.038 -0.013,0.071 -0.04,0.099c-0.026,0.028 -0.06,0.043 -0.102,0.043l-2.951,-0Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M1.609,14c-0.37,-0 -0.681,-0.058 -0.932,-0.173c-0.251,-0.115 -0.436,-0.257 -0.555,-0.427c-0.119,-0.17 -0.182,-0.34 -0.19,-0.51c0,-0.034 0.012,-0.062 0.037,-0.085c0.024,-0.023 0.054,-0.034 0.088,-0.034l0.781,-0c0.046,-0 0.081,0.008 0.108,0.025c0.026,0.017 0.051,0.045 0.074,0.083c0.071,0.185 0.27,0.277 0.594,0.277c0.185,0 0.329,-0.043 0.431,-0.13c0.102,-0.087 0.153,-0.204 0.153,-0.351c-0,-0.284 -0.189,-0.425 -0.567,-0.425l-0.634,-0c-0.042,-0 -0.077,-0.014 -0.105,-0.043c-0.028,-0.028 -0.042,-0.063 -0.042,-0.104l-0,-0.335c-0,-0.086 0.032,-0.151 0.096,-0.192l0.912,-0.748l-1.563,0c-0.038,0 -0.071,-0.014 -0.1,-0.042c-0.028,-0.029 -0.042,-0.062 -0.042,-0.099l-0,-0.561c-0,-0.042 0.013,-0.077 0.04,-0.105c0.026,-0.028 0.06,-0.042 0.102,-0.042l2.639,-0c0.041,-0 0.075,0.014 0.102,0.042c0.026,0.028 0.04,0.063 0.04,0.105l-0,0.504c-0,0.079 -0.031,0.142 -0.091,0.187l-0.838,0.764l0.039,0.012c0.34,0.049 0.61,0.166 0.81,0.351c0.2,0.185 0.3,0.451 0.3,0.799c0,0.253 -0.072,0.474 -0.218,0.665c-0.145,0.191 -0.345,0.337 -0.6,0.439c-0.255,0.102 -0.545,0.153 -0.869,0.153Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M14,1.75c0,-0.138 -0.112,-0.25 -0.25,-0.25l-9.5,0c-0.138,0 -0.25,0.112 -0.25,0.25l0,0.5c0,0.138 0.112,0.25 0.25,0.25l9.5,0c0.138,0 0.25,-0.112 0.25,-0.25l0,-0.5Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M14,6.75c0,-0.138 -0.112,-0.25 -0.25,-0.25l-9.5,0c-0.138,0 -0.25,0.112 -0.25,0.25l0,0.5c0,0.138 0.112,0.25 0.25,0.25l9.5,0c0.138,0 0.25,-0.112 0.25,-0.25l0,-0.5Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M14,11.75c0,-0.138 -0.112,-0.25 -0.25,-0.25l-9.5,0c-0.138,0 -0.25,0.112 -0.25,0.25l0,0.5c0,0.138 0.112,0.25 0.25,0.25l9.5,0c0.138,0 0.25,-0.112 0.25,-0.25l0,-0.5Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const editorUl = () => {
  return (
    <svg
      className="icon-editor"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M1.5,10.5c0.384,0 0.735,0.145 1,0.382c0.307,0.275 0.5,0.674 0.5,1.118c0,0.828 -0.672,1.5 -1.5,1.5c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M1.5,5.5c0.384,0 0.735,0.145 1,0.382c0.307,0.275 0.5,0.674 0.5,1.118c0,0.828 -0.672,1.5 -1.5,1.5c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M1.5,0.5c0.384,0 0.735,0.145 1,0.382c0.307,0.275 0.5,0.674 0.5,1.118c0,0.828 -0.672,1.5 -1.5,1.5c-0.828,0 -1.5,-0.672 -1.5,-1.5c0,-0.828 0.672,-1.5 1.5,-1.5Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M14,11.75c0,-0.138 -0.112,-0.25 -0.25,-0.25l-9.5,0c-0.138,0 -0.25,0.112 -0.25,0.25l0,0.5c0,0.138 0.112,0.25 0.25,0.25l9.5,0c0.138,0 0.25,-0.112 0.25,-0.25l0,-0.5Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M14,6.75c0,-0.138 -0.112,-0.25 -0.25,-0.25l-9.5,0c-0.138,0 -0.25,0.112 -0.25,0.25l0,0.5c0,0.138 0.112,0.25 0.25,0.25l9.5,0c0.138,0 0.25,-0.112 0.25,-0.25l0,-0.5Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M14,1.75c0,-0.138 -0.112,-0.25 -0.25,-0.25l-9.5,0c-0.138,0 -0.25,0.112 -0.25,0.25l0,0.5c0,0.138 0.112,0.25 0.25,0.25l9.5,0c0.138,0 0.25,-0.112 0.25,-0.25l0,-0.5Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const editorReference = () => {
  return (
    <svg
      className="icon-editor"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M6.937,14c-1.87,0 -3.335,-0.474 -4.395,-1.421c-1.06,-0.947 -1.68,-2.29 -1.859,-4.029c-0.035,-0.345 -0.053,-0.864 -0.053,-1.554c-0,-0.703 0.018,-1.204 0.053,-1.501c0.191,-1.692 0.816,-3.029 1.877,-4.012c1.06,-0.983 2.501,-1.474 4.324,-1.474c2.096,-0 3.672,0.473 4.726,1.42c1.054,0.948 1.629,2.303 1.724,4.066c0.024,0.548 0.036,0.964 0.036,1.25c0,0.489 -0.012,0.852 -0.036,1.09c-0.047,0.858 -0.283,1.513 -0.705,1.966c-0.423,0.453 -0.992,0.679 -1.707,0.679c-0.512,-0 -0.929,-0.11 -1.251,-0.331c-0.321,-0.22 -0.56,-0.449 -0.714,-0.688c-0.203,0.334 -0.474,0.617 -0.813,0.849c-0.34,0.232 -0.819,0.349 -1.439,0.349c-0.905,-0 -1.617,-0.319 -2.135,-0.956c-0.518,-0.638 -0.777,-1.534 -0.777,-2.69c-0,-1.143 0.262,-2.04 0.786,-2.689c0.524,-0.649 1.227,-0.974 2.108,-0.974c0.441,0 0.801,0.078 1.081,0.233c0.28,0.154 0.527,0.363 0.742,0.625l-0,-0.304c-0,-0.119 0.039,-0.217 0.116,-0.295c0.077,-0.077 0.176,-0.116 0.295,-0.116l0.464,0c0.12,0 0.218,0.039 0.295,0.116c0.078,0.078 0.116,0.176 0.116,0.295l0,3.824c0,0.489 0.105,0.864 0.313,1.126c0.209,0.262 0.48,0.393 0.813,0.393c0.429,-0 0.724,-0.158 0.885,-0.474c0.16,-0.315 0.247,-0.67 0.259,-1.063c0.024,-0.286 0.035,-0.655 0.035,-1.108c0,-0.202 -0.011,-0.542 -0.035,-1.018c-0.107,-1.453 -0.563,-2.54 -1.367,-3.261c-0.804,-0.721 -2.076,-1.081 -3.815,-1.081c-1.453,-0 -2.594,0.378 -3.422,1.135c-0.828,0.756 -1.325,1.825 -1.492,3.207c-0.036,0.298 -0.054,0.768 -0.054,1.412c0,0.631 0.018,1.119 0.054,1.465c0.155,1.382 0.652,2.445 1.492,3.189c0.84,0.745 1.998,1.117 3.475,1.117c0.787,0 1.433,-0.065 1.939,-0.196c0.506,-0.132 0.867,-0.26 1.081,-0.385c0.215,-0.125 0.495,-0.318 0.84,-0.58c0.024,-0.024 0.077,-0.069 0.161,-0.134c0.083,-0.066 0.185,-0.099 0.304,-0.099l0.679,0c0.107,0 0.199,0.036 0.277,0.108c0.077,0.071 0.116,0.166 0.116,0.285c-0,0.084 -0.03,0.173 -0.09,0.268c-0.559,0.596 -1.259,1.073 -2.099,1.43c-0.84,0.357 -1.909,0.536 -3.208,0.536Zm-0.142,-4.574c0.619,-0 1.06,-0.209 1.322,-0.626c0.262,-0.417 0.393,-1.012 0.393,-1.787c-0,-0.786 -0.134,-1.39 -0.402,-1.813c-0.268,-0.423 -0.706,-0.635 -1.313,-0.635c-1.156,0 -1.734,0.816 -1.734,2.448c0,0.715 0.143,1.296 0.429,1.743c0.286,0.446 0.721,0.67 1.305,0.67Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const editorTable = () => {
  return (
    <svg
      className="icon-editor icon-editor_table"
      width="100%"
      height="100%"
      viewBox="0 0 13 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M0,12.5l0,-11c-0,-0.276 0.224,-0.5 0.5,-0.5l12,0c0.276,0 0.5,0.224 0.5,0.5l0,11c0,0.276 -0.224,0.5 -0.5,0.5l-12,0c-0.276,0 -0.5,-0.224 -0.5,-0.5Zm12,-6.5l0,-2l-3,-0l-0,2l3,-0Zm0,3l0,-2l-3,0l0,2l3,-0Zm-3,3l3,0l0,-2l-3,-0l0,2Zm-4,0l3,-0l-0,-2l-3,-0l-0,2Zm-4,-2l0,2l3,0l0,-2l-3,-0Zm0,-3l0,2l3,-0l0,-2l-3,0Zm0,-3l0,2l3,-0l0,-2l-3,0Zm7,-0l-3,0l-0,2l3,-0l-0,-2Zm-0,5l-0,-2l-3,0l-0,2l3,-0Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const editorRelationships = () => {
  return (
    <svg
      className="icon-editor icon-editor_relationships"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M3.825,10.181c0.112,0.25 0.175,0.527 0.175,0.819c0,1.104 -0.896,2 -2,2c-1.104,-0 -2,-0.896 -2,-2c0,-1.104 0.896,-2 2,-2c0.45,-0 0.865,0.149 1.2,0.4l1.975,-1.581c-0.112,-0.25 -0.175,-0.527 -0.175,-0.819c-0,-0.371 0.101,-0.718 0.277,-1.016l-1.261,-1.261c-0.298,0.176 -0.645,0.277 -1.016,0.277c-1.104,-0 -2,-0.896 -2,-2c-0,-1.104 0.896,-2 2,-2c1.104,-0 2,0.896 2,2c-0,0.371 -0.101,0.718 -0.277,1.016l1.261,1.261c0.298,-0.176 0.645,-0.277 1.016,-0.277c0.086,-0 0.172,0.005 0.255,0.016l0.562,-1.404c-0.495,-0.364 -0.817,-0.951 -0.817,-1.612c-0,-1.104 0.896,-2 2,-2c1.104,-0 2,0.896 2,2c-0,1.104 -0.896,2 -2,2c-0.086,-0 -0.172,-0.005 -0.255,-0.016l-0.562,1.404c0.262,0.192 0.475,0.447 0.618,0.742l1.202,-0.241c0.057,-1.052 0.93,-1.889 1.997,-1.889c1.104,-0 2,0.896 2,2c0,1.104 -0.896,2 -2,2c-0.792,-0 -1.477,-0.461 -1.801,-1.13l-1.202,0.241c-0.018,0.329 -0.116,0.637 -0.274,0.905l2.261,2.261c0.298,-0.176 0.645,-0.277 1.016,-0.277c1.104,-0 2,0.896 2,2c0,1.104 -0.896,2 -2,2c-1.104,-0 -2,-0.896 -2,-2c-0,-0.371 0.101,-0.718 0.277,-1.016l-2.261,-2.261c-0.298,0.176 -0.645,0.277 -1.016,0.277c-0.45,-0 -0.865,-0.149 -1.2,-0.4l-1.975,1.581Zm3.175,-4.181c-0.552,-0 -1,0.448 -1,1c-0,0.552 0.448,1 1,1c0.552,-0 1,-0.448 1,-1c-0,-0.552 -0.448,-1 -1,-1Zm-4,-4c-0.552,-0 -1,0.448 -1,1c0,0.552 0.448,1 1,1c0.552,-0 1,-0.448 1,-1c0,-0.552 -0.448,-1 -1,-1Zm-1,8c-0.552,-0 -1,0.448 -1,1c0,0.552 0.448,1 1,1c0.552,-0 1,-0.448 1,-1c0,-0.552 -0.448,-1 -1,-1Zm10,1c-0.552,-0 -1,0.448 -1,1c0,0.552 0.448,1 1,1c0.552,-0 1,-0.448 1,-1c0,-0.552 -0.448,-1 -1,-1Zm-3,-10c-0.552,-0 -1,0.448 -1,1c0,0.552 0.448,1 1,1c0.552,-0 1,-0.448 1,-1c0,-0.552 -0.448,-1 -1,-1Zm3,4c-0.552,-0 -1,0.448 -1,1c0,0.552 0.448,1 1,1c0.552,-0 1,-0.448 1,-1c0,-0.552 -0.448,-1 -1,-1Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const editorLineage = () => {
  return (
    <svg
      className="icon-editor icon-editor_lineage"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M4.95,4c-0.232,1.141 -1.241,2 -2.45,2c-1.38,0 -2.5,-1.12 -2.5,-2.5c-0,-1.38 1.12,-2.5 2.5,-2.5c1.209,0 2.218,0.859 2.45,2l1.55,0c0.276,-0 0.5,0.224 0.5,0.5l0,1.5l2.05,0c0.232,-1.141 1.241,-2 2.45,-2c1.38,0 2.5,1.12 2.5,2.5c-0,1.38 -1.12,2.5 -2.5,2.5c-1.209,0 -2.218,-0.859 -2.45,-2l-1.514,0l-0.536,0l0,4.5c-0,0.276 -0.224,0.5 -0.5,0.5l-1.55,0c-0.232,1.141 -1.241,2 -2.45,2c-1.38,0 -2.5,-1.12 -2.5,-2.5c0,-1.38 1.12,-2.5 2.5,-2.5c1.209,0 2.218,0.859 2.45,2l1.05,-0l0,-4.5l-0,-1.5l-1.05,0Zm-0.95,-0.5c-0,-0.828 -0.672,-1.5 -1.5,-1.5c-0.828,0 -1.5,0.672 -1.5,1.5c0,0.828 0.672,1.5 1.5,1.5c0.828,0 1.5,-0.672 1.5,-1.5Zm6,2c-0,0.828 0.672,1.5 1.5,1.5c0.828,0 1.5,-0.672 1.5,-1.5c-0,-0.828 -0.672,-1.5 -1.5,-1.5c-0.828,0 -1.5,0.672 -1.5,1.5Zm-6,5c-0,-0.828 -0.672,-1.5 -1.5,-1.5c-0.828,0 -1.5,0.672 -1.5,1.5c0,0.828 0.672,1.5 1.5,1.5c0.828,0 1.5,-0.672 1.5,-1.5Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const editorCode = () => {
  return (
    <svg
      className="icon-editor icon-editor_code"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M3.012,3.151c0.192,-0.269 0.567,-0.332 0.837,-0.139c0.269,0.192 0.332,0.567 0.139,0.837l-2.251,3.151l2.251,3.151c0.193,0.27 0.13,0.645 -0.139,0.837c-0.27,0.193 -0.645,0.13 -0.837,-0.139l-2.5,-3.5c-0.149,-0.209 -0.149,-0.489 -0,-0.698l2.5,-3.5Zm7,0.698c-0.193,-0.27 -0.13,-0.645 0.139,-0.837c0.27,-0.193 0.645,-0.13 0.837,0.139l2.5,3.5c0.149,0.209 0.149,0.489 0,0.698l-2.5,3.5c-0.192,0.269 -0.567,0.332 -0.837,0.139c-0.269,-0.192 -0.332,-0.567 -0.139,-0.837l2.251,-3.151l-2.251,-3.151Zm-3.933,8.809c-0.087,0.319 -0.417,0.508 -0.737,0.421c-0.319,-0.087 -0.508,-0.417 -0.421,-0.737l3,-11c0.087,-0.319 0.417,-0.508 0.737,-0.421c0.319,0.087 0.508,0.417 0.421,0.737l-3,11Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const editorFocus = () => {
  return (
    <svg
      className="icon-editor icon-editor_focus"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M1.022,7l-0.522,-0c-0.276,-0 -0.5,-0.224 -0.5,-0.5c0,-0.276 0.224,-0.5 0.5,-0.5l0.522,0c0.239,-2.637 2.341,-4.739 4.978,-4.978l0,-0.522c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l-0,0.522c2.637,0.239 4.739,2.341 4.978,4.978l0.522,-0c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-0.522,0c-0.239,2.637 -2.341,4.739 -4.978,4.978l0,0.522c-0,0.276 -0.224,0.5 -0.5,0.5c-0.276,-0 -0.5,-0.224 -0.5,-0.5l0,-0.522c-2.637,-0.239 -4.739,-2.341 -4.978,-4.978Zm4.978,-4.972c-2.085,0.23 -3.742,1.887 -3.972,3.972l1.472,-0c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-1.472,-0c0.23,2.085 1.887,3.742 3.972,3.972l0,-1.472c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l0,1.472c2.085,-0.23 3.742,-1.887 3.972,-3.972l-1.472,-0c-0.276,-0 -0.5,-0.224 -0.5,-0.5c0,-0.276 0.224,-0.5 0.5,-0.5l1.472,0c-0.23,-2.085 -1.887,-3.742 -3.972,-3.972l-0,1.472c-0,0.276 -0.224,0.5 -0.5,0.5c-0.276,-0 -0.5,-0.224 -0.5,-0.5l0,-1.472Zm0.5,3.472c0.552,0 1,0.448 1,1c-0,0.552 -0.448,1 -1,1c-0.552,-0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const search = () => {
  return (
    <svg
      className="icon-search"
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M13.291,13.998c-1.41,1.246 -3.263,2.002 -5.291,2.002c-4.415,0 -8,-3.585 -8,-8c0,-4.415 3.585,-8 8,-8c1.348,0 2.619,0.334 3.734,0.924c2.536,1.342 4.266,4.009 4.266,7.076c-0,2.028 -0.756,3.881 -2.002,5.291l1.355,1.355c0.558,-0.266 1.246,-0.168 1.708,0.293l6.5,6.5c0.585,0.586 0.585,1.536 -0,2.122c-0.586,0.585 -1.536,0.585 -2.122,-0l-6.5,-6.5c-0.461,-0.462 -0.559,-1.15 -0.293,-1.708l-1.355,-1.355Zm-0.342,-1.049c1.267,-1.267 2.051,-3.017 2.051,-4.949c-0,-2.684 -1.514,-5.017 -3.734,-6.192c-0.975,-0.516 -2.087,-0.808 -3.266,-0.808c-3.863,0 -7,3.137 -7,7c0,3.863 3.137,7 7,7c1.932,0 3.682,-0.784 4.949,-2.051Zm2.697,2.697c-0.195,0.196 -0.195,0.512 0,0.708l6.5,6.5c0.196,0.195 0.512,0.195 0.708,-0c0.195,-0.196 0.195,-0.512 -0,-0.708l-6.5,-6.5c-0.196,-0.195 -0.512,-0.195 -0.708,0Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const filter = () => {
  return (
    <svg
      className="icon-filter"
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M0.5,0.5l22.969,0l-0,2.081c-0,0.465 -0.162,0.916 -0.458,1.274c-1.756,2.127 -7.964,9.645 -7.964,9.645l-0,6l-6.047,4l0,-10c0,-0 -6.262,-7.514 -8.036,-9.643c-0.3,-0.36 -0.464,-0.813 -0.464,-1.281c-0,-0.829 -0,-2.076 -0,-2.076Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M11.5,13.5l3.547,-0"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
    </svg>
  );
};

/**
 * Four corner brackets -- the conventional "open this in a bigger window" mark. Stroked like the
 * other toolbar glyphs in this file rather than filled, so it reads at small sizes.
 */
const expand = () => {
  return (
    <svg
      className="icon icon-expand"
      width="100%"
      height="100%"
      viewBox="0 0 14 14"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M11.5,2.5l-3.031,3.031"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M8.5,2.5l2.994,-0l-0,3.031"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M2.5,11.5l3,-2.992"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M5.5,11.5l-2.992,-0l-0.008,-3"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />

      <path
        d="M11.5,0.5c0.53,-0 1.039,0.211 1.414,0.586c0.375,0.375 0.586,0.884 0.586,1.414l-0,9c0,0.53 -0.211,1.039 -0.586,1.414c-0.375,0.375 -0.884,0.586 -1.414,0.586l-9,-0c-0.53,0 -1.039,-0.211 -1.414,-0.586c-0.375,-0.375 -0.586,-0.884 -0.586,-1.414l-0,-9c-0,-0.53 0.211,-1.039 0.586,-1.414c0.375,-0.375 0.884,-0.586 1.414,-0.586l9,0Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
    </svg>
  );
};

const fit = () => {
  return (
    <svg
      className="icon icon-fit"
      width="100%"
      height="100%"
      viewBox="0 0 21 21"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path d="M2.5,10.5l5,0" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path
        d="M4.5,12.5l-2,-1.981"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path d="M4.5,8.5l-2,1.981" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path d="M10.5,2.5l0,5" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path
        d="M12.5,4.5l-1.981,-2"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path d="M8.5,4.5l1.981,-2" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path
        d="M18.5,10.5l-5,-0.019"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path d="M16.5,12.5l2,-2" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path
        d="M16.538,8.5l1.962,1.962"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path d="M10.5,18.5l0,-5" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path
        d="M12.5,16.5l-1.981,2"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path d="M8.5,16.5l1.981,2" style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }} />
      <path
        d="M20.484,1.5c0,-0.265 -0.105,-0.52 -0.293,-0.707c-0.187,-0.188 -0.441,-0.293 -0.707,-0.293l-18,-0c-0.265,-0 -0.519,0.105 -0.707,0.293c-0.187,0.187 -0.293,0.442 -0.293,0.707l0,18c0,0.265 0.106,0.52 0.293,0.707c0.188,0.188 0.442,0.293 0.707,0.293l18,-0c0.266,0 0.52,-0.105 0.707,-0.293c0.188,-0.187 0.293,-0.442 0.293,-0.707l0,-18Z"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
    </svg>
  );
};

const reload = () => {
  return (
    <svg
      className="icon icon-reload"
      width="100%"
      height="100%"
      viewBox="0 0 21 21"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M20.5,10.5c-0,5.519 -4.481,10 -10,10c-5.519,0 -10,-4.481 -10,-10c0,-5.519 4.481,-10 10,-10c2.702,0 5.156,1.074 6.956,2.818"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <path
        d="M17.572,0.5l0,2.928l-2.986,-0"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
    </svg>
  );
};

const nodeSize = () => {
  return (
    <svg
      className="icon icon-node_size"
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <circle
        cx="17"
        cy="17"
        r="6.5"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <circle
        cx="3"
        cy="14.5"
        r="2.5"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
      <circle
        cx="11"
        cy="4.5"
        r="4"
        style={{ fill: 'none', stroke: '#222020', strokeWidth: '1px' }}
      />
    </svg>
  );
};

const lightMode = () => {
  return (
    <svg
      className="icon icon-light-mode"
      width="100%"
      height="100%"
      viewBox="0 0 21 21"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M10,16.5c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l0,4c-0,0.276 -0.224,0.5 -0.5,0.5c-0.276,-0 -0.5,-0.224 -0.5,-0.5l0,-4Zm-4.096,-2.111c0.195,-0.195 0.512,-0.195 0.707,0c0.195,0.195 0.195,0.512 -0,0.707l-2.829,2.829c-0.195,0.195 -0.511,0.195 -0.707,-0c-0.195,-0.196 -0.195,-0.512 0,-0.707l2.829,-2.829Zm5.096,-9.889c-0,0.276 -0.224,0.5 -0.5,0.5c-0.276,-0 -0.5,-0.224 -0.5,-0.5l-0,-4c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l-0,4Zm4.096,2.111c-0.195,0.195 -0.512,0.195 -0.707,-0c-0.195,-0.195 -0.195,-0.512 0,-0.707l2.829,-2.829c0.195,-0.195 0.511,-0.195 0.707,0c0.195,0.196 0.195,0.512 -0,0.707l-2.829,2.829Zm1.404,4.389c-0.276,-0 -0.5,-0.224 -0.5,-0.5c0,-0.276 0.224,-0.5 0.5,-0.5l4,-0c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-4,-0Zm-2.111,4.096c-0.195,-0.195 -0.195,-0.512 0,-0.707c0.195,-0.195 0.512,-0.195 0.707,0l2.829,2.829c0.195,0.195 0.195,0.511 -0,0.707c-0.196,0.195 -0.512,0.195 -0.707,-0l-2.829,-2.829Zm-13.889,-4.096c-0.276,-0 -0.5,-0.224 -0.5,-0.5c0,-0.276 0.224,-0.5 0.5,-0.5l4,-0c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-4,-0Zm2.575,-7.218c-0.195,-0.195 -0.195,-0.511 0,-0.707c0.196,-0.195 0.512,-0.195 0.707,0l2.829,2.829c0.195,0.195 0.195,0.512 -0,0.707c-0.195,0.195 -0.512,0.195 -0.707,-0l-2.829,-2.829Zm7.425,2.218c0.388,-0 0.765,0.049 1.125,0.142c1.94,0.5 3.375,2.263 3.375,4.358c-0,2.484 -2.016,4.5 -4.5,4.5c-2.484,-0 -4.5,-2.016 -4.5,-4.5c0,-2.484 2.016,-4.5 4.5,-4.5Zm0,1c-1.932,-0 -3.5,1.568 -3.5,3.5c0,1.932 1.568,3.5 3.5,3.5c1.932,-0 3.5,-1.568 3.5,-3.5c-0,-1.63 -1.116,-3.001 -2.625,-3.39c-0.28,-0.072 -0.573,-0.11 -0.875,-0.11Z"
        style={{ fill: '#222020' }}
      />

      <path
        className="fill"
        d="M10,16.5c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l0,4c-0,0.276 -0.224,0.5 -0.5,0.5c-0.276,-0 -0.5,-0.224 -0.5,-0.5l0,-4Zm-4.096,-2.111c0.195,-0.195 0.512,-0.195 0.707,0c0.195,0.195 0.195,0.512 -0,0.707l-2.829,2.829c-0.195,0.195 -0.511,0.195 -0.707,-0c-0.195,-0.196 -0.195,-0.512 0,-0.707l2.829,-2.829Zm5.096,-9.889c-0,0.276 -0.224,0.5 -0.5,0.5c-0.276,-0 -0.5,-0.224 -0.5,-0.5l-0,-4c-0,-0.276 0.224,-0.5 0.5,-0.5c0.276,-0 0.5,0.224 0.5,0.5l-0,4Zm4.096,2.111c-0.195,0.195 -0.512,0.195 -0.707,-0c-0.195,-0.195 -0.195,-0.512 0,-0.707l2.829,-2.829c0.195,-0.195 0.511,-0.195 0.707,0c0.195,0.196 0.195,0.512 -0,0.707l-2.829,2.829Zm1.404,4.389c-0.276,-0 -0.5,-0.224 -0.5,-0.5c0,-0.276 0.224,-0.5 0.5,-0.5l4,-0c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-4,-0Zm-2.111,4.096c-0.195,-0.195 -0.195,-0.512 0,-0.707c0.195,-0.195 0.512,-0.195 0.707,0l2.829,2.829c0.195,0.195 0.195,0.511 -0,0.707c-0.196,0.195 -0.512,0.195 -0.707,-0l-2.829,-2.829Zm-13.889,-4.096c-0.276,-0 -0.5,-0.224 -0.5,-0.5c0,-0.276 0.224,-0.5 0.5,-0.5l4,-0c0.276,-0 0.5,0.224 0.5,0.5c0,0.276 -0.224,0.5 -0.5,0.5l-4,-0Zm2.575,-7.218c-0.195,-0.195 -0.195,-0.511 0,-0.707c0.196,-0.195 0.512,-0.195 0.707,0l2.829,2.829c0.195,0.195 0.195,0.512 -0,0.707c-0.195,0.195 -0.512,0.195 -0.707,-0l-2.829,-2.829Zm7.425,2.218c0.388,-0 0.765,0.049 1.125,0.142c1.94,0.5 3.375,2.263 3.375,4.358c-0,2.484 -2.016,4.5 -4.5,4.5c-2.484,-0 -4.5,-2.016 -4.5,-4.5c0,-2.484 2.016,-4.5 4.5,-4.5Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const darkMode = () => {
  return (
    <svg
      className="icon icon-dark-mode"
      width="100%"
      height="100%"
      viewBox="0 0 21 21"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        className="fill"
        d="M17.753,13.088c-1.057,2.861 -3.81,4.904 -7.037,4.904c-4.14,-0 -7.5,-3.361 -7.5,-7.5c-0,-3.965 3.083,-7.215 6.98,-7.483c0.203,-0.013 0.394,0.097 0.483,0.279c0.09,0.182 0.06,0.401 -0.075,0.552c-0.864,0.972 -1.388,2.251 -1.388,3.652c-0,3.035 2.464,5.5 5.5,5.5c0.842,-0 1.64,-0.19 2.354,-0.529c0.183,-0.087 0.402,-0.054 0.552,0.083c0.15,0.137 0.202,0.351 0.131,0.542Z"
        style={{ fill: '#222020' }}
      />
      <path
        d="M17.753,13.088c-1.057,2.861 -3.81,4.904 -7.037,4.904c-4.14,-0 -7.5,-3.361 -7.5,-7.5c-0,-3.965 3.083,-7.215 6.98,-7.483c0.203,-0.013 0.394,0.097 0.483,0.279c0.09,0.182 0.06,0.401 -0.075,0.552c-0.864,0.972 -1.388,2.251 -1.388,3.652c-0,3.035 2.464,5.5 5.5,5.5c0.842,-0 1.64,-0.19 2.354,-0.529c0.183,-0.087 0.402,-0.054 0.552,0.083c0.15,0.137 0.202,0.351 0.131,0.542Zm-1.438,0.705c-0.512,0.129 -1.048,0.199 -1.599,0.199c-3.588,-0 -6.5,-2.913 -6.5,-6.5c-0,-1.205 0.328,-2.334 0.901,-3.302c-2.815,0.713 -4.901,3.266 -4.901,6.302c-0,3.587 2.912,6.5 6.5,6.5c2.383,-0 4.467,-1.285 5.599,-3.199Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const autoMode = () => {
  return (
    <svg
      className="icon icon-auto-mode"
      width="100%"
      height="100%"
      viewBox="0 0 21 21"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M19.224,16.549c-0.635,1.717 -2.287,2.943 -4.223,2.943c-2.484,-0 -4.501,-2.017 -4.501,-4.501c0,-2.379 1.85,-4.329 4.189,-4.49c0.203,-0.014 0.393,0.096 0.483,0.279c0.089,0.182 0.06,0.4 -0.075,0.552c-0.46,0.518 -0.739,1.199 -0.739,1.945c-0,1.616 1.312,2.929 2.929,2.929c0.448,-0 0.873,-0.101 1.253,-0.282c0.184,-0.087 0.402,-0.054 0.552,0.083c0.15,0.137 0.202,0.352 0.132,0.542Zm-1.493,0.632c-0.146,0.016 -0.294,0.025 -0.444,0.025c-2.169,-0 -3.929,-1.761 -3.929,-3.929c-0,-0.539 0.108,-1.053 0.306,-1.522c-1.27,0.526 -2.164,1.777 -2.164,3.236c-0,1.932 1.569,3.501 3.501,3.501c1.103,-0 2.088,-0.512 2.73,-1.311Zm-12.691,-4.928c0.195,-0.195 0.512,-0.195 0.707,-0c0.195,0.195 0.195,0.512 0,0.707l-2.404,2.404c-0.195,0.195 -0.512,0.195 -0.707,-0c-0.195,-0.195 -0.195,-0.512 0,-0.707l2.404,-2.404Zm4.46,-8.353c0,0.276 -0.224,0.5 -0.5,0.5c-0.276,0 -0.5,-0.224 -0.5,-0.5l0,-3.4c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l-0,3.4Zm3.46,1.847c-0.195,0.195 -0.512,0.195 -0.707,0c-0.195,-0.195 -0.195,-0.512 -0,-0.707l2.404,-2.404c0.195,-0.195 0.512,-0.195 0.707,0c0.195,0.195 0.195,0.512 -0,0.707l-2.404,2.404Zm-12.46,3.753c-0.276,0 -0.5,-0.224 -0.5,-0.5c-0,-0.276 0.224,-0.5 0.5,-0.5l3.4,-0c0.276,0 0.5,0.224 0.5,0.5c-0,0.276 -0.224,0.5 -0.5,0.5l-3.4,-0Zm2.136,-6.157c-0.195,-0.195 -0.195,-0.512 0,-0.707c0.195,-0.195 0.512,-0.195 0.707,0l2.404,2.404c0.195,0.195 0.195,0.512 0,0.707c-0.195,0.195 -0.512,0.195 -0.707,0l-2.404,-2.404Zm4.314,7.707c0.195,0.195 0.195,0.512 -0,0.707c-0.195,0.195 -0.512,0.195 -0.707,0c-0.706,-0.706 -1.143,-1.681 -1.143,-2.757c-0,-2.152 1.748,-3.9 3.9,-3.9c0.337,-0 0.663,0.043 0.975,0.123c0.685,0.177 1.297,0.535 1.782,1.02c0.195,0.195 0.195,0.512 0,0.707c-0.195,0.195 -0.512,0.195 -0.707,-0c-0.361,-0.361 -0.816,-0.627 -1.325,-0.759c-0.232,-0.059 -0.475,-0.091 -0.725,-0.091c-1.601,-0 -2.9,1.299 -2.9,2.9c-0,0.8 0.325,1.525 0.85,2.05Zm-5.096,8.804c-0.196,0.195 -0.512,0.195 -0.708,-0c-0.195,-0.196 -0.195,-0.512 0,-0.708l18,-18c0.196,-0.195 0.512,-0.195 0.708,0c0.195,0.196 0.195,0.512 -0,0.708l-18,18Z"
        style={{ fill: '#222020' }}
      />

      <path
        className="fill"
        d="M6.95,11.757c-0.195,0.195 -0.512,0.195 -0.707,0c-0.706,-0.706 -1.143,-1.681 -1.143,-2.757c-0,-2.152 1.748,-3.9 3.9,-3.9c0.337,-0 0.663,0.043 0.975,0.123c0.685,0.177 1.297,0.535 1.782,1.02c0.195,0.195 0.195,0.512 0,0.707l-4.807,4.807Zm12.274,4.792c-0.635,1.717 -2.287,2.943 -4.223,2.943c-2.484,-0 -4.501,-2.017 -4.501,-4.501c0,-2.379 1.85,-4.329 4.189,-4.49c0.203,-0.014 0.393,0.096 0.483,0.279c0.089,0.182 0.06,0.4 -0.075,0.552c-0.46,0.518 -0.739,1.199 -0.739,1.945c-0,1.616 1.312,2.929 2.929,2.929c0.448,-0 0.873,-0.101 1.253,-0.282c0.184,-0.087 0.402,-0.054 0.552,0.083c0.15,0.137 0.202,0.352 0.132,0.542Zm-14.184,-4.296c0.195,-0.195 0.512,-0.195 0.707,-0c0.195,0.195 0.195,0.512 0,0.707l-2.404,2.404c-0.195,0.195 -0.512,0.195 -0.707,-0c-0.195,-0.195 -0.195,-0.512 0,-0.707l2.404,-2.404Zm4.46,-8.353c0,0.276 -0.224,0.5 -0.5,0.5c-0.276,0 -0.5,-0.224 -0.5,-0.5l0,-3.4c0,-0.276 0.224,-0.5 0.5,-0.5c0.276,0 0.5,0.224 0.5,0.5l-0,3.4Zm3.46,1.847c-0.195,0.195 -0.512,0.195 -0.707,0c-0.195,-0.195 -0.195,-0.512 -0,-0.707l2.404,-2.404c0.195,-0.195 0.512,-0.195 0.707,0c0.195,0.195 0.195,0.512 -0,0.707l-2.404,2.404Zm-12.46,3.753c-0.276,0 -0.5,-0.224 -0.5,-0.5c-0,-0.276 0.224,-0.5 0.5,-0.5l3.4,-0c0.276,0 0.5,0.224 0.5,0.5c-0,0.276 -0.224,0.5 -0.5,0.5l-3.4,-0Zm2.136,-6.157c-0.195,-0.195 -0.195,-0.512 0,-0.707c0.195,-0.195 0.512,-0.195 0.707,0l2.404,2.404c0.195,0.195 0.195,0.512 0,0.707c-0.195,0.195 -0.512,0.195 -0.707,0l-2.404,-2.404Zm-0.782,16.511c-0.196,0.195 -0.512,0.195 -0.708,-0c-0.195,-0.196 -0.195,-0.512 0,-0.708l18,-18c0.196,-0.195 0.512,-0.195 0.708,0c0.195,0.196 0.195,0.512 -0,0.708l-18,18Z"
        style={{ fill: '#222020' }}
      />
    </svg>
  );
};

const relationshipGraphSpinner = () => {
  return (
    <svg
      className="icon-relationship_graph_spinner"
      width="100%"
      height="100%"
      viewBox="0 0 160 135"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M102.474,20.988l-0,21.86c-5.563,0.261 -10,4.861 -10,10.489c-0,3.04 1.295,5.78 3.363,7.698l-18.491,21.958c-1.767,-1.352 -3.976,-2.156 -6.372,-2.156c-1.632,-0 -3.177,0.373 -4.556,1.038l-16.009,-29.555c3.035,-1.841 5.065,-5.177 5.065,-8.983c-0,-5.796 -4.705,-10.5 -10.5,-10.5c-5.795,-0 -10.5,4.704 -10.5,10.5c-0,5.795 4.705,10.5 10.5,10.5c1.632,-0 3.177,-0.374 4.556,-1.039l16.009,29.555c-3.036,1.841 -5.065,5.177 -5.065,8.984c-0,0.976 0.133,1.922 0.383,2.819l-40.555,13.412c-1.515,-3.935 -5.335,-6.731 -9.802,-6.731c-5.795,-0 -10.5,4.704 -10.5,10.5c-0,5.795 4.705,10.5 10.5,10.5c5.795,-0 10.5,-4.705 10.5,-10.5c-0,-0.977 -0.134,-1.922 -0.383,-2.82l40.554,-13.412c1.516,3.936 5.335,6.732 9.803,6.732c3.231,-0 6.123,-1.463 8.05,-3.761l25.479,19.343c-1.276,1.739 -2.029,3.884 -2.029,6.203c-0,5.795 4.705,10.5 10.5,10.5c5.795,-0 10.5,-4.705 10.5,-10.5c-0,-5.795 -4.705,-10.5 -10.5,-10.5c-3.117,-0 -5.918,1.361 -7.841,3.52l-25.504,-19.362c1.164,-1.691 1.845,-3.738 1.845,-5.943c-0,-3.041 -1.295,-5.781 -3.363,-7.699l18.49,-21.958c1.768,1.353 3.977,2.157 6.373,2.157c5.627,-0 10.227,-4.437 10.488,-10l25.076,-0c0.261,5.563 4.861,10 10.488,10c5.795,-0 10.5,-4.705 10.5,-10.5c0,-5.796 -4.705,-10.5 -10.5,-10.5c-5.627,-0 -10.227,4.436 -10.488,10l-25.076,-0c-0.254,-5.398 -4.591,-9.735 -9.988,-9.989l-0,-21.86c5.563,-0.261 10,-4.86 10,-10.488c-0,-5.795 -4.705,-10.5 -10.5,-10.5c-5.795,0 -10.5,4.705 -10.5,10.5c-0,5.628 4.437,10.227 10,10.488Zm3.115,96.66c-1.323,1.633 -2.115,3.712 -2.115,5.974c-0,5.243 4.257,9.5 9.5,9.5c5.243,-0 9.5,-4.257 9.5,-9.5c-0,-5.243 -4.257,-9.5 -9.5,-9.5c-2.975,-0 -5.632,1.37 -7.374,3.512c-0.003,0.005 -0.007,0.009 -0.011,0.014Zm-95.089,-15.811c5.243,-0 9.5,4.256 9.5,9.5c-0,5.243 -4.257,9.5 -9.5,9.5c-5.243,-0 -9.5,-4.257 -9.5,-9.5c-0,-5.244 4.257,-9.5 9.5,-9.5Zm66.585,-17.772c-1.653,-1.39 -3.785,-2.228 -6.111,-2.228c-5.243,-0 -9.5,4.256 -9.5,9.5c-0,5.243 4.257,9.5 9.5,9.5c3.083,-0 5.825,-1.472 7.561,-3.751l0.009,-0.012c1.211,-1.595 1.93,-3.583 1.93,-5.737c-0,-2.911 -1.312,-5.517 -3.375,-7.26l-0.014,-0.012Zm19.778,-23.457c1.652,1.391 3.784,2.229 6.111,2.229c5.243,-0 9.5,-4.257 9.5,-9.5c-0,-5.244 -4.257,-9.5 -9.5,-9.5c-5.243,-0 -9.5,4.256 -9.5,9.5c-0,2.91 1.312,5.517 3.375,7.26l0.014,0.011Zm52.163,-16.771c5.243,-0 9.5,4.256 9.5,9.5c0,5.243 -4.257,9.5 -9.5,9.5c-5.243,-0 -9.5,-4.257 -9.5,-9.5c0,-5.244 4.257,-9.5 9.5,-9.5Zm-104.052,-10c5.243,-0 9.5,4.256 9.5,9.5c-0,5.243 -4.257,9.5 -9.5,9.5c-5.243,-0 -9.5,-4.257 -9.5,-9.5c-0,-5.244 4.257,-9.5 9.5,-9.5Zm58,-32.837c5.243,-0 9.5,4.257 9.5,9.5c-0,5.243 -4.257,9.5 -9.5,9.5c-5.243,-0 -9.5,-4.257 -9.5,-9.5c-0,-5.243 4.257,-9.5 9.5,-9.5Z"
        style={{ fill: 'url(#relationship-graph-gradient)' }}
      />

      <defs>
        <linearGradient id="relationship-graph-gradient">
          <stop offset="0%" style={{ stopColor: '#A59CA5', stopOpacity: '1' }} />
          <stop offset="25%" style={{ stopColor: '#A59CA5', stopOpacity: '1' }} />
          <stop offset="50%" style={{ stopColor: '#fff', stopOpacity: '1' }} />
          <stop offset="75%" style={{ stopColor: '#A59CA5', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#A59CA5', stopOpacity: '1' }} />
          <animateTransform
            attributeName="gradientTransform"
            type="translate"
            from="-1 0"
            to="1 0"
            begin="0s"
            dur="1s"
            repeatCount="indefinite"
          />
        </linearGradient>
      </defs>
    </svg>
  );
};

const lineageGraphSpinner = () => {
  return (
    <svg
      className="icon-lineage_graph_spinner"
      width="100%"
      height="100%"
      viewBox="0 0 171 81"
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeMiterlimit: 1,
      }}
    >
      <path
        d="M20.988,41c-0.261,5.563 -4.861,10 -10.488,10c-5.795,-0 -10.5,-4.705 -10.5,-10.5c0,-5.795 4.705,-10.5 10.5,-10.5c5.627,-0 10.227,4.437 10.488,10l25.012,-0l0,-1.75l4.003,2.001c0.132,-5.68 4.785,-10.251 10.497,-10.251c5.627,-0 10.227,4.437 10.488,10l9.512,-0c1.193,0 2.338,-0.474 3.182,-1.318c0.844,-0.844 1.318,-1.989 1.318,-3.182c-0,-2.219 1.314,-4.13 3.206,-5c-1.892,-0.87 -3.206,-2.781 -3.206,-5l0,-10c-0,-1.193 -0.474,-2.338 -1.318,-3.182c-0.844,-0.844 -1.989,-1.318 -3.182,-1.318l-9.512,-0c-0.261,5.563 -4.861,10 -10.488,10c-5.795,0 -10.5,-4.705 -10.5,-10.5c0,-5.795 4.705,-10.5 10.5,-10.5c5.627,0 10.227,4.437 10.488,10l9.512,0c1.459,-0 2.858,0.579 3.889,1.611c1.032,1.031 1.611,2.43 1.611,3.889l0,10c-0,2.485 2.015,4.5 4.5,4.5l5.5,-0l-0,-1.75l3.5,1.75l0.512,-0c0.261,-5.563 4.861,-10 10.488,-10c5.628,-0 10.227,4.437 10.488,10l12.014,-0c1.103,-0 1.998,-0.895 1.998,-1.998l-0,-0.004c0,-1.656 1.342,-2.998 2.998,-2.998l8.002,0l0,-1.75l4.003,2.001c0.132,-5.68 4.785,-10.251 10.497,-10.251c5.795,-0 10.5,4.705 10.5,10.5c0,5.795 -4.705,10.5 -10.5,10.5c-5.712,-0 -10.365,-4.571 -10.497,-10.251l-4.003,2.001l0,-1.75l-8.002,-0c-1.103,0 -1.998,0.895 -1.998,1.998l0,0.004c-0,1.458 -1.04,2.672 -2.419,2.942c0.288,0.195 0.559,0.418 0.808,0.667c1.032,1.031 1.611,2.43 1.611,3.889l-0,15c-0,1.193 0.474,2.338 1.318,3.182c0.844,0.844 1.989,1.318 3.182,1.318l5.5,-0l0,-1.75l4.003,2.001c0.132,-5.68 4.785,-10.251 10.497,-10.251c5.795,-0 10.5,4.705 10.5,10.5c0,5.795 -4.705,10.5 -10.5,10.5c-5.712,-0 -10.365,-4.571 -10.497,-10.251l-4.003,2.001l0,-1.75l-5.5,-0c-1.459,0 -2.858,-0.579 -3.889,-1.611c-1.032,-1.031 -1.611,-2.43 -1.611,-3.889l0,-15c0,-1.193 -0.474,-2.338 -1.318,-3.182c-0.844,-0.844 -1.989,-1.318 -3.182,-1.318l-9.512,0c-0.261,5.563 -4.861,10 -10.488,10c-5.628,-0 -10.227,-4.437 -10.488,-10l-0.512,-0l-3.5,1.75l0,-1.75l-5.5,-0c-2.485,0 -4.5,2.015 -4.5,4.5l0,30c-0,1.459 -0.579,2.858 -1.611,3.889c-1.031,1.032 -2.43,1.611 -3.889,1.611l-9.512,-0c-0.261,5.563 -4.861,10 -10.488,10c-5.795,-0 -10.5,-4.705 -10.5,-10.5c0,-5.795 4.705,-10.5 10.5,-10.5c5.627,-0 10.227,4.437 10.488,10l9.512,0c1.193,0 2.338,-0.474 3.182,-1.318c0.844,-0.844 1.318,-1.989 1.318,-3.182l0,-26.838c-0.181,0.258 -0.385,0.501 -0.611,0.727c-1.031,1.032 -2.43,1.611 -3.889,1.611l-9.512,-0c-0.261,5.563 -4.861,10 -10.488,10c-5.712,-0 -10.365,-4.571 -10.497,-10.251l-4.003,2.001l0,-1.75l-25.012,-0Zm-0.988,-0.5c0,-5.243 -4.257,-9.5 -9.5,-9.5c-5.243,-0 -9.5,4.257 -9.5,9.5c0,5.243 4.257,9.5 9.5,9.5c5.243,-0 9.5,-4.257 9.5,-9.5Zm50,-0c0,-5.243 -4.257,-9.5 -9.5,-9.5c-5.243,-0 -9.5,4.257 -9.5,9.5c0,5.243 4.257,9.5 9.5,9.5c5.243,-0 9.5,-4.257 9.5,-9.5Zm0,-30c0,-5.243 -4.257,-9.5 -9.5,-9.5c-5.243,0 -9.5,4.257 -9.5,9.5c0,5.243 4.257,9.5 9.5,9.5c5.243,0 9.5,-4.257 9.5,-9.5Zm50,20c0,-5.243 -4.257,-9.5 -9.5,-9.5c-5.243,-0 -9.5,4.257 -9.5,9.5c0,5.243 4.257,9.5 9.5,9.5c5.243,-0 9.5,-4.257 9.5,-9.5Zm-50,40c0,-5.243 -4.257,-9.5 -9.5,-9.5c-5.243,-0 -9.5,4.257 -9.5,9.5c0,5.243 4.257,9.5 9.5,9.5c5.243,-0 9.5,-4.257 9.5,-9.5Zm90.5,-54.5c-5.243,-0 -9.5,4.257 -9.5,9.5c0,5.243 4.257,9.5 9.5,9.5c5.243,-0 9.5,-4.257 9.5,-9.5c0,-5.243 -4.257,-9.5 -9.5,-9.5Zm0,30c-5.243,-0 -9.5,4.257 -9.5,9.5c0,5.243 4.257,9.5 9.5,9.5c5.243,-0 9.5,-4.257 9.5,-9.5c0,-5.243 -4.257,-9.5 -9.5,-9.5Z"
        style={{ fill: 'url(#lineage-graph-gradient)' }}
      />

      <defs>
        <linearGradient id="lineage-graph-gradient">
          <stop offset="0%" style={{ stopColor: '#A59CA5', stopOpacity: '1' }} />
          <stop offset="25%" style={{ stopColor: '#A59CA5', stopOpacity: '1' }} />
          <stop offset="50%" style={{ stopColor: '#000', stopOpacity: '1' }} />
          <stop offset="75%" style={{ stopColor: '#A59CA5', stopOpacity: '1' }} />
          <stop offset="100%" style={{ stopColor: '#A59CA5', stopOpacity: '1' }} />
          <animateTransform
            attributeName="gradientTransform"
            type="translate"
            from="-1 0"
            to="1 0"
            begin="0s"
            dur="1s"
            repeatCount="indefinite"
          />
        </linearGradient>
      </defs>
    </svg>
  );
};
